import { useState, useEffect } from 'react'
import axios from 'axios'
import { FiX, FiLoader, FiSave, FiUpload, FiImage } from 'react-icons/fi'
import toast from 'react-hot-toast'
import ProductsManagement from './ProductsManagement'
import FixturesManagement from './FixturesManagement'
import ResumesManagement from './ResumesManagement'
import TeamMembersEditor from './TeamMembersEditor'
import TicketsManagement from './TicketsManagement'
import AudioManagement from './AudioManagement'
import VideoUrlInput from './VideoUrlInput'
import ImageUrlInput from './ImageUrlInput'
import WebsiteUrlInput from './WebsiteUrlInput'
import VideoUrlFrame from '../public/VideoUrlFrame'
import WebsiteUrlCard from '../public/WebsiteUrlCard'
import SpecialMediaImageCarousel from '../public/SpecialMediaImageCarousel'
import { isExternalVideoUrl } from '../../utils/videoUrl'
import { getCurrencyOptions } from '../../utils/currency'

const SECTION_TYPES = [
  'hero',
  'features',
  'products',
  'tickets',
  'services',
  'testimonials',
  'pricing',
  'gallery',
  'contact',
  'newsletter',
  'team',
  'about',
  'faq',
  'blog',
  'cart',
  'checkout',
  'reviews',
  'fixtures',
  'points-table',
  'resume',
  'audio',
  'crypto-product',
  'donation'
]

export default function SectionEditorModal({ isOpen, onClose, onSubmit, section, website }) {
  // Normalize section type (some places use `sectionType` instead of `type`)
  const sectionType = section?.type || section?.sectionType || ''
  const createUniqueId = (prefix) => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  }

  const [formData, setFormData] = useState({
    title: '',
    titleAvailable: true,
    description: '',
    type: '',
    enabled: true,
    displayOrder: 0,
    styling: {
      backgroundColor: '',
      textColor: '',
      hoverBackgroundColor: '',
      hoverTextColor: '',
      ctaButtonBackgroundColor: '',
      ctaButtonTextColor: '',
      ctaButtonHoverBackgroundColor: '',
      ctaButtonHoverTextColor: ''
    },
    content: {
      heading: '',
      headingAvailable: true,
      headingPosition: 'left',
      subheading: '',
      subheadingPosition: 'left',
      subheadingTextStyle: 'default',
      ctaText: '',
      ctaLink: '',
      ctaBackgroundColor: '',
      ctaTextColor: '',
      heroImage: { url: '', publicId: '' },
      heroImages: [],
      carouselSettings: {
        autoPlay: true,
        interval: 5000,
        transitionSpeed: 1000,
        showIndicators: true,
        showNavigation: true
      },
      specialMedia: [],
      leftSidebar: { contentText: '', media: [] },
      middleBar: { contentText: '', media: [] },
      rightSidebar: { contentText: '', media: [] },
      items: [],
      teamMembers: [],
      audio: [],
      buyRate: 0,
      sellRate: 0,
      usdtBuyRate: 0,
      usdtSellRate: 0,
      buyEnabled: true,
      sellEnabled: true,
      giftCardEnabled: true,
      giftCardWhatsApp: '',
      giftCardNames: []
    },
    isNew: false
  })
  const [saving, setSaving] = useState(false)
  const [uploadingMediaKey, setUploadingMediaKey] = useState(null)
  const [draggedHeroIndex, setDraggedHeroIndex] = useState(null)

  useEffect(() => {
    if (section && isOpen) {
      // Check if this is a new section
      const isNew = !section._id
      
      // Normalize section type (some places use `sectionType` instead of `type`)
      const sectionType = section?.type || section?.sectionType || ''
      
      // Ensure items have gallery arrays when loading from section
      const contentWithGalleries = section.content ? {
        ...section.content,
        subheadingPosition: section.content.subheadingPosition || 'left',
        items: (section.content.items || []).map(item => ({
          ...item,
          // Migrate old single image format to gallery format if needed
          gallery: item.gallery && Array.isArray(item.gallery) 
            ? item.gallery.map((galleryItem, galleryIndex) => ({
                ...galleryItem,
                id: galleryItem?.id || createUniqueId(`item-${item.id || 'gallery'}-${galleryIndex}`)
              }))
            : (item.image ? [{
                id: createUniqueId(`item-${item.id || 'gallery'}`),
                image: typeof item.image === 'string' 
                  ? { url: item.image, publicId: '' } 
                  : item.image,
                video: { url: '', publicId: '' },
                title: item.title || '',
                description: item.description || ''
              }] : [])
        })),
        specialMedia: (section.content.specialMedia || []).map((media, mediaIndex) => ({
          ...media,
          id: media.id || media._id || createUniqueId(`special-media-${mediaIndex}`)
        })),
        leftSidebar: {
          contentText: section.content.leftSidebar?.contentText || '',
          textStyle: section.content.leftSidebar?.textStyle || 'default',
          media: section.content.leftSidebar?.media || []
        },
        middleBar: {
          contentText: section.content.middleBar?.contentText || '',
          textStyle: section.content.middleBar?.textStyle || 'default',
          media: section.content.middleBar?.media || []
        },
        rightSidebar: {
          contentText: section.content.rightSidebar?.contentText || '',
          textStyle: section.content.rightSidebar?.textStyle || 'default',
          media: section.content.rightSidebar?.media || []
        },
        teamMembers: section.content.teamMembers || [],
        heroImages: section.content.heroImages || [],
        heroDisplayMode: section.content.heroDisplayMode || 'contain',
        carouselSettings: section.content.carouselSettings || {
          autoPlay: true,
          interval: 5000,
          transitionSpeed: 1000,
          showIndicators: true,
          showNavigation: true
        },
        audio: section.content.audio || [],
        buyRate: section.content.buyRate || 0,
        sellRate: section.content.sellRate || 0,
        usdtBuyRate: section.content.usdtBuyRate || 0,
        usdtSellRate: section.content.usdtSellRate || 0,
        buyEnabled: section.content.buyEnabled !== false,
        sellEnabled: section.content.sellEnabled !== false,
        giftCardEnabled: section.content.giftCardEnabled !== false,
        giftCardWhatsApp: section.content.giftCardWhatsApp || '',
        giftCardNames: Array.isArray(section.content.giftCardNames) ? section.content.giftCardNames : []
      } : {
        heading: '',
        headingPosition: 'left',
        subheading: '',
        subheadingPosition: 'left',
        subheadingTextStyle: 'default',
        ctaText: '',
        ctaLink: '',
        ctaBackgroundColor: '',
        ctaTextColor: '',
        heroImage: { url: '', publicId: '' },
        heroImages: [],
        heroDisplayMode: 'contain',
        carouselSettings: {
          autoPlay: true,
          interval: 5000,
          transitionSpeed: 1000,
          showIndicators: true,
          showNavigation: true
        },
        specialMedia: [],
        leftSidebar: { contentText: '', media: [] },
        middleBar: { contentText: '', media: [] },
        rightSidebar: { contentText: '', media: [] },
        items: [],
        teamMembers: [],
        audio: [],
        buyRate: 0,
        sellRate: 0,
        usdtBuyRate: 0,
        usdtSellRate: 0,
        giftCardEnabled: true,
        giftCardWhatsApp: '',
        giftCardNames: []
      }
      
        setFormData({
          title: section.title || '',
          titleAvailable: section.titleAvailable !== false,
          description: section.description || '',
          type: sectionType || '',
          enabled: section.status !== 'disabled' && section.disabled !== true,
          displayOrder: section.displayOrder || 0,
          styling: section.styling ? {
            backgroundColor: section.styling.backgroundColor || '',
            textColor: section.styling.textColor || '',
            hoverBackgroundColor: section.styling.hoverBackgroundColor || '',
            hoverTextColor: section.styling.hoverTextColor || '',
            ctaButtonBackgroundColor: section.styling.ctaButtonBackgroundColor || '',
            ctaButtonTextColor: section.styling.ctaButtonTextColor || '',
            ctaButtonHoverBackgroundColor: section.styling.ctaButtonHoverBackgroundColor || '',
            ctaButtonHoverTextColor: section.styling.ctaButtonHoverTextColor || ''
          } : {
            backgroundColor: '',
            textColor: '',
            hoverBackgroundColor: '',
            hoverTextColor: '',
            ctaButtonBackgroundColor: '',
            ctaButtonTextColor: '',
            ctaButtonHoverBackgroundColor: '',
            ctaButtonHoverTextColor: ''
          },
          content: contentWithGalleries,
          isNew
        })
    }
  }, [section, isOpen])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleStylingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      styling: {
        ...prev.styling,
        [field]: value
      }
    }))
  }

  const handleContentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value
      }
    }))
  }

  const getSidebarKey = (side) => side === 'middle' ? 'middleBar' : `${side}Sidebar`

  const getSidebarLabel = (side) => side === 'left' ? 'Left Sidebar' : side === 'middle' ? 'Middle Bar' : 'Right Sidebar'

  const handleSidebarTextStyleChange = (side, textStyle) => {
    const key = getSidebarKey(side)
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [key]: {
          ...(prev.content[key] || { contentText: '', media: [] }),
          textStyle
        }
      }
    }))
  }

  const handleAddGiftCardName = () => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        giftCardNames: [...(prev.content.giftCardNames || []), '']
      }
    }))
  }

  const handleUpdateGiftCardName = (index, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        giftCardNames: prev.content.giftCardNames.map((name, idx) => idx === index ? value : name)
      }
    }))
  }

  const handleRemoveGiftCardName = (index) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        giftCardNames: prev.content.giftCardNames.filter((_, idx) => idx !== index)
      }
    }))
  }

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: [
          ...prev.content.items,
          { 
            id: Date.now(), 
            title: '', 
            description: '', 
            price: '', 
            currency: website?.paymentSettings?.currency || 'NGN',
            whatsappContact: '', 
            gallery: [] 
          }
        ]
      }
    }))
  }

  const handleUpdateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: prev.content.items.map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      }
    }))
  }

  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: prev.content.items.filter(item => item.id !== itemId)
      }
    }))
  }

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
  const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg', 'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/x-ms-wmv', 'video/x-m4v', 'video/mpeg', 'video/3gpp', 'video/3gpp2']
  const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg', 'ogv', 'ogg', '3gp', '3g2']

  const getFileExtension = (file) => {
    if (!file?.name) return ''
    return file.name.split('.').pop().toLowerCase()
  }

  const isImageFile = (file) => {
    return file && ACCEPTED_IMAGE_TYPES.includes(file.type)
  }

  const isVideoFile = (file) => {
    if (!file) return false
    const extension = getFileExtension(file)
    return (file.type && file.type.startsWith('video/')) || ACCEPTED_VIDEO_TYPES.includes(file.type) || SUPPORTED_VIDEO_EXTENSIONS.includes(extension)
  }

  const validateMediaFile = (file, imageOnly = false) => {
    if (!file) {
      return { valid: false, message: 'Please select a file to upload' }
    }

    if (imageOnly) {
      if (!isImageFile(file)) {
        return { valid: false, message: 'Please upload a valid image file' }
      }
    } else {
      if (!isImageFile(file) && !isVideoFile(file)) {
        return {
          valid: false,
          message: 'Please upload a supported image or video file (JPG, PNG, GIF, WEBP, SVG, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, MPEG, OGG, 3GP, 3G2)'
        }
      }
    }

    // Website builder media uploads are not capped by a hard client-side size limit.
    return { valid: true }
  }

  const uploadMediaFile = async (file) => {
    const formDataUpload = new FormData()
    formDataUpload.append('file', file)
    formDataUpload.append('folder', 'website/sections')

    const response = await axios.post('upload', formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (!response?.data?.url) {
      throw new Error('Upload failed to return a valid URL')
    }

    return response.data
  }

  const getVideoMimeType = (url) => {
    if (!url) return undefined
    const extension = url.split('?')[0].split('.').pop().toLowerCase()
    const mimeMap = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
      ogv: 'video/ogg',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      mkv: 'video/x-matroska',
      flv: 'video/x-flv',
      wmv: 'video/x-ms-wmv',
      m4v: 'video/x-m4v',
      mpeg: 'video/mpeg',
      mpg: 'video/mpeg',
      '3gp': 'video/3gpp',
      '3g2': 'video/3gpp2'
    }
    return mimeMap[extension]
  }

  const handleSpecialMediaUpload = async (files, imageOnly = false) => {
    const selectedFiles = files && typeof files === 'object' && typeof files.name === 'string'
      ? [files]
      : Array.from(files || []).filter(Boolean)
    if (selectedFiles.length === 0) return

    const invalidFile = selectedFiles.find((file) => !validateMediaFile(file, imageOnly).valid)
    if (invalidFile) {
      toast.error(validateMediaFile(invalidFile, imageOnly).message)
      return
    }

    setUploadingMediaKey('special-media')

    try {
      const uploadResults = await Promise.allSettled(
        selectedFiles.map(async (file) => ({
          file,
          uploadResult: await uploadMediaFile(file)
        }))
      )
      const successfulUploads = uploadResults
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)

      if (successfulUploads.length === 0) {
        throw uploadResults.find((result) => result.status === 'rejected')?.reason || new Error('No files were uploaded')
      }

      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          specialMedia: [
            ...(prev.content.specialMedia || []),
            ...successfulUploads.map(({ file, uploadResult }) => ({
              id: createUniqueId('special-media'),
              url: uploadResult.url,
              videoUrl: uploadResult.resourceType === 'video' ? uploadResult.url : '',
              thumbnail: uploadResult.thumbnail || uploadResult.poster || '',
              poster: uploadResult.poster || uploadResult.thumbnail || '',
              publicId: uploadResult.publicId || '',
              source: 'upload',
              type: ACCEPTED_IMAGE_TYPES.includes(file.type) ? 'image' : 'video',
              display: 'content',
              alt: file.name,
              style: {
                width: '100%',
                height: 'auto',
                paddingLeft: '0px',
                paddingRight: '0px',
                marginLeft: '0px',
                marginRight: '0px'
              }
            }))
          ]
        }
      }))

      toast.success(`${successfulUploads.length} special ${imageOnly ? 'image' : 'media'} file${successfulUploads.length === 1 ? '' : 's'} uploaded`)
    } catch (err) {
      console.error('Error uploading special media:', err)
      const status = err.response?.status
      const backendMessage = err.response?.data?.message
      let message = backendMessage || 'Unexpected Error: Something went wrong while processing this media. Please try again.'
      if (status === 401 || status === 403) {
        message = 'Authentication Error: Your session has expired or you do not have permission to upload this media.'
      } else if (status >= 500) {
        message = 'Server Error: The server could not process this media upload. Please try again.'
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        message = 'Network Error: Unable to connect to the server. Please check your internet connection and try again.'
      } else if (backendMessage && /cloudinary|storage|media storage/i.test(backendMessage)) {
        message = `Media Storage Error: ${backendMessage}`
      }
      toast.error(message)
    } finally {
      setUploadingMediaKey(null)
    }
  }

  const handleAddSpecialMediaVideoUrl = (video) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        specialMedia: [
          ...(prev.content.specialMedia || []),
          {
            id: createUniqueId('special-media'),
            url: video.url,
            videoUrl: video.url,
            embedUrl: video.embedUrl,
            provider: video.provider,
            source: 'url',
            thumbnail: video.thumbnail,
            publicId: '',
            type: 'video',
            display: 'content',
            alt: `${video.provider} video`,
            style: { width: '100%', height: 'auto', paddingLeft: '0px', paddingRight: '0px', marginLeft: '0px', marginRight: '0px' }
          }
        ]
      }
    }))
    toast.success('Video URL added')
  }

  const handleAddSpecialMediaImageUrl = (image) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        specialMedia: [
          ...(prev.content.specialMedia || []),
          {
            id: createUniqueId('special-media'),
            url: image.url,
            source: 'url',
            publicId: '',
            type: 'image',
            display: 'content',
            alt: 'External image',
            style: { width: '100%', height: 'auto', paddingLeft: '0px', paddingRight: '0px', marginLeft: '0px', marginRight: '0px' }
          }
        ]
      }
    }))
    toast.success('Image URL added')
  }

  const handleAddSpecialMediaWebsiteUrl = (link) => {
    setFormData(prev => ({ ...prev, content: { ...prev.content, specialMedia: [...(prev.content.specialMedia || []), { id: createUniqueId('special-media'), url: link.url, source: 'url', type: 'link', display: 'content', alt: 'External website' }] } }))
    toast.success('Website URL added')
  }

  const handleSidebarUpload = async (file, side = 'left') => {
    if (!file) return

    const validation = validateMediaFile(file)
    if (!validation.valid) {
      toast.error(validation.message)
      return
    }

    setUploadingMediaKey(`sidebar-${side}`)

    try {
      const uploadResult = await uploadMediaFile(file)
      const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

      setFormData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          [getSidebarKey(side)]: {
            ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }),
            media: [
              ...((prev.content[getSidebarKey(side)] && prev.content[getSidebarKey(side)].media) || []),
              {
                id: Date.now(),
                url: uploadResult.url,
                publicId: uploadResult.publicId || '',
                type: isImage ? 'image' : 'video',
                alt: file.name
              }
            ]
          }
        }
      }))

      toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar media uploaded`)
    } catch (err) {
      console.error('Error uploading sidebar media:', err)
      toast.error(err.response?.data?.message || 'Failed to upload sidebar media')
    } finally {
      setUploadingMediaKey(null)
    }
  }

  const handleAddSidebarVideoUrl = (side, video) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [getSidebarKey(side)]: {
          ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }),
          media: [
            ...((prev.content[getSidebarKey(side)] && prev.content[getSidebarKey(side)].media) || []),
            {
              id: createUniqueId(`sidebar-${side}`),
              url: video.url,
              videoUrl: video.url,
              embedUrl: video.embedUrl,
              provider: video.provider,
              source: 'url',
              thumbnail: video.thumbnail,
              publicId: '',
              type: 'video',
              alt: `${video.provider} video`
            }
          ]
        }
      }
    }))
    toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar video URL added`)
  }

  const handleAddSidebarImageUrl = (side, image) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [getSidebarKey(side)]: {
          ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }),
          media: [
            ...((prev.content[getSidebarKey(side)] && prev.content[getSidebarKey(side)].media) || []),
            { id: createUniqueId(`sidebar-${side}`), url: image.url, source: 'url', publicId: '', type: 'image', alt: 'External image' }
          ]
        }
      }
    }))
    toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar image URL added`)
  }

  const handleAddSidebarWebsiteUrl = (side, link) => {
    setFormData(prev => ({ ...prev, content: { ...prev.content, [getSidebarKey(side)]: { ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }), media: [...((prev.content[getSidebarKey(side)] && prev.content[getSidebarKey(side)].media) || []), { id: createUniqueId(`sidebar-${side}`), url: link.url, source: 'url', type: 'link', alt: 'External website' }] } } }))
    toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar website URL added`)
  }

  const handleRemoveSpecialMedia = (id) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        specialMedia: (prev.content.specialMedia || []).filter(m => m.id !== id)
      }
    }))
  }

  const handleChangeSpecialDisplay = (id, display) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        specialMedia: (prev.content.specialMedia || []).map(m => m.id === id ? { ...m, display } : m)
      }
    }))
  }

  const handleChangeSpecialMediaStyle = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        specialMedia: (prev.content.specialMedia || []).map(m => {
          if (m.id !== id) return m
          return {
            ...m,
            style: {
              ...m.style,
              [field]: value
            }
          }
        })
      }
    }))
  }

  const handleRemoveSidebarMedia = (side, id) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [getSidebarKey(side)]: {
          ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }),
          media: ((prev.content[getSidebarKey(side)] && prev.content[getSidebarKey(side)].media) || []).filter(m => m.id !== id)
        }
      }
    }))
  }

  const handleSidebarContentChangeText = (side, text) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [getSidebarKey(side)]: {
          ...(prev.content[getSidebarKey(side)] || { contentText: '', media: [] }),
          contentText: text
        }
      }
    }))
  }

  const handleItemImageUpload = async (itemId, file, isHeroImage = false) => {
    if (!file) return

    if (isHeroImage && !isImageFile(file)) {
      toast.error('Please upload a valid image file')
      return
    }

    if (!isHeroImage && !isImageFile(file) && !isVideoFile(file)) {
      toast.error('Please upload a valid image or video file')
      return
    }

    const validation = validateMediaFile(file, isHeroImage)
    if (!validation.valid) {
      toast.error(validation.message)
      return
    }

    setUploadingMediaKey(isHeroImage ? 'hero-carousel' : `item-${itemId}`)

    try {
      const uploadResult = await uploadMediaFile(file)
      if (!uploadResult?.url) {
        throw new Error('Upload failed to return a valid URL')
      }

      if (isHeroImage) {
        // Handle Hero image - add to carousel array
        setFormData(prev => ({
          ...prev,
          content: {
            ...prev.content,
            heroImages: [
              ...(prev.content.heroImages || []),
              {
                id: `hero-${Date.now()}`,
                url: uploadResult.url,
                publicId: uploadResult.publicId || '',
                order: (prev.content.heroImages || []).length
              }
            ]
          }
        }))
        toast.success('Hero image added to carousel')
      } else {
        // Handle gallery items
        const isImage = isImageFile(file)
        const isVideo = isVideoFile(file)

        setFormData(prev => ({
          ...prev,
          content: {
            ...prev.content,
            items: prev.content.items.map(item =>
              item.id === itemId
                ? {
                    ...item,
                    gallery: [
                      ...(item.gallery || []),
                      {
                        id: createUniqueId('gallery-item'),
                        image: isImage ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
                        video: isVideo ? {
                          url: uploadResult.url,
                          publicId: uploadResult.publicId || '',
                          thumbnail: uploadResult.thumbnail || uploadResult.poster || '',
                          poster: uploadResult.poster || uploadResult.thumbnail || ''
                        } : { url: '', publicId: '', thumbnail: '', poster: '' },
                        title: file.name,
                        description: ''
                      }
                    ]
                  }
                : item
            )
          }
        }))

        toast.success('Image/Video uploaded successfully')
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error(error.response?.data?.message || 'Failed to upload file')
    } finally {
      setUploadingMediaKey(null)
    }
  }

  const handleAddItemVideoUrl = (itemId, video) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: prev.content.items.map(item => item.id === itemId ? {
          ...item,
          gallery: [
            ...(item.gallery || []),
            {
              id: createUniqueId('gallery-item'),
              image: { url: '', publicId: '' },
              video: {
                url: video.url,
                videoUrl: video.url,
                embedUrl: video.embedUrl,
                provider: video.provider,
                source: 'url',
                thumbnail: video.thumbnail,
                publicId: ''
              },
              title: `${video.provider} video`,
              description: ''
            }
          ]
        } : item)
      }
    }))
    toast.success('Video URL added to gallery')
  }

  const handleAddItemImageUrl = (itemId, image) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: prev.content.items.map(item => item.id === itemId ? {
          ...item,
          gallery: [
            ...(item.gallery || []),
            {
              id: createUniqueId('gallery-item'),
              image: { url: image.url, imageUrl: image.url, source: 'url', publicId: '' },
              title: 'External image',
              description: ''
            }
          ]
        } : item)
      }
    }))
    toast.success('Image URL added to gallery')
  }

  const handleAddItemWebsiteUrl = (itemId, link) => {
    setFormData(prev => ({ ...prev, content: { ...prev.content, items: prev.content.items.map(item => item.id === itemId ? { ...item, gallery: [...(item.gallery || []), { id: createUniqueId('gallery-item'), link: { url: link.url, source: 'url' }, title: 'External website', description: '' }] } : item) } }))
    toast.success('Website URL added to gallery')
  }

  const handleImageInputChange = (e, itemId) => {
    const file = e.target.files?.[0]
    if (file) {
      handleItemImageUpload(itemId, file)
    }
  }

  // Helper functions for carousel/hero images management
  const handleRemoveHeroImage = (id) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        heroImages: (prev.content.heroImages || []).filter(img => img.id !== id)
      }
    }))
    toast.success('Media removed from carousel')
  }

  const addHeroCarouselItem = (item) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        heroImages: [
          ...(prev.content.heroImages || []),
          { ...item, id: createUniqueId('hero'), order: (prev.content.heroImages || []).length }
        ]
      }
    }))
  }

  const handleAddHeroImageUrl = (image) => {
    addHeroCarouselItem({ type: 'image', source: 'url', url: image.url, publicId: '' })
    toast.success('Image URL added to carousel')
  }

  const handleAddHeroVideoUrl = (video) => {
    addHeroCarouselItem({
      type: 'video', source: 'url', url: video.url, videoUrl: video.url,
      embedUrl: video.embedUrl, provider: video.provider, thumbnail: video.thumbnail, publicId: ''
    })
    toast.success('Video URL added to carousel')
  }

  const handleAddHeroWebsiteUrl = (link) => {
    addHeroCarouselItem({ type: 'website', source: 'url', url: link.url, publicId: '' })
    toast.success('Website URL added to carousel')
  }

  const handleReorderHeroImages = (fromIndex, toIndex) => {
    setFormData(prev => {
      const newImages = [...(prev.content.heroImages || [])]
      const [movedImage] = newImages.splice(fromIndex, 1)
      newImages.splice(toIndex, 0, movedImage)
      // Update order property
      const reorderedImages = newImages.map((img, idx) => ({ ...img, order: idx }))
      return {
        ...prev,
        content: {
          ...prev.content,
          heroImages: reorderedImages
        }
      }
    })
  }

  const handleDropHeroImage = (toIndex) => {
    if (draggedHeroIndex === null || draggedHeroIndex === toIndex) return
    handleReorderHeroImages(draggedHeroIndex, toIndex)
    setDraggedHeroIndex(null)
  }


  const handleRemoveGalleryItem = (itemId, galleryItemId) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: prev.content.items.map(item =>
          item.id === itemId
            ? {
                ...item,
                gallery: (item.gallery || []).filter(g => g.id !== galleryItemId)
              }
            : item
        )
      }
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Section title is optional - allow empty for flexible layouts
    // if (!formData.title.trim()) {
    //   toast.error('Section title is required')
    //   return
    // }

    // For NEW sections, section type is required
    if (formData.isNew && !formData.type) {
      toast.error('Please select a section type')
      return
    }

    setSaving(true)
    try {
      const submitData = {
        title: formData.title,
            titleAvailable: formData.titleAvailable !== false,
        description: formData.description,
        enabled: formData.enabled === true,
        disabled: !formData.enabled,
        displayOrder: (function() { const v = parseInt(formData.displayOrder); return isNaN(v) ? 0 : v })(),
        styling: formData.styling,
        // sanitize content to match backend schema expectations
        content: (function sanitizeContent(ct) {
          if (!ct) return {}

          const pickHeroImage = (h) => ({
            url: h?.url || '',
            publicId: h?.publicId || ''
          })

          const sanitizeMediaArray = (arr) => ((arr || [])
            .map(m => ({
              id: m?.id || createUniqueId('special-media'),
              url: m?.url || '',
              videoUrl: m?.videoUrl || '',
              embedUrl: m?.embedUrl || '',
              provider: m?.provider || '',
              source: m?.source || 'upload',
              thumbnail: m?.thumbnail || '',
              publicId: m?.publicId || '',
              type: ['video', 'link'].includes(m?.type) ? m.type : 'image',
              display: m?.display === 'full' ? 'full' : 'content',
              alt: m?.alt || '',
              style: {
                width: m?.style?.width || '100%',
                height: m?.style?.height || 'auto',
                paddingLeft: m?.style?.paddingLeft || '0px',
                paddingRight: m?.style?.paddingRight || '0px',
                marginLeft: m?.style?.marginLeft || '0px',
                marginRight: m?.style?.marginRight || '0px'
              }
            }))
            .filter(m => typeof m.url === 'string' && m.url.trim() !== '')
          )

          const specialMedia = sanitizeMediaArray(ct.specialMedia).map(m => ({
            id: m.id,
            url: m.url,
            videoUrl: m.videoUrl,
            embedUrl: m.embedUrl,
            provider: m.provider,
            source: m.source,
            thumbnail: m.thumbnail,
            publicId: m.publicId,
            type: m.type,
            display: m.display,
            alt: m.alt,
            style: {
              width: m.style.width,
              height: m.style.height,
              paddingLeft: m.style.paddingLeft,
              paddingRight: m.style.paddingRight,
              marginLeft: m.style.marginLeft,
              marginRight: m.style.marginRight
            }
          }))

          const sanitizeSidebar = (bar) => ({
            contentText: (bar && bar.contentText) || '',
            textStyle: ['default', 'slightly-bold', 'bold'].includes(bar?.textStyle) ? bar.textStyle : 'default',
            media: sanitizeMediaArray(bar?.media)
          })

          const leftSidebar = sanitizeSidebar(ct.leftSidebar)
          const middleBar = sanitizeSidebar(ct.middleBar)
          const rightSidebar = sanitizeSidebar(ct.rightSidebar)

          const items = (ct.items || []).map(it => {
            const itemObj = {
              title: it?.title || '',
              description: it?.description || '',
              gallery: (it.gallery || []).map((g, galleryIndex) => {
                const galleryItem = {
                  id: g?.id || createUniqueId(`gallery-${it?.id || 'item'}-${galleryIndex}`),
                  title: g?.title || '',
                  description: g?.description || '',
                  alt: g?.alt || ''
                }
                
                // Include image if it has a valid URL
                if (g?.image?.url) {
                  galleryItem.image = { url: g.image.url, imageUrl: g.image.imageUrl || g.image.url, source: g.image.source || 'upload', publicId: g.image.publicId || '' }
                }
                
                // Include video if it has a valid URL
                if (g?.video?.url) {
                  galleryItem.video = {
                    url: g.video.url,
                    videoUrl: g.video.videoUrl || '',
                    embedUrl: g.video.embedUrl || '',
                    provider: g.video.provider || '',
                    source: g.video.source || 'upload',
                    publicId: g.video.publicId || '',
                    thumbnail: g.video.thumbnail || g.video.poster || '',
                    poster: g.video.poster || g.video.thumbnail || ''
                  }
                }

                if (g?.link?.url) {
                  galleryItem.link = { url: g.link.url, source: g.link.source || 'url' }
                }
                
                return galleryItem
              }).filter(g => (g.image && g.image.url) || (g.video && g.video.url) || (g.link && g.link.url))
            }
            
            // Include WhatsApp contact if provided
            if (it?.whatsappContact && typeof it.whatsappContact === 'string' && it.whatsappContact.trim()) {
              itemObj.whatsappContact = it.whatsappContact.trim()
            }
            
            // Include price fields
            if (it?.price !== undefined && it?.price !== null && it?.price !== '') {
              itemObj.price = Number(it.price) || 0
            }
            if (it?.currency && typeof it.currency === 'string' && it.currency.trim()) {
              itemObj.currency = it.currency.trim()
            }
            
            // Include any other fields that might be present
            if (it?.link) itemObj.link = it.link
            if (it?.image) itemObj.image = it.image
            if (it?.video) itemObj.video = it.video
            if (it?.name) itemObj.name = it.name
            if (it?.id) itemObj.id = it.id
            
            return itemObj
          })

          const logo = ct.logo ? { url: ct.logo.url || '', publicId: ct.logo.publicId || '', fileName: ct.logo.fileName || '' } : undefined

          // Sanitize team members array
          const teamMembers = Array.isArray(ct.teamMembers) ? ct.teamMembers.map(member => ({
            id: member?.id || Date.now(),
            name: (member?.name || '').trim(),
            position: (member?.position || '').trim(),
            bio: (member?.bio || '').trim(),
            profileImage: member?.profileImage ? {
              url: member.profileImage.url || '',
              publicId: member.profileImage.publicId || ''
            } : { url: '', publicId: '' }
          })).filter(member => member.name) : []

          // Sanitize heroImages array for carousel
          const heroImages = Array.isArray(ct.heroImages) 
            ? ct.heroImages
                .filter(img => img && (img.url || img.publicId))
                .map((img, idx) => ({
                  id: img.id || `hero-${idx}`,
                  url: img.url || '',
                  publicId: img.publicId || '',
                  type: ['image', 'video', 'website'].includes(img.type) ? img.type : 'image',
                  source: img.source === 'url' ? 'url' : 'upload',
                  videoUrl: img.videoUrl || '',
                  embedUrl: img.embedUrl || '',
                  provider: img.provider || '',
                  thumbnail: img.thumbnail || '',
                  order: typeof img.order === 'number' ? img.order : idx
                }))
            : []

          // Sanitize carousel settings
          const carouselSettings = ct.carouselSettings ? {
            autoPlay: ct.carouselSettings.autoPlay !== false,
            interval: Math.max(1000, Math.min(30000, ct.carouselSettings.interval || 5000)),
            transitionSpeed: Math.max(300, Math.min(3000, ct.carouselSettings.transitionSpeed || 1000)),
            showIndicators: ct.carouselSettings.showIndicators !== false,
            showNavigation: ct.carouselSettings.showNavigation !== false
          } : {
            autoPlay: true,
            interval: 5000,
            transitionSpeed: 1000,
            showIndicators: true,
            showNavigation: true
          }

          const audio = Array.isArray(ct.audio)
            ? ct.audio
                .map(audioItem => ({
                  id: audioItem?.id || Date.now().toString(),
                  name: (audioItem?.name || '').trim(),
                  description: (audioItem?.description || '').trim(),
                  banner: {
                    url: audioItem?.banner?.url || '',
                    publicId: audioItem?.banner?.publicId || '',
                    filename: audioItem?.banner?.filename || ''
                  },
                  audioFile: {
                    url: audioItem?.audioFile?.url || '',
                    publicId: audioItem?.audioFile?.publicId || '',
                    filename: audioItem?.audioFile?.filename || ''
                  }
                }))
                .filter(item => item.name && item.audioFile?.url)
            : []

          return {
            heading: ct.heading || '',
            headingAvailable: ct.headingAvailable !== false,
            donationName: typeof ct.donationName === 'string' ? ct.donationName.trim() : '',
            donationDescription: typeof ct.donationDescription === 'string' ? ct.donationDescription.trim() : '',
            headingPosition: ['left', 'center', 'right'].includes(ct.headingPosition) ? ct.headingPosition : 'left',
            subheading: ct.subheading || '',
            subheadingPosition: ['left', 'center', 'right'].includes(ct.subheadingPosition) ? ct.subheadingPosition : 'left',
            subheadingTextStyle: ['default', 'slightly-bold', 'bold'].includes(ct.subheadingTextStyle) ? ct.subheadingTextStyle : 'default',
            ctaText: (ct.ctaText || '').toString().trim(),
            ctaLink: (ct.ctaLink || '').toString().trim(),
            heroDisplayMode: ct.heroDisplayMode || 'contain',
            heroImage: ct.heroImage ? pickHeroImage(ct.heroImage) : undefined,
            heroImages: heroImages.length > 0 ? heroImages : undefined,
            carouselSettings: heroImages.length > 0 ? carouselSettings : undefined,
            heroOverlay: ct.heroOverlay || undefined,
            heroButtons: Array.isArray(ct.heroButtons) ? ct.heroButtons.map(hb => ({ id: hb.id, text: hb.text, link: hb.link, style: hb.style })) : [],
            buyRate: typeof ct.buyRate === 'number' ? ct.buyRate : undefined,
            sellRate: typeof ct.sellRate === 'number' ? ct.sellRate : undefined,
            usdtBuyRate: typeof ct.usdtBuyRate === 'number' ? ct.usdtBuyRate : undefined,
            usdtSellRate: typeof ct.usdtSellRate === 'number' ? ct.usdtSellRate : undefined,
            buyEnabled: ct.buyEnabled !== undefined ? Boolean(ct.buyEnabled) : undefined,
            sellEnabled: ct.sellEnabled !== undefined ? Boolean(ct.sellEnabled) : undefined,
            giftCardEnabled: ct.giftCardEnabled !== undefined ? Boolean(ct.giftCardEnabled) : undefined,
            giftCardWhatsApp: typeof ct.giftCardWhatsApp === 'string' ? ct.giftCardWhatsApp.trim() : undefined,
            giftCardNames: Array.isArray(ct.giftCardNames) ? ct.giftCardNames.map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean) : undefined,
            backgroundImage: pickHeroImage(ct.backgroundImage || {}),
            backgroundVideo: pickHeroImage(ct.backgroundVideo || {}),
            logo,
            specialMedia,
            leftSidebar,
            middleBar,
            rightSidebar,
            items,
            teamMembers,
            gallery: (ct.gallery || []).map(g => ({ title: g.title || '', description: g.description || '', image: g.image?.url ? { url: g.image.url, publicId: g.image.publicId || '' } : undefined, video: g.video?.url ? { url: g.video.url, publicId: g.video.publicId || '' } : undefined, alt: g.alt || '' })).filter(g => (g.image && g.image.url) || (g.video && g.video.url)),
            audio
          }
        })(formData.content)
      }

      // Include sectionType for new sections or when it was changed during edit
      if (formData.type || (formData.isNew && formData.type !== undefined)) {
        const currentType = section?.sectionType || section?.type || null
        if (formData.isNew || formData.type !== currentType) {
          submitData.sectionType = formData.type
        }
      }

      console.log('[SectionEditorModal] ===== SUBMIT START =====')
      console.log('[SectionEditorModal] Submitting:', {
        isNew: formData.isNew,
        title: submitData.title,
        sectionType: submitData.sectionType,
        hasHeroImage: !!submitData.content?.heroImage?.url,
        heroImageSize: submitData.content?.heroImage?.url ? Math.round(submitData.content.heroImage.url.length / 1024) + ' KB' : 'N/A',
        heroImagesCount: submitData.content?.heroImages?.length || 0,
        carouselSettingsAutoPlay: submitData.content?.carouselSettings?.autoPlay,
        carouselSettingsInterval: submitData.content?.carouselSettings?.interval
      })
      if (submitData.content?.heroImages && submitData.content.heroImages.length > 0) {
        console.log('[SectionEditorModal] CAROUSEL DATA:', {
          imageCount: submitData.content.heroImages.length,
          images: submitData.content.heroImages.map((img, idx) => ({
            index: idx,
            id: img.id,
            hasUrl: !!img.url,
            urlSize: img.url ? Math.round(img.url.length / 1024) + ' KB' : 'N/A',
            order: img.order
          })),
          settings: submitData.content.carouselSettings
        })
      }
      console.log('[SectionEditorModal] ITEMS WITH PRICE AND GALLERY:', (submitData.content?.items || []).map((item, idx) => ({
        itemIndex: idx,
        title: item.title,
        price: item.price,
        currency: item.currency,
        whatsappContact: item.whatsappContact,
        galleryCount: item.gallery?.length || 0,
        galleryItems: (item.gallery || []).map((g, gi) => ({
          galleryIndex: gi,
          hasImage: !!g.image?.url,
          hasVideo: !!g.video?.url,
          imageSize: g.image?.url ? Math.round(g.image.url.length / 1024) + ' KB' : 'N/A',
          videoSize: g.video?.url ? Math.round(g.video.url.length / 1024) + ' KB' : 'N/A'
        }))
      })))
      console.log('[SectionEditorModal] TEAM MEMBERS:', submitData.content?.teamMembers ? submitData.content.teamMembers.length : 0, submitData.content?.teamMembers ? submitData.content.teamMembers.map((m, idx) => ({
        index: idx,
        name: m.name,
        position: m.position,
        hasImage: !!m.profileImage?.url,
        imageSize: m.profileImage?.url ? Math.round(m.profileImage.url.length / 1024) + ' KB' : 'N/A'
      })) : 'none')
      console.log('[SectionEditorModal] CTA FIELDS SUBMITTED:', {
        hasCtaText: !!submitData.content?.ctaText,
        hasCtaLink: !!submitData.content?.ctaLink,
        ctaText: submitData.content?.ctaText,
        ctaLink: submitData.content?.ctaLink,
        isValid: !!submitData.content?.ctaText && !!submitData.content?.ctaLink
      })
      console.log('[SectionEditorModal] Full submit data keys:', Object.keys(submitData))
      console.log('[SectionEditorModal] Content keys:', Object.keys(submitData.content || {}))
      if (submitData.content?.heroImage) {
        console.log('[SectionEditorModal] Hero Image Object:', {
          hasUrl: !!submitData.content.heroImage.url,
          hasPublicId: !!submitData.content.heroImage.publicId,
          urlLength: submitData.content.heroImage.url?.length || 0,
          urlPreview: submitData.content.heroImage.url?.substring(0, 50)
        })
      }
      // Await the async onSubmit callback and it will handle closing modal
      await onSubmit(submitData)
      console.log('[Modal] onSubmit completed successfully - modal will close via parent')
    } catch (error) {
      // Error is already handled and toasted in parent, just log it
      console.error('[Modal] onSubmit failed (error handled by parent):', error.message)
      // Don't show another toast - parent already showed one
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">
            {formData.isNew ? 'Create New Section' : 'Edit Section'}
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Hero Section Banner */}
          {formData.type === 'hero' && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🎯</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 mb-1">Hero Section</h3>
                  <p className="text-sm text-blue-700">
                    This is your website's hero/banner section. It appears at the top of your website with a full-width background image.
                    The hero section cannot be deleted but can be edited and temporarily hidden.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Section Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title (optional)</label>
                {/* Carousel Settings */}
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter section title (optional)"
                  className="input-field w-full"
                />
                <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">Section Title Availability</label>
                <select
                  value={formData.titleAvailable === false ? 'not-available' : 'available'}
                  onChange={(e) => setFormData(prev => ({ ...prev, titleAvailable: e.target.value === 'available' }))}
                  className="input-field w-full"
                >
                  <option value="available">Available</option>
                  <option value="not-available">Not Available</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="input-field w-full"
                >
                  <option value="">Select type</option>
                  {SECTION_TYPES.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of this section"
                rows="2"
                className="input-field w-full resize-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                Section is enabled
              </label>
            </div>
          </div>

          {/* Products Management - Show when products section type is selected */}
          {formData.type === 'products' && website ? (
            <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Product Management</h3>
              <ProductsManagement website={website} section={{ ...section, type: formData.type }} />
            </div>
          ) : null}

          {formData.type === 'donation' && (
            <div className="pt-6 border-t border-gray-200 bg-emerald-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Donation Control</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Donation Name (optional)</label>
                  <input
                    type="text"
                    value={formData.content.donationName || ''}
                    onChange={(e) => handleContentChange('donationName', e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Donation Description (optional)</label>
                  <textarea
                    value={formData.content.donationDescription || ''}
                    onChange={(e) => handleContentChange('donationDescription', e.target.value)}
                    rows="3"
                    className="input-field w-full resize-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tickets Management - Show when tickets section type is selected */}
          {formData.type === 'tickets' && website ? (
            <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🎫 Ticket Management</h3>
              <TicketsManagement website={website} section={{ ...section, type: formData.type }} />
            </div>
          ) : null}

          {/* Contact Form - Show message when contact section type is selected */}
            {formData.type === 'contact' && website ? (
              <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📧 Contact Form</h3>
                <p className="text-sm text-gray-700">
                  A contact form will be displayed on your public website for this section after you save the settings.
                </p>
              </div>
            ) : null}

          {/* Reviews Section - Show when reviews section type is selected */}
          {formData.type === 'reviews' && (
            <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">⭐ Customer Reviews Section</h3>
              <p className="text-sm text-gray-700 mb-3">
                This section displays customer reviews and allows visitors to submit new reviews.
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>✓ Visitors can submit reviews with a comment and optional star rating</li>
                <li>✓ All reviews are saved as "pending" until you approve them</li>
                <li>✓ Only approved reviews appear on the public website</li>
                <li>✓ Manage reviews (approve/reject/delete) in the "Reviews" dashboard tab</li>
              </ul>
            </div>
          )}

          {/* Fixtures Section - Show when fixtures section type is selected */}
          {formData.type === 'fixtures' && website && (
            <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 Football Fixtures Management</h3>
              <p className="text-sm text-gray-700 mb-4">
                Manage football match fixtures and view automated league standings.
              </p>
              <FixturesManagement website={website} section={{ ...section, type: formData.type }} />
            </div>
          )}

          {/* Points Table Section - Show when points-table section type is selected */}
          {formData.type === 'points-table' && (
            <div className="pt-6 border-t border-gray-200 bg-indigo-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 League Points Table</h3>
              <p className="text-sm text-gray-700 mb-3">
                This section displays the league standings and is automatically updated when fixtures are completed.
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>✓ Standings are calculated automatically from finished fixtures</li>
                <li>✓ Table shows: Position, Team, Played, Wins, Draws, Losses, Goals For/Against, Goal Difference, Points</li>
                <li>✓ Teams are ranked by points, then goal difference, then goals for</li>
                <li>✓ Points: Win = 3, Draw = 1, Loss = 0</li>
              </ul>
            </div>
          )}

          {/* Resume Upload Section - Show when resume section type is selected */}
          {formData.type === 'resume' && website && (
            <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Resume Upload Section</h3>
              <p className="text-sm text-gray-700 mb-4">
                This section allows visitors to submit their resumes for job positions.
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-4">
                <li>✓ Visitors can upload PDF, DOC, DOCX, or TXT files (max 5MB)</li>
                <li>✓ Submitted resumes appear in your Resume Dashboard</li>
                <li>✓ You receive Telegram notifications for new submissions</li>
                <li>✓ Track candidate status: Submitted → Reviewed → Shortlisted/Rejected</li>
              </ul>
              
              {/* Resume Section Toggle */}
              <div className="mb-4 p-3 bg-white rounded border">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="resumeToggle"
                    name="enabled"
                    checked={formData.enabled}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="resumeToggle" className="text-sm font-medium text-gray-700">
                    Resume form is visible on public website
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Turn ON to show the resume submission form to visitors. Turn OFF to hide it completely.
                </p>
              </div>
              
              <ResumesManagement website={website} />
            </div>
          )}

          {/* Team Members Section - Show when team section type is selected */}
          {formData.type === 'team' && (
            <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">👥 Team Members Management</h3>
              <p className="text-sm text-gray-700 mb-4">
                Create and manage your team members. Each team member can have a profile picture, name, position, and bio.
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
                <li>✓ Add multiple team members with full names and roles</li>
                <li>✓ Upload profile pictures for each team member</li>
                <li>✓ Add optional bio/description for each member</li>
                <li>✓ Team will display in a responsive grid on your public website</li>
                <li>✓ Fully responsive layout for mobile, tablet, and desktop</li>
              </ul>
              
              <TeamMembersEditor formData={formData} setFormData={setFormData} />
            </div>
          )}
          {/* Audio Section - Show when audio section type is selected */}
          {formData.type === 'audio' && (
            <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">🎵 Audio Section</h3>
              <p className="text-sm text-gray-700 mb-4">
                Add audio files to your website. Visitors can play audio directly on your site.
              </p>
              <AudioManagement formData={formData} setFormData={setFormData} />
            </div>
          )}

          {/* Crypto Product Section - Show when crypto-product section type is selected */}
          {formData.type === 'crypto-product' && (
            <div className="pt-6 border-t border-gray-200 bg-orange-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">₿ Crypto Trading Section</h3>
              <p className="text-sm text-gray-700 mb-4">
                Enable cryptocurrency buy/sell functionality on your website. Users can purchase or sell crypto directly through secure payment processing.
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
                <li>✓ Support for multiple cryptocurrencies (BTC, ETH, USDT, BNB, ADA, SOL, DOT, DOGE, XRP, LTC)</li>
                <li>✓ Secure buy/sell transactions with payment provider integration</li>
                <li>✓ Real-time exchange rates with 10-minute price locking</li>
                <li>✓ Automatic payment detection and status updates</li>
                <li>✓ Transaction dashboard for managing all orders</li>
                <li>✓ Admin notifications for completed transactions</li>
              </ul>

              {/* Buy/Sell Toggle Controls */}
              <div className="bg-white rounded-lg p-4 border mb-4">
                <h4 className="font-semibold text-gray-900 mb-4">Feature Controls</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Enable Buy Crypto</label>
                      <p className="text-xs text-gray-500 mt-1">Allow users to buy cryptocurrency</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.content.buyEnabled !== false}
                        onChange={(e) => handleContentChange('buyEnabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Enable Sell Crypto</label>
                      <p className="text-xs text-gray-500 mt-1">Allow users to sell cryptocurrency</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.content.sellEnabled !== false}
                        onChange={(e) => handleContentChange('sellEnabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Rate Configuration */}
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Exchange Rate Configuration</h4>
                  {(formData.content.buyRate || formData.content.sellRate || formData.content.usdtBuyRate || formData.content.usdtSellRate) && (
                    <button
                      type="button"
                      onClick={() => {
                        handleContentChange('buyRate', null)
                        handleContentChange('sellRate', null)
                        handleContentChange('usdtBuyRate', null)
                        handleContentChange('usdtSellRate', null)
                      }}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded border border-red-300"
                    >
                      Clear All Rates
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      BTC Buy Rate (₦ per BTC)
                    </label>
                    <input
                      type="number"
                      value={formData.content.buyRate || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          handleContentChange('buyRate', null)
                        } else {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num > 0) {
                            handleContentChange('buyRate', num)
                          }
                        }
                      }}
                      placeholder="e.g., 50000000"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Rate at which users buy BTC (in Naira)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      BTC Sell Rate (₦ per BTC)
                    </label>
                    <input
                      type="number"
                      value={formData.content.sellRate || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          handleContentChange('sellRate', null)
                        } else {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num > 0) {
                            handleContentChange('sellRate', num)
                          }
                        }
                      }}
                      placeholder="e.g., 48000000"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Rate at which users sell BTC (in Naira)
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      USDT Buy Rate (₦ per USDT)
                    </label>
                    <input
                      type="number"
                      value={formData.content.usdtBuyRate || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          handleContentChange('usdtBuyRate', null)
                        } else {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num > 0) {
                            handleContentChange('usdtBuyRate', num)
                          }
                        }
                      }}
                      placeholder="e.g., 1500"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Specific buy rate for USDT transactions
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      USDT Sell Rate (₦ per USDT)
                    </label>
                    <input
                      type="number"
                      value={formData.content.usdtSellRate || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          handleContentChange('usdtSellRate', null)
                        } else {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num > 0) {
                            handleContentChange('usdtSellRate', num)
                          }
                        }
                      }}
                      placeholder="e.g., 1450"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Specific sell rate for USDT transactions
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Rates must be valid numeric values greater than 0. These rates will be displayed on your public website and used for all transaction calculations.
                    Update them regularly to reflect current market conditions. Use "Clear Rates" to remove rates entirely.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">Gift Card / Airtime Settings</h4>
                    <p className="text-sm text-gray-500">Configure the gift card option shown on public crypto pages.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.content.giftCardEnabled !== false}
                      onChange={(e) => handleContentChange('giftCardEnabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp Number for Gift Card Requests
                    </label>
                    <input
                      type="text"
                      value={formData.content.giftCardWhatsApp || ''}
                      onChange={(e) => handleContentChange('giftCardWhatsApp', e.target.value)}
                      placeholder="e.g., +2348012345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This number will be used to prefill WhatsApp messages when customers submit a gift card request.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Gift Card / Airtime Types</label>
                      <button
                        type="button"
                        onClick={handleAddGiftCardName}
                        className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded border border-orange-200 hover:bg-orange-200"
                      >
                        Add type
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Add the gift card names that users can select when requesting gift card payment.
                    </p>
                    <div className="space-y-3">
                      {(formData.content.giftCardNames || []).map((name, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => handleUpdateGiftCardName(index, e.target.value)}
                            placeholder={`Gift card type ${index + 1}`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveGiftCardName(index)}
                            className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded border border-red-200 hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Crypto trading requires proper payment provider setup and compliance with local regulations.
                  Contact support for payment provider integration assistance.
                </p>
              </div>
            </div>
          )}

          {/* Generic Content Fields - Show for all section types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heading
              </label>
              <input
                type="text"
                value={formData.content.heading}
                onChange={(e) => handleContentChange('heading', e.target.value)}
                placeholder="Main heading"
                className="input-field w-full"
              />
              <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">Heading Availability</label>
              <select
                value={formData.content.headingAvailable === false ? 'not-available' : 'available'}
                onChange={(e) => handleContentChange('headingAvailable', e.target.value === 'available')}
                className="input-field w-full"
              >
                <option value="available">Available</option>
                <option value="not-available">Not Available</option>
              </select>
              <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">
                Heading Position
              </label>
              <select
                value={formData.content.headingPosition || 'left'}
                onChange={(e) => handleContentChange('headingPosition', e.target.value)}
                className="input-field w-full"
              >
                <option value="left">Left</option>
                <option value="center">Middle / Center</option>
                <option value="right">Right</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subheading
              </label>
              <textarea
                value={formData.content.subheading}
                onChange={(e) => handleContentChange('subheading', e.target.value)}
                placeholder="Subheading or description"
                rows="2"
                className="input-field w-full resize-none"
              />
              <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">
                Subheading Position
              </label>
              <select
                value={formData.content.subheadingPosition || 'left'}
                onChange={(e) => handleContentChange('subheadingPosition', e.target.value)}
                className="input-field w-full"
              >
                <option value="left">Left</option>
                <option value="center">Middle / Center</option>
                <option value="right">Right</option>
              </select>
              <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">
                Subheading Text Style
              </label>
              <select
                value={formData.content.subheadingTextStyle || 'default'}
                onChange={(e) => handleContentChange('subheadingTextStyle', e.target.value)}
                className="input-field w-full"
              >
                <option value="default">Default</option>
                <option value="slightly-bold">Slightly Bold</option>
                <option value="bold">Bold</option>
              </select>
            </div>

            {/* Special Media Upload (applies to section) - moved up for visibility */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">📁 Special Media Upload</h3>
              <p className="text-sm text-gray-600 mb-3">Upload images or videos that will appear in this section on the public website. You can choose to display them full-width or constrained to the content width. There are no file size limits.</p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm text-center ${uploadingMediaKey === 'special-media' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
                  {uploadingMediaKey === 'special-media' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                  {uploadingMediaKey === 'special-media' ? 'Uploading...' : 'Upload Images'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      handleSpecialMediaUpload(e.target.files, true)
                      e.target.value = ''
                    }}
                    className="hidden"
                  />
                </label>
                <label className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm text-center ${uploadingMediaKey === 'special-media' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
                  {uploadingMediaKey === 'special-media' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                  {uploadingMediaKey === 'special-media' ? 'Uploading...' : 'Upload Media'}
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => {
                      handleSpecialMediaUpload(e.target.files?.[0])
                      e.target.value = ''
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="mt-3">
                <VideoUrlInput onAdd={handleAddSpecialMediaVideoUrl} disabled={uploadingMediaKey === 'special-media'} />
                <div className="mt-2"><ImageUrlInput onAdd={handleAddSpecialMediaImageUrl} disabled={uploadingMediaKey === 'special-media'} /></div>
                <div className="mt-2"><WebsiteUrlInput onAdd={handleAddSpecialMediaWebsiteUrl} disabled={uploadingMediaKey === 'special-media'} /></div>
              </div>

              {(formData.content.specialMedia || []).length > 1 && (
                <div className="mt-4 overflow-hidden rounded-lg border border-blue-200 bg-gray-50 p-3">
                  <p className="mb-2 text-xs font-medium text-gray-600">Special Media carousel preview</p>
                  <SpecialMediaImageCarousel
                    images={formData.content.specialMedia}
                    display="content"
                    getImageStyle={(media) => ({
                      width: '100%',
                      height: media.type === 'image' ? 'auto' : '100%',
                      maxWidth: '100%',
                      maxHeight: '320px',
                      objectFit: 'contain'
                    })}
                    getWrapperStyle={() => ({ minHeight: '180px' })}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
                {(formData.content.specialMedia || []).map(m => (
                  <div key={m.id} className="relative flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    {m.type === 'image' ? (
                      <img src={m.url} alt={m.alt || 'media'} className="h-40 w-full object-cover sm:h-36" />
                    ) : m.type === 'link' ? (
                      <WebsiteUrlCard url={m.url} className="h-40 sm:h-36" />
                    ) : (
                      m.embedUrl ? <VideoUrlFrame video={m} className="h-40 sm:h-36" /> : (
                        <video className="h-40 w-full object-cover sm:h-36" controls playsInline preload="metadata">
                          <source src={m.url} type={getVideoMimeType(m.url)} />
                          Your browser does not support video playback.
                        </video>
                      )
                    )}
                    <div className="flex flex-1 flex-col gap-3 p-3 sm:p-4">
                      <select value={m.display || 'content'} onChange={(e) => handleChangeSpecialDisplay(m.id, e.target.value)} className="input-field w-full">
                        <option value="content">Aligned with content width</option>
                        <option value="full">Full width</option>
                      </select>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Width
                          <input
                            type="text"
                            value={m.style?.width || '100%'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'width', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. 100%, 400px"
                          />
                        </label>
                        <label className="block text-xs font-medium text-gray-600">
                          Height
                          <input
                            type="text"
                            value={m.style?.height || 'auto'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'height', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. auto, 300px"
                          />
                        </label>
                        <label className="block text-xs font-medium text-gray-600">
                          Padding Left
                          <input
                            type="text"
                            value={m.style?.paddingLeft || '0px'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingLeft', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. 0px, 1rem"
                          />
                        </label>
                        <label className="block text-xs font-medium text-gray-600">
                          Padding Right
                          <input
                            type="text"
                            value={m.style?.paddingRight || '0px'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingRight', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. 0px, 1rem"
                          />
                        </label>
                        <label className="block text-xs font-medium text-gray-600">
                          Margin Left
                          <input
                            type="text"
                            value={m.style?.marginLeft || '0px'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginLeft', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. 0px, 5%"
                          />
                        </label>
                        <label className="block text-xs font-medium text-gray-600">
                          Margin Right
                          <input
                            type="text"
                            value={m.style?.marginRight || '0px'}
                            onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginRight', e.target.value)}
                            className="input-field mt-1 w-full"
                            placeholder="e.g. 0px, 5%"
                          />
                        </label>
                      </div>

                      <div className="flex items-center justify-between gap-3 pt-1">
                        <button type="button" onClick={() => handleRemoveSpecialMedia(m.id)} className="text-sm text-red-600">Remove</button>
                        <span className="text-xs text-gray-500">{m.type}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Three independently configured bars */}
            <div className="pt-6 border-t border-gray-200 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {['left', 'middle', 'right'].map((side) => {
                const sidebarKey = getSidebarKey(side)
                const sidebar = formData.content[sidebarKey] || { contentText: '', textStyle: 'default', media: [] }
                const uploadKey = `sidebar-${side}`
                const label = getSidebarLabel(side)

                return (
                  <div key={side} className="bg-gray-50 p-4 rounded-lg min-w-0">
                    <h4 className="font-semibold mb-2">{label}</h4>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
                    <textarea value={sidebar.contentText || ''} onChange={(e) => handleSidebarContentChangeText(side, e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text style</label>
                    <select value={sidebar.textStyle || 'default'} onChange={(e) => handleSidebarTextStyleChange(side, e.target.value)} className="input-field w-full mb-2">
                      <option value="default">Default</option>
                      <option value="slightly-bold">Slightly Bold</option>
                      <option value="bold">Bold</option>
                    </select>
                    <label className={`flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === uploadKey ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
                      {uploadingMediaKey === uploadKey ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                      {uploadingMediaKey === uploadKey ? 'Uploading...' : `Upload ${label} Media`}
                      <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, side); e.target.value = '' }} className="hidden" />
                    </label>
                    <div className="mt-2"><VideoUrlInput onAdd={(video) => handleAddSidebarVideoUrl(side, video)} disabled={uploadingMediaKey === uploadKey} /></div>
                    <div className="mt-2"><ImageUrlInput onAdd={(image) => handleAddSidebarImageUrl(side, image)} disabled={uploadingMediaKey === uploadKey} /></div>
                    <div className="mt-2"><WebsiteUrlInput onAdd={(link) => handleAddSidebarWebsiteUrl(side, link)} disabled={uploadingMediaKey === uploadKey} /></div>
                    <div className="mt-3 space-y-2">
                      {(sidebar.media || []).map(m => (
                        <div key={m.id} className="flex items-center gap-2 min-w-0">
                          {m.type === 'image' ? (
                            <img src={m.url} alt={m.alt} className="w-16 h-16 object-contain rounded bg-white flex-shrink-0" />
                          ) : m.type === 'link' ? (
                            <WebsiteUrlCard url={m.url} className="w-16 h-16 flex-shrink-0" />
                          ) : m.embedUrl ? (
                            <VideoUrlFrame video={m} className="w-16 h-16 flex-shrink-0" />
                          ) : (
                            <video className="w-16 h-16 object-contain rounded bg-white flex-shrink-0" controls playsInline preload="metadata">
                              <source src={m.url} type={getVideoMimeType(m.url)} />
                            </video>
                          )}
                          <div className="flex-1 min-w-0 text-sm text-gray-700 truncate">{m.alt}</div>
                          <button type="button" className="text-red-600 text-sm flex-shrink-0" onClick={() => handleRemoveSidebarMedia(side, m.id)}>Remove</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Hero Image Carousel Upload - Show when hero section type is selected */}
            {formData.type === 'hero' && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-5 rounded-lg border border-purple-200 space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-bold text-gray-900">
                    🎠 Hero Image Carousel
                  </label>
                  {formData.content.heroImages && formData.content.heroImages.length > 0 && (
                    <span className="text-xs font-semibold bg-purple-200 text-purple-900 px-3 py-1 rounded-full">
                      {formData.content.heroImages.length} image{formData.content.heroImages.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                
                {/* Hero Images Gallery Preview */}
                {formData.content.heroImages && formData.content.heroImages.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-600 font-medium">Images in carousel (drag to reorder):</p>
                    <div className="space-y-2 bg-white p-3 rounded-lg border border-purple-100">
                      {formData.content.heroImages.map((img, idx) => (
                        <div
                          key={img.id}
                          draggable
                          onDragStart={() => setDraggedHeroIndex(idx)}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => handleDropHeroImage(idx)}
                          onDragEnd={() => setDraggedHeroIndex(null)}
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200 hover:border-purple-300 transition-colors group cursor-grab"
                        >
                          <div className="flex-shrink-0 text-gray-500 font-semibold text-sm w-6 text-center">
                            {idx + 1}
                          </div>
                          {img.type === 'video' ? (
                            <VideoUrlFrame video={img} className="w-16 h-16" />
                          ) : img.type === 'website' ? (
                            <WebsiteUrlCard url={img.url} className="w-16 h-16" />
                          ) : img.url ? (
                            <img
                              src={img.url}
                              alt={`Hero carousel ${idx + 1}`}
                              className="w-16 h-16 object-cover rounded border border-gray-300"
                              onError={(event) => { event.currentTarget.style.display = 'none' }}
                            />
                          ) : null}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-600 truncate">
                              {img.url ? `${img.type === 'website' ? 'Website' : img.type === 'video' ? 'Video' : 'Image'} ${idx + 1} • ${img.source === 'url' ? 'URL media' : `${Math.round(img.url.length / 1024)}KB`}` : 'Pending...'}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {idx > 0 && (
                              <button
                                type="button"
                                onClick={() => handleReorderHeroImages(idx, idx - 1)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Move up"
                              >
                                ↑
                              </button>
                            )}
                            {idx < formData.content.heroImages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => handleReorderHeroImages(idx, idx + 1)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Move down"
                              >
                                ↓
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveHeroImage(img.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Remove image"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Carousel Settings */}
                    <div className="bg-white p-3 rounded-lg border border-purple-100 space-y-2">
                      <p className="text-xs font-medium text-gray-700">Carousel Settings:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.content.carouselSettings?.autoPlay !== false}
                            onChange={(e) => handleContentChange('carouselSettings', {
                              ...formData.content.carouselSettings,
                              autoPlay: e.target.checked
                            })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-xs text-gray-700">Auto-play</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.content.carouselSettings?.showIndicators !== false}
                            onChange={(e) => handleContentChange('carouselSettings', {
                              ...formData.content.carouselSettings,
                              showIndicators: e.target.checked
                            })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-xs text-gray-700">Show dots</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.content.carouselSettings?.showNavigation !== false}
                            onChange={(e) => handleContentChange('carouselSettings', {
                              ...formData.content.carouselSettings,
                              showNavigation: e.target.checked
                            })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-xs text-gray-700">Show arrows</span>
                        </label>
                        <div>
                          <label className="text-xs text-gray-700">Interval (ms):</label>
                          <input
                            type="number"
                            min="1000"
                            max="30000"
                            step="1000"
                            value={formData.content.carouselSettings?.interval || 5000}
                            onChange={(e) => handleContentChange('carouselSettings', {
                              ...formData.content.carouselSettings,
                              interval: parseInt(e.target.value)
                            })}
                            className="input-field w-full text-xs"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Image Display Mode */}
                    <div className="bg-white p-3 rounded-lg border border-purple-100 space-y-2">
                      <label className="text-xs font-medium text-gray-700">Image Display Mode</label>
                      <select
                        value={formData.content.heroDisplayMode}
                        onChange={(e) => handleContentChange('heroDisplayMode', e.target.value)}
                        className="input-field w-full text-sm"
                      >
                        <option value="contain">Contain (default)</option>
                        <option value="cover">Cover (crop to fill)</option>
                        <option value="original">Original Size</option>
                        <option value="auto">Responsive Auto</option>
                      </select>
                      <p className="text-xs text-gray-500">Default is 'Contain' — shows full image without cropping.</p>
                    </div>
                  </div>
                )}
                
                {/* Upload Button */}
                <label className={`flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-none rounded font-medium text-sm transition-all shadow-md ${uploadingMediaKey === 'hero-carousel' ? 'cursor-not-allowed opacity-75' : 'hover:from-purple-600 hover:to-blue-600 cursor-pointer hover:shadow-lg'}`}>
                  {uploadingMediaKey === 'hero-carousel' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                  {uploadingMediaKey === 'hero-carousel' ? 'Uploading...' : (formData.content.heroImages && formData.content.heroImages.length > 0 ? 'Add More Images' : 'Upload Hero Images')}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files
                      if (files) {
                        Array.from(files).forEach(file => {
                          handleItemImageUpload(null, file, true)
                        })
                      }
                    }}
                    className="hidden"
                  />
                </label>
                <div className="space-y-2">
                  <ImageUrlInput onAdd={handleAddHeroImageUrl} disabled={uploadingMediaKey === 'hero-carousel'} />
                  <VideoUrlInput onAdd={handleAddHeroVideoUrl} disabled={uploadingMediaKey === 'hero-carousel'} />
                  <WebsiteUrlInput onAdd={handleAddHeroWebsiteUrl} disabled={uploadingMediaKey === 'hero-carousel'} />
                </div>
                <p className="text-xs text-gray-600">
                  ✨ Upload 1 image for a static hero, or multiple images for an auto-sliding carousel!<br/>
                  Recommended: 1920x600px or larger. Supports JPEG, PNG, GIF, WebP. No file size limits.
                </p>
              </div>
            )}



            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call-to-Action Text
                </label>
                <input
                  type="text"
                  value={formData.content.ctaText}
                  onChange={(e) => handleContentChange('ctaText', e.target.value)}
                  placeholder="e.g., Learn More"
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Link
                </label>
                <input
                  type="text"
                  value={formData.content.ctaLink}
                  onChange={(e) => handleContentChange('ctaLink', e.target.value)}
                  placeholder="e.g., #products"
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Button Background
                </label>
                <input
                  type="color"
                  value={formData.styling.ctaButtonBackgroundColor || '#000000'}
                  onChange={(e) => handleStylingChange('ctaButtonBackgroundColor', e.target.value)}
                  className="w-full h-10 rounded border border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Button Text Color
                </label>
                <input
                  type="color"
                  value={formData.styling.ctaButtonTextColor || '#ffffff'}
                  onChange={(e) => handleStylingChange('ctaButtonTextColor', e.target.value)}
                  className="w-full h-10 rounded border border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Button Hover Background
                </label>
                <input
                  type="color"
                  value={formData.styling.ctaButtonHoverBackgroundColor || '#2563eb'}
                  onChange={(e) => handleStylingChange('ctaButtonHoverBackgroundColor', e.target.value)}
                  className="w-full h-10 rounded border border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Button Hover Text Color
                </label>
                <input
                  type="color"
                  value={formData.styling.ctaButtonHoverTextColor || '#ffffff'}
                  onChange={(e) => handleStylingChange('ctaButtonHoverTextColor', e.target.value)}
                  className="w-full h-10 rounded border border-gray-300"
                />
              </div>
            </div>

          {/* Items (if applicable) */}
          <div className="space-y-4 pt-6 border-t border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Section Items</h3>
              <button
                type="button"
                onClick={handleAddItem}
                className="text-sm px-3 py-1 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors font-medium"
                style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.ctaButtonBackgroundColor, color: formData.styling?.ctaButtonTextColor } : {}}
              >
                + Add Item
              </button>
            </div>

            <div className="space-y-3">
              {formData.content.items && formData.content.items.map((item, idx) => (
                <div key={item.id} className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-gray-900">Item {idx + 1}</p>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                      style={formData.type === 'section-items' ? { color: formData.styling?.ctaButtonTextColor } : {}}
                    >
                      Remove
                    </button>
                  </div>

                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
                    placeholder="Item title"
                    className="input-field w-full"
                    style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
                  />

                  <textarea
                    value={item.description || ''}
                    onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
                    placeholder="Item description"
                    rows="2"
                    className="input-field w-full resize-none"
                    style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
                      <input
                        type="number"
                        value={item.price || ''}
                        onChange={(e) => handleUpdateItem(item.id, 'price', e.target.value)}
                        placeholder="Item price"
                        step="0.01"
                        min="0"
                        className="input-field w-full"
                        style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={item.currency || website?.paymentSettings?.currency || 'NGN'}
                        onChange={(e) => handleUpdateItem(item.id, 'currency', e.target.value)}
                        className="input-field w-full"
                        style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
                      >
                        {getCurrencyOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={item.whatsappContact || ''}
                    onChange={(e) => handleUpdateItem(item.id, 'whatsappContact', e.target.value)}
                    placeholder="WhatsApp contact (e.g., +1234567890)"
                    className="input-field w-full"
                    style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
                  />

                  {/* Image/Video Upload Section */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Images / Videos (Gallery)
                    </label>
                    
                    {/* Gallery Preview */}
                    {item.gallery && item.gallery.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {item.gallery.map((galleryItem) => (
                          <div key={galleryItem.id} className="relative group">
                            {galleryItem.image?.url ? (
                              <img 
                                src={galleryItem.image.url} 
                                alt={galleryItem.title || 'Gallery item'} 
                                className="h-24 w-full object-cover rounded border border-gray-200"
                              />
                            ) : galleryItem.link?.url ? (
                              <WebsiteUrlCard url={galleryItem.link.url} className="h-24" />
                            ) : galleryItem.video?.url ? (
                              galleryItem.video.embedUrl ? (
                                <VideoUrlFrame video={galleryItem.video} className="h-24 object-cover" />
                              ) : (
                                <video src={galleryItem.video.url} className="h-24 w-full object-cover rounded border border-gray-200" controls />
                              )
                            ) : null}
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryItem(item.id, galleryItem.id)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove"
                            >
                              <FiX className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm transition-colors ${uploadingMediaKey === `item-${item.id}` ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`} style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}>
                        {uploadingMediaKey === `item-${item.id}` ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                        {uploadingMediaKey === `item-${item.id}` ? 'Uploading...' : 'Add Image/Video'}
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={(e) => handleImageInputChange(e, item.id)}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <VideoUrlInput onAdd={(video) => handleAddItemVideoUrl(item.id, video)} disabled={uploadingMediaKey === `item-${item.id}`} />
                    <ImageUrlInput onAdd={(image) => handleAddItemImageUrl(item.id, image)} disabled={uploadingMediaKey === `item-${item.id}`} />
                    <WebsiteUrlInput onAdd={(link) => handleAddItemWebsiteUrl(item.id, link)} disabled={uploadingMediaKey === `item-${item.id}`} />
                    
                    {item.gallery && item.gallery.length > 0 && (
                      <p className="text-xs text-gray-600">
                        {item.gallery.length} file(s) uploaded
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>



          {/* Section Styling */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🎨 Section Styling</h3>
            <p className="text-sm text-gray-600 mb-4">
              Customize the appearance of this section on your public website.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.styling.backgroundColor || '#ffffff'}
                    onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.styling.backgroundColor || ''}
                    onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
                    placeholder="#ffffff or white"
                    className="input-field flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Choose a background color for this section
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.styling.textColor || '#000000'}
                    onChange={(e) => handleStylingChange('textColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.styling.textColor || ''}
                    onChange={(e) => handleStylingChange('textColor', e.target.value)}
                    placeholder="#000000 or black"
                    className="input-field flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Choose a text color for this section
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hover Background Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.styling.hoverBackgroundColor || '#f9fafb'}
                    onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.styling.hoverBackgroundColor || ''}
                    onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
                    placeholder="#f9fafb or light gray"
                    className="input-field flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Background color when hovering over the section
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hover Text Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.styling.hoverTextColor || '#374151'}
                    onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.styling.hoverTextColor || ''}
                    onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
                    placeholder="#374151 or dark gray"
                    className="input-field flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Text color when hovering over the section
                </p>
              </div>
            </div>
          </div>



          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" /> Save Section
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}













// import { useState, useEffect } from 'react'
// import axios from 'axios'
// import { FiX, FiLoader, FiSave, FiUpload, FiImage } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import ProductsManagement from './ProductsManagement'
// import FixturesManagement from './FixturesManagement'
// import ResumesManagement from './ResumesManagement'
// import TeamMembersEditor from './TeamMembersEditor'
// import TicketsManagement from './TicketsManagement'
// import AudioManagement from './AudioManagement'
// import { getCurrencyOptions } from '../../utils/currency'

// const SECTION_TYPES = [
//   'hero',
//   'features',
//   'products',
//   'tickets',
//   'services',
//   'testimonials',
//   'pricing',
//   'gallery',
//   'contact',
//   'newsletter',
//   'team',
//   'about',
//   'faq',
//   'blog',
//   'cart',
//   'checkout',
//   'reviews',
//   'fixtures',
//   'points-table',
//   'resume',
//   'audio',
//   'crypto-product'
// ]

// export default function SectionEditorModal({ isOpen, onClose, onSubmit, section, website }) {
//   // Normalize section type (some places use `sectionType` instead of `type`)
//   const sectionType = section?.type || section?.sectionType || ''
//   const createUniqueId = (prefix) => {
//     if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
//       return `${prefix}-${crypto.randomUUID()}`
//     }

//     return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
//   }

//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     type: '',
//     enabled: true,
//     displayOrder: 0,
//     styling: {
//       backgroundColor: '',
//       textColor: '',
//       hoverBackgroundColor: '',
//       hoverTextColor: '',
//       ctaButtonBackgroundColor: '',
//       ctaButtonTextColor: '',
//       ctaButtonHoverBackgroundColor: '',
//       ctaButtonHoverTextColor: ''
//     },
//     content: {
//       heading: '',
//       subheading: '',
//       ctaText: '',
//       ctaLink: '',
//       ctaBackgroundColor: '',
//       ctaTextColor: '',
//       heroImage: { url: '', publicId: '' },
//       heroImages: [],
//       carouselSettings: {
//         autoPlay: true,
//         interval: 5000,
//         transitionSpeed: 1000,
//         showIndicators: true,
//         showNavigation: true
//       },
//       specialMedia: [],
//       leftSidebar: { contentText: '', media: [] },
//       rightSidebar: { contentText: '', media: [] },
//       items: [],
//       teamMembers: [],
//       audio: [],
//       buyRate: 0,
//       sellRate: 0,
//       usdtBuyRate: 0,
//       usdtSellRate: 0,
//       buyEnabled: true,
//       sellEnabled: true,
//       giftCardEnabled: true,
//       giftCardWhatsApp: '',
//       giftCardNames: []
//     },
//     isNew: false
//   })
//   const [saving, setSaving] = useState(false)
//   const [uploadingMediaKey, setUploadingMediaKey] = useState(null)

//   useEffect(() => {
//     if (section && isOpen) {
//       // Check if this is a new section
//       const isNew = !section._id
      
//       // Normalize section type (some places use `sectionType` instead of `type`)
//       const sectionType = section?.type || section?.sectionType || ''
      
//       // Ensure items have gallery arrays when loading from section
//       const contentWithGalleries = section.content ? {
//         ...section.content,
//         items: (section.content.items || []).map(item => ({
//           ...item,
//           // Migrate old single image format to gallery format if needed
//           gallery: item.gallery && Array.isArray(item.gallery) 
//             ? item.gallery.map((galleryItem, galleryIndex) => ({
//                 ...galleryItem,
//                 id: galleryItem?.id || createUniqueId(`item-${item.id || 'gallery'}-${galleryIndex}`)
//               }))
//             : (item.image ? [{
//                 id: createUniqueId(`item-${item.id || 'gallery'}`),
//                 image: typeof item.image === 'string' 
//                   ? { url: item.image, publicId: '' } 
//                   : item.image,
//                 video: { url: '', publicId: '' },
//                 title: item.title || '',
//                 description: item.description || ''
//               }] : [])
//         })),
//         teamMembers: section.content.teamMembers || [],
//         heroImages: section.content.heroImages || [],
//         carouselSettings: section.content.carouselSettings || {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         audio: section.content.audio || [],
//         buyRate: section.content.buyRate || 0,
//         sellRate: section.content.sellRate || 0,
//         usdtBuyRate: section.content.usdtBuyRate || 0,
//         usdtSellRate: section.content.usdtSellRate || 0,
//         buyEnabled: section.content.buyEnabled !== false,
//         sellEnabled: section.content.sellEnabled !== false,
//         giftCardEnabled: section.content.giftCardEnabled !== false,
//         giftCardWhatsApp: section.content.giftCardWhatsApp || '',
//         giftCardNames: Array.isArray(section.content.giftCardNames) ? section.content.giftCardNames : []
//       } : {
//         heading: '',
//         subheading: '',
//         ctaText: '',
//         ctaLink: '',
//         ctaBackgroundColor: '',
//         ctaTextColor: '',
//         heroImage: { url: '', publicId: '' },
//         heroImages: [],
//         carouselSettings: {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         specialMedia: [],
//         leftSidebar: { contentText: '', media: [] },
//         rightSidebar: { contentText: '', media: [] },
//         items: [],
//         teamMembers: [],
//         audio: [],
//         buyRate: 0,
//         sellRate: 0,
//         usdtBuyRate: 0,
//         usdtSellRate: 0,
//         giftCardEnabled: true,
//         giftCardWhatsApp: '',
//         giftCardNames: []
//       }
      
//         setFormData({
//           title: section.title || '',
//           description: section.description || '',
//           type: sectionType || '',
//           enabled: !section.disabled,
//           displayOrder: section.displayOrder || 0,
//           styling: section.styling ? {
//             backgroundColor: section.styling.backgroundColor || '',
//             textColor: section.styling.textColor || '',
//             hoverBackgroundColor: section.styling.hoverBackgroundColor || '',
//             hoverTextColor: section.styling.hoverTextColor || '',
//             ctaButtonBackgroundColor: section.styling.ctaButtonBackgroundColor || '',
//             ctaButtonTextColor: section.styling.ctaButtonTextColor || '',
//             ctaButtonHoverBackgroundColor: section.styling.ctaButtonHoverBackgroundColor || '',
//             ctaButtonHoverTextColor: section.styling.ctaButtonHoverTextColor || ''
//           } : {
//             backgroundColor: '',
//             textColor: '',
//             hoverBackgroundColor: '',
//             hoverTextColor: '',
//             ctaButtonBackgroundColor: '',
//             ctaButtonTextColor: '',
//             ctaButtonHoverBackgroundColor: '',
//             ctaButtonHoverTextColor: ''
//           },
//           content: contentWithGalleries,
//           isNew
//         })
//     }
//   }, [section, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value
//     }))
//   }

//   const handleStylingChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       styling: {
//         ...prev.styling,
//         [field]: value
//       }
//     }))
//   }

//   const handleContentChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [field]: value
//       }
//     }))
//   }

//   const handleAddGiftCardName = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: [...(prev.content.giftCardNames || []), '']
//       }
//     }))
//   }

//   const handleUpdateGiftCardName = (index, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.map((name, idx) => idx === index ? value : name)
//       }
//     }))
//   }

//   const handleRemoveGiftCardName = (index) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.filter((_, idx) => idx !== index)
//       }
//     }))
//   }

//   const handleAddItem = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: [
//           ...prev.content.items,
//           { 
//             id: Date.now(), 
//             title: '', 
//             description: '', 
//             price: '', 
//             currency: website?.paymentSettings?.currency || 'NGN',
//             whatsappContact: '', 
//             gallery: [] 
//           }
//         ]
//       }
//     }))
//   }

//   const handleUpdateItem = (itemId, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId ? { ...item, [field]: value } : item
//         )
//       }
//     }))
//   }

//   const handleRemoveItem = (itemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.filter(item => item.id !== itemId)
//       }
//     }))
//   }

//   const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
//   const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg', 'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/x-ms-wmv', 'video/x-m4v', 'video/mpeg', 'video/3gpp', 'video/3gpp2']
//   const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg', 'ogv', 'ogg', '3gp', '3g2']
//   const MAX_IMAGE_SIZE = 20 * 1024 * 1024
//   const MAX_VIDEO_SIZE = 150 * 1024 * 1024

//   const getFileExtension = (file) => {
//     if (!file?.name) return ''
//     return file.name.split('.').pop().toLowerCase()
//   }

//   const isImageFile = (file) => {
//     return file && ACCEPTED_IMAGE_TYPES.includes(file.type)
//   }

//   const isVideoFile = (file) => {
//     if (!file) return false
//     const extension = getFileExtension(file)
//     return (file.type && file.type.startsWith('video/')) || ACCEPTED_VIDEO_TYPES.includes(file.type) || SUPPORTED_VIDEO_EXTENSIONS.includes(extension)
//   }

//   const validateMediaFile = (file, imageOnly = false) => {
//     if (!file) {
//       return { valid: false, message: 'Please select a file to upload' }
//     }

//     if (imageOnly) {
//       if (!isImageFile(file)) {
//         return { valid: false, message: 'Please upload a valid image file' }
//       }
//     } else {
//       if (!isImageFile(file) && !isVideoFile(file)) {
//         return {
//           valid: false,
//           message: 'Please upload a supported image or video file (JPG, PNG, GIF, WEBP, SVG, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, MPEG, OGG, 3GP, 3G2)'
//         }
//       }
//     }

//     const maxSize = isVideoFile(file) ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE
//     if (file.size > maxSize) {
//       return { valid: false, message: `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB` }
//     }

//     return { valid: true }
//   }

//   const uploadMediaFile = async (file) => {
//     const formDataUpload = new FormData()
//     formDataUpload.append('file', file)
//     formDataUpload.append('folder', 'website/sections')

//     const response = await axios.post('upload', formDataUpload, {
//       headers: { 'Content-Type': 'multipart/form-data' }
//     })

//     if (!response?.data?.url) {
//       throw new Error('Upload failed to return a valid URL')
//     }

//     return response.data
//   }

//   const getVideoMimeType = (url) => {
//     if (!url) return undefined
//     const extension = url.split('?')[0].split('.').pop().toLowerCase()
//     const mimeMap = {
//       mp4: 'video/mp4',
//       webm: 'video/webm',
//       ogg: 'video/ogg',
//       ogv: 'video/ogg',
//       mov: 'video/quicktime',
//       avi: 'video/x-msvideo',
//       mkv: 'video/x-matroska',
//       flv: 'video/x-flv',
//       wmv: 'video/x-ms-wmv',
//       m4v: 'video/x-m4v',
//       mpeg: 'video/mpeg',
//       mpg: 'video/mpeg',
//       '3gp': 'video/3gpp',
//       '3g2': 'video/3gpp2'
//     }
//     return mimeMap[extension]
//   }

//   const handleSpecialMediaUpload = async (file, index = null) => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey('special-media')

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           specialMedia: [
//             ...(prev.content.specialMedia || []),
//             {
//               id: createUniqueId('special-media'),
//               url: uploadResult.url,
//               publicId: uploadResult.publicId || '',
//               type: isImage ? 'image' : 'video',
//               display: 'content',
//               alt: file.name,
//               style: {
//                 width: '100%',
//                 height: 'auto',
//                 paddingLeft: '0px',
//                 paddingRight: '0px',
//                 marginLeft: '0px',
//                 marginRight: '0px'
//               }
//             }
//           ]
//         }
//       }))

//       toast.success('Special media uploaded')
//     } catch (err) {
//       console.error('Error uploading special media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload special media')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleSidebarUpload = async (file, side = 'left') => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey(`sidebar-${side}`)

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           [`${side}Sidebar`]: {
//             ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//             media: [
//               ...((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []),
//               {
//                 id: Date.now(),
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 type: isImage ? 'image' : 'video',
//                 alt: file.name
//               }
//             ]
//           }
//         }
//       }))

//       toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar media uploaded`)
//     } catch (err) {
//       console.error('Error uploading sidebar media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload sidebar media')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleRemoveSpecialMedia = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).filter(m => m.id !== id)
//       }
//     }))
//   }

//   const handleChangeSpecialDisplay = (id, display) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => m.id === id ? { ...m, display } : m)
//       }
//     }))
//   }

//   const handleChangeSpecialMediaStyle = (id, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => {
//           if (m.id !== id) return m
//           return {
//             ...m,
//             style: {
//               ...m.style,
//               [field]: value
//             }
//           }
//         })
//       }
//     }))
//   }

//   const handleRemoveSidebarMedia = (side, id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           media: ((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []).filter(m => m.id !== id)
//         }
//       }
//     }))
//   }

//   const handleSidebarContentChangeText = (side, text) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           contentText: text
//         }
//       }
//     }))
//   }

//   const handleItemImageUpload = async (itemId, file, isHeroImage = false) => {
//     if (!file) return

//     if (isHeroImage && !isImageFile(file)) {
//       toast.error('Please upload a valid image file')
//       return
//     }

//     if (!isHeroImage && !isImageFile(file) && !isVideoFile(file)) {
//       toast.error('Please upload a valid image or video file')
//       return
//     }

//     const validation = validateMediaFile(file, isHeroImage)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey(isHeroImage ? 'hero-carousel' : `item-${itemId}`)

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       if (!uploadResult?.url) {
//         throw new Error('Upload failed to return a valid URL')
//       }

//       if (isHeroImage) {
//         // Handle Hero image - add to carousel array
//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             heroImages: [
//               ...(prev.content.heroImages || []),
//               {
//                 id: `hero-${Date.now()}`,
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 order: (prev.content.heroImages || []).length
//               }
//             ]
//           }
//         }))
//         toast.success('Hero image added to carousel')
//       } else {
//         // Handle gallery items
//         const isImage = isImageFile(file)
//         const isVideo = isVideoFile(file)

//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             items: prev.content.items.map(item =>
//               item.id === itemId
//                 ? {
//                     ...item,
//                     gallery: [
//                       ...(item.gallery || []),
//                       {
//                         id: createUniqueId('gallery-item'),
//                         image: isImage ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         video: isVideo ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         title: file.name,
//                         description: ''
//                       }
//                     ]
//                   }
//                 : item
//             )
//           }
//         }))

//         toast.success('Image/Video uploaded successfully')
//       }
//     } catch (error) {
//       console.error('Error uploading file:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload file')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleImageInputChange = (e, itemId) => {
//     const file = e.target.files?.[0]
//     if (file) {
//       handleItemImageUpload(itemId, file)
//     }
//   }

//   // Helper functions for carousel/hero images management
//   const handleRemoveHeroImage = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         heroImages: (prev.content.heroImages || []).filter(img => img.id !== id)
//       }
//     }))
//     toast.success('Image removed from carousel')
//   }

//   const handleReorderHeroImages = (fromIndex, toIndex) => {
//     setFormData(prev => {
//       const newImages = [...(prev.content.heroImages || [])]
//       const [movedImage] = newImages.splice(fromIndex, 1)
//       newImages.splice(toIndex, 0, movedImage)
//       // Update order property
//       const reorderedImages = newImages.map((img, idx) => ({ ...img, order: idx }))
//       return {
//         ...prev,
//         content: {
//           ...prev.content,
//           heroImages: reorderedImages
//         }
//       }
//     })
//   }


//   const handleRemoveGalleryItem = (itemId, galleryItemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId
//             ? {
//                 ...item,
//                 gallery: (item.gallery || []).filter(g => g.id !== galleryItemId)
//               }
//             : item
//         )
//       }
//     }))
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()

//     // Section title is optional - allow empty for flexible layouts
//     // if (!formData.title.trim()) {
//     //   toast.error('Section title is required')
//     //   return
//     // }

//     // For NEW sections, section type is required
//     if (formData.isNew && !formData.type) {
//       toast.error('Please select a section type')
//       return
//     }

//     setSaving(true)
//     try {
//       const submitData = {
//         title: formData.title,
//         description: formData.description,
//         disabled: !formData.enabled,
//         displayOrder: (function() { const v = parseInt(formData.displayOrder); return isNaN(v) ? 0 : v })(),
//         styling: formData.styling,
//         // sanitize content to match backend schema expectations
//         content: (function sanitizeContent(ct) {
//           if (!ct) return {}

//           const pickHeroImage = (h) => ({
//             url: h?.url || '',
//             publicId: h?.publicId || ''
//           })

//           const sanitizeMediaArray = (arr) => ((arr || [])
//             .map(m => ({
//               url: m?.url || '',
//               publicId: m?.publicId || '',
//               type: m?.type === 'video' ? 'video' : 'image',
//               display: m?.display === 'full' ? 'full' : 'content',
//               alt: m?.alt || '',
//               style: {
//                 width: m?.style?.width || '100%',
//                 height: m?.style?.height || 'auto',
//                 paddingLeft: m?.style?.paddingLeft || '0px',
//                 paddingRight: m?.style?.paddingRight || '0px',
//                 marginLeft: m?.style?.marginLeft || '0px',
//                 marginRight: m?.style?.marginRight || '0px'
//               }
//             }))
//             .filter(m => typeof m.url === 'string' && m.url.trim() !== '')
//           )

//           const specialMedia = sanitizeMediaArray(ct.specialMedia).map(m => ({
//             url: m.url,
//             publicId: m.publicId,
//             type: m.type,
//             display: m.display,
//             alt: m.alt,
//             style: {
//               width: m.style.width,
//               height: m.style.height,
//               paddingLeft: m.style.paddingLeft,
//               paddingRight: m.style.paddingRight,
//               marginLeft: m.style.marginLeft,
//               marginRight: m.style.marginRight
//             }
//           }))

//           const leftSidebar = {
//             contentText: (ct.leftSidebar && ct.leftSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.leftSidebar?.media)
//           }

//           const rightSidebar = {
//             contentText: (ct.rightSidebar && ct.rightSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.rightSidebar?.media)
//           }

//           const items = (ct.items || []).map(it => {
//             const itemObj = {
//               title: it?.title || '',
//               description: it?.description || '',
//               gallery: (it.gallery || []).map((g, galleryIndex) => {
//                 const galleryItem = {
//                   id: g?.id || createUniqueId(`gallery-${it?.id || 'item'}-${galleryIndex}`),
//                   title: g?.title || '',
//                   description: g?.description || '',
//                   alt: g?.alt || ''
//                 }
                
//                 // Include image if it has a valid URL
//                 if (g?.image?.url) {
//                   galleryItem.image = { url: g.image.url, publicId: g.image.publicId || '' }
//                 }
                
//                 // Include video if it has a valid URL
//                 if (g?.video?.url) {
//                   galleryItem.video = { url: g.video.url, publicId: g.video.publicId || '' }
//                 }
                
//                 return galleryItem
//               }).filter(g => (g.image && g.image.url) || (g.video && g.video.url))
//             }
            
//             // Include WhatsApp contact if provided
//             if (it?.whatsappContact && typeof it.whatsappContact === 'string' && it.whatsappContact.trim()) {
//               itemObj.whatsappContact = it.whatsappContact.trim()
//             }
            
//             // Include price fields
//             if (it?.price !== undefined && it?.price !== null && it?.price !== '') {
//               itemObj.price = Number(it.price) || 0
//             }
//             if (it?.currency && typeof it.currency === 'string' && it.currency.trim()) {
//               itemObj.currency = it.currency.trim()
//             }
            
//             // Include any other fields that might be present
//             if (it?.link) itemObj.link = it.link
//             if (it?.image) itemObj.image = it.image
//             if (it?.video) itemObj.video = it.video
//             if (it?.name) itemObj.name = it.name
//             if (it?.id) itemObj.id = it.id
            
//             return itemObj
//           })

//           const logo = ct.logo ? { url: ct.logo.url || '', publicId: ct.logo.publicId || '', fileName: ct.logo.fileName || '' } : undefined

//           // Sanitize team members array
//           const teamMembers = Array.isArray(ct.teamMembers) ? ct.teamMembers.map(member => ({
//             id: member?.id || Date.now(),
//             name: (member?.name || '').trim(),
//             position: (member?.position || '').trim(),
//             bio: (member?.bio || '').trim(),
//             profileImage: member?.profileImage ? {
//               url: member.profileImage.url || '',
//               publicId: member.profileImage.publicId || ''
//             } : { url: '', publicId: '' }
//           })).filter(member => member.name) : []

//           // Sanitize heroImages array for carousel
//           const heroImages = Array.isArray(ct.heroImages) 
//             ? ct.heroImages
//                 .filter(img => img && (img.url || img.publicId))
//                 .map((img, idx) => ({
//                   id: img.id || `hero-${idx}`,
//                   url: img.url || '',
//                   publicId: img.publicId || '',
//                   order: typeof img.order === 'number' ? img.order : idx
//                 }))
//             : []

//           // Sanitize carousel settings
//           const carouselSettings = ct.carouselSettings ? {
//             autoPlay: ct.carouselSettings.autoPlay !== false,
//             interval: Math.max(1000, Math.min(30000, ct.carouselSettings.interval || 5000)),
//             transitionSpeed: Math.max(300, Math.min(3000, ct.carouselSettings.transitionSpeed || 1000)),
//             showIndicators: ct.carouselSettings.showIndicators !== false,
//             showNavigation: ct.carouselSettings.showNavigation !== false
//           } : {
//             autoPlay: true,
//             interval: 5000,
//             transitionSpeed: 1000,
//             showIndicators: true,
//             showNavigation: true
//           }

//           const audio = Array.isArray(ct.audio)
//             ? ct.audio
//                 .map(audioItem => ({
//                   id: audioItem?.id || Date.now().toString(),
//                   name: (audioItem?.name || '').trim(),
//                   description: (audioItem?.description || '').trim(),
//                   banner: {
//                     url: audioItem?.banner?.url || '',
//                     publicId: audioItem?.banner?.publicId || '',
//                     filename: audioItem?.banner?.filename || ''
//                   },
//                   audioFile: {
//                     url: audioItem?.audioFile?.url || '',
//                     publicId: audioItem?.audioFile?.publicId || '',
//                     filename: audioItem?.audioFile?.filename || ''
//                   }
//                 }))
//                 .filter(item => item.name && item.audioFile?.url)
//             : []

//           return {
//             heading: ct.heading || '',
//             subheading: ct.subheading || '',
//             ctaText: (ct.ctaText || '').toString().trim(),
//             ctaLink: (ct.ctaLink || '').toString().trim(),
//             heroImage: ct.heroImage ? pickHeroImage(ct.heroImage) : undefined,
//             heroImages: heroImages.length > 0 ? heroImages : undefined,
//             carouselSettings: heroImages.length > 0 ? carouselSettings : undefined,
//             heroOverlay: ct.heroOverlay || undefined,
//             heroButtons: Array.isArray(ct.heroButtons) ? ct.heroButtons.map(hb => ({ id: hb.id, text: hb.text, link: hb.link, style: hb.style })) : [],
//             buyRate: typeof ct.buyRate === 'number' ? ct.buyRate : undefined,
//             sellRate: typeof ct.sellRate === 'number' ? ct.sellRate : undefined,
//             usdtBuyRate: typeof ct.usdtBuyRate === 'number' ? ct.usdtBuyRate : undefined,
//             usdtSellRate: typeof ct.usdtSellRate === 'number' ? ct.usdtSellRate : undefined,
//             buyEnabled: ct.buyEnabled !== undefined ? Boolean(ct.buyEnabled) : undefined,
//             sellEnabled: ct.sellEnabled !== undefined ? Boolean(ct.sellEnabled) : undefined,
//             giftCardEnabled: ct.giftCardEnabled !== undefined ? Boolean(ct.giftCardEnabled) : undefined,
//             giftCardWhatsApp: typeof ct.giftCardWhatsApp === 'string' ? ct.giftCardWhatsApp.trim() : undefined,
//             giftCardNames: Array.isArray(ct.giftCardNames) ? ct.giftCardNames.map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean) : undefined,
//             backgroundImage: pickHeroImage(ct.backgroundImage || {}),
//             backgroundVideo: pickHeroImage(ct.backgroundVideo || {}),
//             logo,
//             specialMedia,
//             leftSidebar,
//             rightSidebar,
//             items,
//             teamMembers,
//             gallery: (ct.gallery || []).map(g => ({ title: g.title || '', description: g.description || '', image: g.image?.url ? { url: g.image.url, publicId: g.image.publicId || '' } : undefined, video: g.video?.url ? { url: g.video.url, publicId: g.video.publicId || '' } : undefined, alt: g.alt || '' })).filter(g => (g.image && g.image.url) || (g.video && g.video.url)),
//             audio
//           }
//         })(formData.content)
//       }

//       // Include sectionType for new sections or when it was changed during edit
//       if (formData.type || (formData.isNew && formData.type !== undefined)) {
//         const currentType = section?.sectionType || section?.type || null
//         if (formData.isNew || formData.type !== currentType) {
//           submitData.sectionType = formData.type
//         }
//       }

//       console.log('[SectionEditorModal] ===== SUBMIT START =====')
//       console.log('[SectionEditorModal] Submitting:', {
//         isNew: formData.isNew,
//         title: submitData.title,
//         sectionType: submitData.sectionType,
//         hasHeroImage: !!submitData.content?.heroImage?.url,
//         heroImageSize: submitData.content?.heroImage?.url ? Math.round(submitData.content.heroImage.url.length / 1024) + ' KB' : 'N/A',
//         heroImagesCount: submitData.content?.heroImages?.length || 0,
//         carouselSettingsAutoPlay: submitData.content?.carouselSettings?.autoPlay,
//         carouselSettingsInterval: submitData.content?.carouselSettings?.interval
//       })
//       if (submitData.content?.heroImages && submitData.content.heroImages.length > 0) {
//         console.log('[SectionEditorModal] CAROUSEL DATA:', {
//           imageCount: submitData.content.heroImages.length,
//           images: submitData.content.heroImages.map((img, idx) => ({
//             index: idx,
//             id: img.id,
//             hasUrl: !!img.url,
//             urlSize: img.url ? Math.round(img.url.length / 1024) + ' KB' : 'N/A',
//             order: img.order
//           })),
//           settings: submitData.content.carouselSettings
//         })
//       }
//       console.log('[SectionEditorModal] ITEMS WITH PRICE AND GALLERY:', (submitData.content?.items || []).map((item, idx) => ({
//         itemIndex: idx,
//         title: item.title,
//         price: item.price,
//         currency: item.currency,
//         whatsappContact: item.whatsappContact,
//         galleryCount: item.gallery?.length || 0,
//         galleryItems: (item.gallery || []).map((g, gi) => ({
//           galleryIndex: gi,
//           hasImage: !!g.image?.url,
//           hasVideo: !!g.video?.url,
//           imageSize: g.image?.url ? Math.round(g.image.url.length / 1024) + ' KB' : 'N/A',
//           videoSize: g.video?.url ? Math.round(g.video.url.length / 1024) + ' KB' : 'N/A'
//         }))
//       })))
//       console.log('[SectionEditorModal] TEAM MEMBERS:', submitData.content?.teamMembers ? submitData.content.teamMembers.length : 0, submitData.content?.teamMembers ? submitData.content.teamMembers.map((m, idx) => ({
//         index: idx,
//         name: m.name,
//         position: m.position,
//         hasImage: !!m.profileImage?.url,
//         imageSize: m.profileImage?.url ? Math.round(m.profileImage.url.length / 1024) + ' KB' : 'N/A'
//       })) : 'none')
//       console.log('[SectionEditorModal] CTA FIELDS SUBMITTED:', {
//         hasCtaText: !!submitData.content?.ctaText,
//         hasCtaLink: !!submitData.content?.ctaLink,
//         ctaText: submitData.content?.ctaText,
//         ctaLink: submitData.content?.ctaLink,
//         isValid: !!submitData.content?.ctaText && !!submitData.content?.ctaLink
//       })
//       console.log('[SectionEditorModal] Full submit data keys:', Object.keys(submitData))
//       console.log('[SectionEditorModal] Content keys:', Object.keys(submitData.content || {}))
//       if (submitData.content?.heroImage) {
//         console.log('[SectionEditorModal] Hero Image Object:', {
//           hasUrl: !!submitData.content.heroImage.url,
//           hasPublicId: !!submitData.content.heroImage.publicId,
//           urlLength: submitData.content.heroImage.url?.length || 0,
//           urlPreview: submitData.content.heroImage.url?.substring(0, 50)
//         })
//       }
//       // Await the async onSubmit callback and it will handle closing modal
//       await onSubmit(submitData)
//       console.log('[Modal] onSubmit completed successfully - modal will close via parent')
//     } catch (error) {
//       // Error is already handled and toasted in parent, just log it
//       console.error('[Modal] onSubmit failed (error handled by parent):', error.message)
//       // Don't show another toast - parent already showed one
//     } finally {
//       setSaving(false)
//     }
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">
//             {formData.isNew ? 'Create New Section' : 'Edit Section'}
//           </h2>
//           <button
//             onClick={onClose}
//             disabled={saving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Hero Section Banner */}
//           {formData.type === 'hero' && (
//             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
//               <div className="flex items-start gap-3">
//                 <div className="text-2xl">🎯</div>
//                 <div className="flex-1">
//                   <h3 className="font-semibold text-blue-900 mb-1">Hero Section</h3>
//                   <p className="text-sm text-blue-700">
//                     This is your website's hero/banner section. It appears at the top of your website with a full-width background image.
//                     The hero section cannot be deleted but can be edited and temporarily hidden.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Section Information</h3>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Title (optional)
//                 </label>
//                 <input
//                   type="text"
//                   name="title"
//                   value={formData.title}
//                   onChange={handleInputChange}
//                   placeholder="Enter section title (optional)"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Type
//                 </label>
//                 <select
//                   name="type"
//                   value={formData.type}
//                   onChange={handleInputChange}
//                   className="input-field w-full"
//                 >
//                   <option value="">Select type</option>
//                   {SECTION_TYPES.map(type => (
//                     <option key={type} value={type}>
//                       {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of this section"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             <div className="flex items-center gap-2">
//               <input
//                 type="checkbox"
//                 id="enabled"
//                 name="enabled"
//                 checked={formData.enabled}
//                 onChange={handleInputChange}
//                 className="w-4 h-4 rounded border-gray-300"
//               />
//               <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
//                 Section is enabled
//               </label>
//             </div>
//           </div>

//           {/* Products Management - Show when products section type is selected */}
//           {formData.type === 'products' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Product Management</h3>
//               <ProductsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Tickets Management - Show when tickets section type is selected */}
//           {formData.type === 'tickets' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">🎫 Ticket Management</h3>
//               <TicketsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Contact Form - Show message when contact section type is selected */}
//             {formData.type === 'contact' && website ? (
//               <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//                 <h3 className="text-lg font-semibold text-gray-900 mb-2">📧 Contact Form</h3>
//                 <p className="text-sm text-gray-700">
//                   A contact form will be displayed on your public website for this section after you save the settings.
//                 </p>
//               </div>
//             ) : null}

//           {/* Reviews Section - Show when reviews section type is selected */}
//           {formData.type === 'reviews' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">⭐ Customer Reviews Section</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays customer reviews and allows visitors to submit new reviews.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Visitors can submit reviews with a comment and optional star rating</li>
//                 <li>✓ All reviews are saved as "pending" until you approve them</li>
//                 <li>✓ Only approved reviews appear on the public website</li>
//                 <li>✓ Manage reviews (approve/reject/delete) in the "Reviews" dashboard tab</li>
//               </ul>
//             </div>
//           )}

//           {/* Fixtures Section - Show when fixtures section type is selected */}
//           {formData.type === 'fixtures' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 Football Fixtures Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Manage football match fixtures and view automated league standings.
//               </p>
//               <FixturesManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           )}

//           {/* Points Table Section - Show when points-table section type is selected */}
//           {formData.type === 'points-table' && (
//             <div className="pt-6 border-t border-gray-200 bg-indigo-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 League Points Table</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays the league standings and is automatically updated when fixtures are completed.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Standings are calculated automatically from finished fixtures</li>
//                 <li>✓ Table shows: Position, Team, Played, Wins, Draws, Losses, Goals For/Against, Goal Difference, Points</li>
//                 <li>✓ Teams are ranked by points, then goal difference, then goals for</li>
//                 <li>✓ Points: Win = 3, Draw = 1, Loss = 0</li>
//               </ul>
//             </div>
//           )}

//           {/* Resume Upload Section - Show when resume section type is selected */}
//           {formData.type === 'resume' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Resume Upload Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 This section allows visitors to submit their resumes for job positions.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-4">
//                 <li>✓ Visitors can upload PDF, DOC, DOCX, or TXT files (max 5MB)</li>
//                 <li>✓ Submitted resumes appear in your Resume Dashboard</li>
//                 <li>✓ You receive Telegram notifications for new submissions</li>
//                 <li>✓ Track candidate status: Submitted → Reviewed → Shortlisted/Rejected</li>
//               </ul>
              
//               {/* Resume Section Toggle */}
//               <div className="mb-4 p-3 bg-white rounded border">
//                 <div className="flex items-center gap-2">
//                   <input
//                     type="checkbox"
//                     id="resumeToggle"
//                     name="enabled"
//                     checked={formData.enabled}
//                     onChange={handleInputChange}
//                     className="w-4 h-4 rounded border-gray-300"
//                   />
//                   <label htmlFor="resumeToggle" className="text-sm font-medium text-gray-700">
//                     Resume form is visible on public website
//                   </label>
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Turn ON to show the resume submission form to visitors. Turn OFF to hide it completely.
//                 </p>
//               </div>
              
//               <ResumesManagement website={website} />
//             </div>
//           )}

//           {/* Team Members Section - Show when team section type is selected */}
//           {formData.type === 'team' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">👥 Team Members Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Create and manage your team members. Each team member can have a profile picture, name, position, and bio.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Add multiple team members with full names and roles</li>
//                 <li>✓ Upload profile pictures for each team member</li>
//                 <li>✓ Add optional bio/description for each member</li>
//                 <li>✓ Team will display in a responsive grid on your public website</li>
//                 <li>✓ Fully responsive layout for mobile, tablet, and desktop</li>
//               </ul>
              
//               <TeamMembersEditor formData={formData} setFormData={setFormData} />
//             </div>
//           )}
//           {/* Audio Section - Show when audio section type is selected */}
//           {formData.type === 'audio' && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🎵 Audio Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Add audio files to your website. Visitors can play audio directly on your site.
//               </p>
//               <AudioManagement formData={formData} setFormData={setFormData} />
//             </div>
//           )}

//           {/* Crypto Product Section - Show when crypto-product section type is selected */}
//           {formData.type === 'crypto-product' && (
//             <div className="pt-6 border-t border-gray-200 bg-orange-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">₿ Crypto Trading Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Enable cryptocurrency buy/sell functionality on your website. Users can purchase or sell crypto directly through secure payment processing.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Support for multiple cryptocurrencies (BTC, ETH, USDT, BNB, ADA, SOL, DOT, DOGE, XRP, LTC)</li>
//                 <li>✓ Secure buy/sell transactions with payment provider integration</li>
//                 <li>✓ Real-time exchange rates with 10-minute price locking</li>
//                 <li>✓ Automatic payment detection and status updates</li>
//                 <li>✓ Transaction dashboard for managing all orders</li>
//                 <li>✓ Admin notifications for completed transactions</li>
//               </ul>

//               {/* Buy/Sell Toggle Controls */}
//               <div className="bg-white rounded-lg p-4 border mb-4">
//                 <h4 className="font-semibold text-gray-900 mb-4">Feature Controls</h4>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Buy Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to buy cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.buyEnabled !== false}
//                         onChange={(e) => handleContentChange('buyEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Sell Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to sell cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.sellEnabled !== false}
//                         onChange={(e) => handleContentChange('sellEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                 </div>
//               </div>

//               {/* Rate Configuration */}
//               <div className="bg-white rounded-lg p-4 border">
//                 <div className="flex items-center justify-between mb-4">
//                   <h4 className="font-semibold text-gray-900">Exchange Rate Configuration</h4>
//                   {(formData.content.buyRate || formData.content.sellRate || formData.content.usdtBuyRate || formData.content.usdtSellRate) && (
//                     <button
//                       type="button"
//                       onClick={() => {
//                         handleContentChange('buyRate', null)
//                         handleContentChange('sellRate', null)
//                         handleContentChange('usdtBuyRate', null)
//                         handleContentChange('usdtSellRate', null)
//                       }}
//                       className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded border border-red-300"
//                     >
//                       Clear All Rates
//                     </button>
//                   )}
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Buy Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.buyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('buyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('buyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 50000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users buy BTC (in Naira)
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Sell Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.sellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('sellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('sellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 48000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users sell BTC (in Naira)
//                     </p>
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Buy Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtBuyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtBuyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtBuyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1500"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific buy rate for USDT transactions
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Sell Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtSellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtSellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtSellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1450"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific sell rate for USDT transactions
//                     </p>
//                   </div>
//                 </div>
//                 <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
//                   <p className="text-sm text-blue-800">
//                     <strong>Note:</strong> Rates must be valid numeric values greater than 0. These rates will be displayed on your public website and used for all transaction calculations.
//                     Update them regularly to reflect current market conditions. Use "Clear Rates" to remove rates entirely.
//                   </p>
//                 </div>
//               </div>

//               <div className="bg-white rounded-lg p-4 border mt-4">
//                 <div className="flex items-center justify-between mb-4">
//                   <div>
//                     <h4 className="font-semibold text-gray-900">Gift Card / Airtime Settings</h4>
//                     <p className="text-sm text-gray-500">Configure the gift card option shown on public crypto pages.</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={formData.content.giftCardEnabled !== false}
//                       onChange={(e) => handleContentChange('giftCardEnabled', e.target.checked)}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                   </label>
//                 </div>

//                 <div className="grid grid-cols-1 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       WhatsApp Number for Gift Card Requests
//                     </label>
//                     <input
//                       type="text"
//                       value={formData.content.giftCardWhatsApp || ''}
//                       onChange={(e) => handleContentChange('giftCardWhatsApp', e.target.value)}
//                       placeholder="e.g., +2348012345678"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       This number will be used to prefill WhatsApp messages when customers submit a gift card request.
//                     </p>
//                   </div>

//                   <div>
//                     <div className="flex items-center justify-between mb-2">
//                       <label className="block text-sm font-medium text-gray-700">Gift Card / Airtime Types</label>
//                       <button
//                         type="button"
//                         onClick={handleAddGiftCardName}
//                         className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded border border-orange-200 hover:bg-orange-200"
//                       >
//                         Add type
//                       </button>
//                     </div>
//                     <p className="text-xs text-gray-500 mb-3">
//                       Add the gift card names that users can select when requesting gift card payment.
//                     </p>
//                     <div className="space-y-3">
//                       {(formData.content.giftCardNames || []).map((name, index) => (
//                         <div key={index} className="flex items-center gap-2">
//                           <input
//                             type="text"
//                             value={name}
//                             onChange={(e) => handleUpdateGiftCardName(index, e.target.value)}
//                             placeholder={`Gift card type ${index + 1}`}
//                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveGiftCardName(index)}
//                             className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded border border-red-200 hover:bg-red-200"
//                           >
//                             Remove
//                           </button>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
//                 <p className="text-sm text-yellow-800">
//                   <strong>Note:</strong> Crypto trading requires proper payment provider setup and compliance with local regulations.
//                   Contact support for payment provider integration assistance.
//                 </p>
//               </div>
//             </div>
//           )}

//           {/* Generic Content Fields - Show for all section types */}
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Heading
//               </label>
//               <input
//                 type="text"
//                 value={formData.content.heading}
//                 onChange={(e) => handleContentChange('heading', e.target.value)}
//                 placeholder="Main heading"
//                 className="input-field w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Subheading
//               </label>
//               <textarea
//                 value={formData.content.subheading}
//                 onChange={(e) => handleContentChange('subheading', e.target.value)}
//                 placeholder="Subheading or description"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Special Media Upload (applies to section) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📁 Special Media Upload</h3>
//               <p className="text-sm text-gray-600 mb-3">Upload images or videos that will appear in this section on the public website. You can choose to display them full-width or constrained to the content width. There are no file size limits.</p>

//               <div className="flex flex-col sm:flex-row sm:items-center gap-3">
//                 <label className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm text-center ${uploadingMediaKey === 'special-media' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'special-media' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'special-media' ? 'Uploading...' : 'Upload Media'}
//                   <input
//                     type="file"
//                     accept="image/*,video/*"
//                     onChange={(e) => {
//                       const file = e.target.files?.[0]
//                       if (file) handleSpecialMediaUpload(file)
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//               </div>

//               <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
//                 {(formData.content.specialMedia || []).map(m => (
//                   <div key={m.id} className="relative flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
//                     {m.type === 'image' ? (
//                       <img src={m.url} alt={m.alt || 'media'} className="h-40 w-full object-cover sm:h-36" />
//                     ) : (
//                       <video className="h-40 w-full object-cover sm:h-36" controls playsInline preload="metadata">
//                         <source src={m.url} type={getVideoMimeType(m.url)} />
//                         Your browser does not support video playback.
//                       </video>
//                     )}
//                     <div className="flex flex-1 flex-col gap-3 p-3 sm:p-4">
//                       <select value={m.display || 'content'} onChange={(e) => handleChangeSpecialDisplay(m.id, e.target.value)} className="input-field w-full">
//                         <option value="content">Aligned with content width</option>
//                         <option value="full">Full width</option>
//                       </select>

//                       <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//                         <label className="block text-xs font-medium text-gray-600">
//                           Width
//                           <input
//                             type="text"
//                             value={m.style?.width || '100%'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'width', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. 100%, 400px"
//                           />
//                         </label>
//                         <label className="block text-xs font-medium text-gray-600">
//                           Height
//                           <input
//                             type="text"
//                             value={m.style?.height || 'auto'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'height', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. auto, 300px"
//                           />
//                         </label>
//                         <label className="block text-xs font-medium text-gray-600">
//                           Padding Left
//                           <input
//                             type="text"
//                             value={m.style?.paddingLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingLeft', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="block text-xs font-medium text-gray-600">
//                           Padding Right
//                           <input
//                             type="text"
//                             value={m.style?.paddingRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingRight', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="block text-xs font-medium text-gray-600">
//                           Margin Left
//                           <input
//                             type="text"
//                             value={m.style?.marginLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginLeft', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                         <label className="block text-xs font-medium text-gray-600">
//                           Margin Right
//                           <input
//                             type="text"
//                             value={m.style?.marginRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginRight', e.target.value)}
//                             className="input-field mt-1 w-full"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                       </div>

//                       <div className="flex items-center justify-between gap-3 pt-1">
//                         <button type="button" onClick={() => handleRemoveSpecialMedia(m.id)} className="text-sm text-red-600">Remove</button>
//                         <span className="text-xs text-gray-500">{m.type}</span>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Sidebars (Left / Right) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Left Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.leftSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('left', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className={`flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === 'sidebar-left' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'sidebar-left' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'sidebar-left' ? 'Uploading...' : 'Upload Left Sidebar Media'}
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'left') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.leftSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('left', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>

//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Right Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.rightSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('right', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className={`flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === 'sidebar-right' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'sidebar-right' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'sidebar-right' ? 'Uploading...' : 'Upload Right Sidebar Media'}
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'right') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.rightSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('right', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             {/* Hero Image Carousel Upload - Show when hero section type is selected */}
//             {formData.type === 'hero' && (
//               <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-5 rounded-lg border border-purple-200 space-y-4">
//                 <div className="flex items-center justify-between">
//                   <label className="block text-sm font-bold text-gray-900">
//                     🎠 Hero Image Carousel
//                   </label>
//                   {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                     <span className="text-xs font-semibold bg-purple-200 text-purple-900 px-3 py-1 rounded-full">
//                       {formData.content.heroImages.length} image{formData.content.heroImages.length !== 1 ? 's' : ''}
//                     </span>
//                   )}
//                 </div>
                
//                 {/* Hero Images Gallery Preview */}
//                 {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                   <div className="space-y-3">
//                     <p className="text-xs text-gray-600 font-medium">Images in carousel (drag to reorder):</p>
//                     <div className="space-y-2 bg-white p-3 rounded-lg border border-purple-100">
//                       {formData.content.heroImages.map((img, idx) => (
//                         <div
//                           key={img.id}
//                           className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200 hover:border-purple-300 transition-colors group"
//                         >
//                           <div className="flex-shrink-0 text-gray-500 font-semibold text-sm w-6 text-center">
//                             {idx + 1}
//                           </div>
//                           {img.url && (
//                             <img 
//                               src={img.url} 
//                               alt={`Hero carousel ${idx + 1}`}
//                               className="w-16 h-16 object-cover rounded border border-gray-300"
//                             />
//                           )}
//                           <div className="flex-1 min-w-0">
//                             <p className="text-xs text-gray-600 truncate">
//                               {img.url ? `Image ${idx + 1} • ${Math.round(img.url.length / 1024)}KB` : 'Pending...'}
//                             </p>
//                           </div>
//                           <div className="flex gap-1">
//                             {idx > 0 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx - 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move up"
//                               >
//                                 ↑
//                               </button>
//                             )}
//                             {idx < formData.content.heroImages.length - 1 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx + 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move down"
//                               >
//                                 ↓
//                               </button>
//                             )}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveHeroImage(img.id)}
//                               className="p-1 text-gray-400 hover:text-red-600 transition-colors"
//                               title="Remove image"
//                             >
//                               ✕
//                             </button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Carousel Settings */}
//                     <div className="bg-white p-3 rounded-lg border border-purple-100 space-y-2">
//                       <p className="text-xs font-medium text-gray-700">Carousel Settings:</p>
//                       <div className="grid grid-cols-2 gap-2">
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.autoPlay !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               autoPlay: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Auto-play</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showIndicators !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showIndicators: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show dots</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showNavigation !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showNavigation: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show arrows</span>
//                         </label>
//                         <div>
//                           <label className="text-xs text-gray-700">Interval (ms):</label>
//                           <input
//                             type="number"
//                             min="1000"
//                             max="30000"
//                             step="1000"
//                             value={formData.content.carouselSettings?.interval || 5000}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               interval: parseInt(e.target.value)
//                             })}
//                             className="input-field w-full text-xs"
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 )}
                
//                 {/* Upload Button */}
//                 <label className={`flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-none rounded font-medium text-sm transition-all shadow-md ${uploadingMediaKey === 'hero-carousel' ? 'cursor-not-allowed opacity-75' : 'hover:from-purple-600 hover:to-blue-600 cursor-pointer hover:shadow-lg'}`}>
//                   {uploadingMediaKey === 'hero-carousel' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'hero-carousel' ? 'Uploading...' : (formData.content.heroImages && formData.content.heroImages.length > 0 ? 'Add More Images' : 'Upload Hero Images')}
//                   <input
//                     type="file"
//                     accept="image/*"
//                     multiple
//                     onChange={(e) => {
//                       const files = e.target.files
//                       if (files) {
//                         Array.from(files).forEach(file => {
//                           handleItemImageUpload(null, file, true)
//                         })
//                       }
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//                 <p className="text-xs text-gray-600">
//                   ✨ Upload 1 image for a static hero, or multiple images for an auto-sliding carousel!<br/>
//                   Recommended: 1920x600px or larger. Supports JPEG, PNG, GIF, WebP. No file size limits.
//                 </p>
//               </div>
//             )}



//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Call-to-Action Text
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaText}
//                   onChange={(e) => handleContentChange('ctaText', e.target.value)}
//                   placeholder="e.g., Learn More"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Link
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaLink}
//                   onChange={(e) => handleContentChange('ctaLink', e.target.value)}
//                   placeholder="e.g., #products"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonBackgroundColor || '#000000'}
//                   onChange={(e) => handleStylingChange('ctaButtonBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverBackgroundColor || '#2563eb'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>
//             </div>

//           {/* Items (if applicable) */}
//           <div className="space-y-4 pt-6 border-t border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//             <div className="flex justify-between items-center">
//               <h3 className="text-lg font-semibold text-gray-900">Section Items</h3>
//               <button
//                 type="button"
//                 onClick={handleAddItem}
//                 className="text-sm px-3 py-1 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors font-medium"
//                 style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.ctaButtonBackgroundColor, color: formData.styling?.ctaButtonTextColor } : {}}
//               >
//                 + Add Item
//               </button>
//             </div>

//             <div className="space-y-3">
//               {formData.content.items && formData.content.items.map((item, idx) => (
//                 <div key={item.id} className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//                   <div className="flex justify-between items-center">
//                     <p className="font-medium text-gray-900">Item {idx + 1}</p>
//                     <button
//                       type="button"
//                       onClick={() => handleRemoveItem(item.id)}
//                       className="text-red-600 hover:text-red-900 text-sm font-medium"
//                       style={formData.type === 'section-items' ? { color: formData.styling?.ctaButtonTextColor } : {}}
//                     >
//                       Remove
//                     </button>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.title || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
//                     placeholder="Item title"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <textarea
//                     value={item.description || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
//                     placeholder="Item description"
//                     rows="2"
//                     className="input-field w-full resize-none"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <div className="grid grid-cols-2 gap-3">
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
//                       <input
//                         type="number"
//                         value={item.price || ''}
//                         onChange={(e) => handleUpdateItem(item.id, 'price', e.target.value)}
//                         placeholder="Item price"
//                         step="0.01"
//                         min="0"
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       />
//                     </div>
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
//                       <select
//                         value={item.currency || website?.paymentSettings?.currency || 'NGN'}
//                         onChange={(e) => handleUpdateItem(item.id, 'currency', e.target.value)}
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       >
//                         {getCurrencyOptions().map(option => (
//                           <option key={option.value} value={option.value}>
//                             {option.label}
//                           </option>
//                         ))}
//                       </select>
//                     </div>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.whatsappContact || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'whatsappContact', e.target.value)}
//                     placeholder="WhatsApp contact (e.g., +1234567890)"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   {/* Image/Video Upload Section */}
//                   <div className="space-y-2">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Images / Videos (Gallery)
//                     </label>
                    
//                     {/* Gallery Preview */}
//                     {item.gallery && item.gallery.length > 0 && (
//                       <div className="grid grid-cols-2 gap-2 mb-3">
//                         {item.gallery.map((galleryItem) => (
//                           <div key={galleryItem.id} className="relative group">
//                             {galleryItem.image?.url ? (
//                               <img 
//                                 src={galleryItem.image.url} 
//                                 alt={galleryItem.title || 'Gallery item'} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                               />
//                             ) : galleryItem.video?.url ? (
//                               <video 
//                                 src={galleryItem.video.url} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                                 controls
//                               />
//                             ) : null}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveGalleryItem(item.id, galleryItem.id)}
//                               className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
//                               title="Remove"
//                             >
//                               <FiX className="w-3 h-3" />
//                             </button>
//                           </div>
//                         ))}
//                       </div>
//                     )}

//                     <div className="flex gap-2">
//                       <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm transition-colors ${uploadingMediaKey === `item-${item.id}` ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`} style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}>
//                         {uploadingMediaKey === `item-${item.id}` ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                         {uploadingMediaKey === `item-${item.id}` ? 'Uploading...' : 'Add Image/Video'}
//                         <input
//                           type="file"
//                           accept="image/*,video/*"
//                           onChange={(e) => handleImageInputChange(e, item.id)}
//                           className="hidden"
//                         />
//                       </label>
//                     </div>
                    
//                     {item.gallery && item.gallery.length > 0 && (
//                       <p className="text-xs text-gray-600">
//                         {item.gallery.length} file(s) uploaded
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>



//           {/* Section Styling */}
//           <div className="pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">🎨 Section Styling</h3>
//             <p className="text-sm text-gray-600 mb-4">
//               Customize the appearance of this section on your public website.
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.backgroundColor || '#ffffff'}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.backgroundColor || ''}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     placeholder="#ffffff or white"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a background color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.textColor || '#000000'}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.textColor || ''}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     placeholder="#000000 or black"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a text color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverBackgroundColor || '#f9fafb'}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverBackgroundColor || ''}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     placeholder="#f9fafb or light gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Background color when hovering over the section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverTextColor || '#374151'}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverTextColor || ''}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     placeholder="#374151 or dark gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Text color when hovering over the section
//                 </p>
//               </div>
//             </div>
//           </div>



//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {saving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Section
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }



















// import { useState, useEffect } from 'react'
// import axios from 'axios'
// import { FiX, FiLoader, FiSave, FiUpload, FiImage } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import ProductsManagement from './ProductsManagement'
// import FixturesManagement from './FixturesManagement'
// import ResumesManagement from './ResumesManagement'
// import TeamMembersEditor from './TeamMembersEditor'
// import TicketsManagement from './TicketsManagement'
// import AudioManagement from './AudioManagement'
// import { getCurrencyOptions } from '../../utils/currency'

// const SECTION_TYPES = [
//   'hero',
//   'features',
//   'products',
//   'tickets',
//   'services',
//   'testimonials',
//   'pricing',
//   'gallery',
//   'contact',
//   'newsletter',
//   'team',
//   'about',
//   'faq',
//   'blog',
//   'cart',
//   'checkout',
//   'reviews',
//   'fixtures',
//   'points-table',
//   'resume',
//   'audio',
//   'crypto-product'
// ]

// export default function SectionEditorModal({ isOpen, onClose, onSubmit, section, website }) {
//   // Normalize section type (some places use `sectionType` instead of `type`)
//   const sectionType = section?.type || section?.sectionType || ''
//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     type: '',
//     enabled: true,
//     displayOrder: 0,
//     styling: {
//       backgroundColor: '',
//       textColor: '',
//       hoverBackgroundColor: '',
//       hoverTextColor: '',
//       ctaButtonBackgroundColor: '',
//       ctaButtonTextColor: '',
//       ctaButtonHoverBackgroundColor: '',
//       ctaButtonHoverTextColor: ''
//     },
//     content: {
//       heading: '',
//       subheading: '',
//       ctaText: '',
//       ctaLink: '',
//       ctaBackgroundColor: '',
//       ctaTextColor: '',
//       heroImage: { url: '', publicId: '' },
//       heroImages: [],
//       carouselSettings: {
//         autoPlay: true,
//         interval: 5000,
//         transitionSpeed: 1000,
//         showIndicators: true,
//         showNavigation: true
//       },
//       specialMedia: [],
//       leftSidebar: { contentText: '', media: [] },
//       rightSidebar: { contentText: '', media: [] },
//       items: [],
//       teamMembers: [],
//       audio: [],
//       buyRate: 0,
//       sellRate: 0,
//       usdtBuyRate: 0,
//       usdtSellRate: 0,
//       buyEnabled: true,
//       sellEnabled: true,
//       giftCardEnabled: true,
//       giftCardWhatsApp: '',
//       giftCardNames: []
//     },
//     isNew: false
//   })
//   const [saving, setSaving] = useState(false)
//   const [uploadingMediaKey, setUploadingMediaKey] = useState(null)

//   useEffect(() => {
//     if (section && isOpen) {
//       // Check if this is a new section
//       const isNew = !section._id
      
//       // Normalize section type (some places use `sectionType` instead of `type`)
//       const sectionType = section?.type || section?.sectionType || ''
      
//       // Ensure items have gallery arrays when loading from section
//       const contentWithGalleries = section.content ? {
//         ...section.content,
//         items: (section.content.items || []).map(item => ({
//           ...item,
//           // Migrate old single image format to gallery format if needed
//           gallery: item.gallery && Array.isArray(item.gallery) 
//             ? item.gallery 
//             : (item.image ? [{
//                 id: Date.now(),
//                 image: typeof item.image === 'string' 
//                   ? { url: item.image, publicId: '' } 
//                   : item.image,
//                 video: { url: '', publicId: '' },
//                 title: item.title || '',
//                 description: item.description || ''
//               }] : [])
//         })),
//         teamMembers: section.content.teamMembers || [],
//         heroImages: section.content.heroImages || [],
//         carouselSettings: section.content.carouselSettings || {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         audio: section.content.audio || [],
//         buyRate: section.content.buyRate || 0,
//         sellRate: section.content.sellRate || 0,
//         usdtBuyRate: section.content.usdtBuyRate || 0,
//         usdtSellRate: section.content.usdtSellRate || 0,
//         buyEnabled: section.content.buyEnabled !== false,
//         sellEnabled: section.content.sellEnabled !== false,
//         giftCardEnabled: section.content.giftCardEnabled !== false,
//         giftCardWhatsApp: section.content.giftCardWhatsApp || '',
//         giftCardNames: Array.isArray(section.content.giftCardNames) ? section.content.giftCardNames : []
//       } : {
//         heading: '',
//         subheading: '',
//         ctaText: '',
//         ctaLink: '',
//         ctaBackgroundColor: '',
//         ctaTextColor: '',
//         heroImage: { url: '', publicId: '' },
//         heroImages: [],
//         carouselSettings: {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         specialMedia: [],
//         leftSidebar: { contentText: '', media: [] },
//         rightSidebar: { contentText: '', media: [] },
//         items: [],
//         teamMembers: [],
//         audio: [],
//         buyRate: 0,
//         sellRate: 0,
//         usdtBuyRate: 0,
//         usdtSellRate: 0,
//         giftCardEnabled: true,
//         giftCardWhatsApp: '',
//         giftCardNames: []
//       }
      
//         setFormData({
//           title: section.title || '',
//           description: section.description || '',
//           type: sectionType || '',
//           enabled: !section.disabled,
//           displayOrder: section.displayOrder || 0,
//           styling: section.styling ? {
//             backgroundColor: section.styling.backgroundColor || '',
//             textColor: section.styling.textColor || '',
//             hoverBackgroundColor: section.styling.hoverBackgroundColor || '',
//             hoverTextColor: section.styling.hoverTextColor || '',
//             ctaButtonBackgroundColor: section.styling.ctaButtonBackgroundColor || '',
//             ctaButtonTextColor: section.styling.ctaButtonTextColor || '',
//             ctaButtonHoverBackgroundColor: section.styling.ctaButtonHoverBackgroundColor || '',
//             ctaButtonHoverTextColor: section.styling.ctaButtonHoverTextColor || ''
//           } : {
//             backgroundColor: '',
//             textColor: '',
//             hoverBackgroundColor: '',
//             hoverTextColor: '',
//             ctaButtonBackgroundColor: '',
//             ctaButtonTextColor: '',
//             ctaButtonHoverBackgroundColor: '',
//             ctaButtonHoverTextColor: ''
//           },
//           content: contentWithGalleries,
//           isNew
//         })
//     }
//   }, [section, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value
//     }))
//   }

//   const handleStylingChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       styling: {
//         ...prev.styling,
//         [field]: value
//       }
//     }))
//   }

//   const handleContentChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [field]: value
//       }
//     }))
//   }

//   const handleAddGiftCardName = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: [...(prev.content.giftCardNames || []), '']
//       }
//     }))
//   }

//   const handleUpdateGiftCardName = (index, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.map((name, idx) => idx === index ? value : name)
//       }
//     }))
//   }

//   const handleRemoveGiftCardName = (index) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.filter((_, idx) => idx !== index)
//       }
//     }))
//   }

//   const handleAddItem = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: [
//           ...prev.content.items,
//           { 
//             id: Date.now(), 
//             title: '', 
//             description: '', 
//             price: '', 
//             currency: website?.paymentSettings?.currency || 'NGN',
//             whatsappContact: '', 
//             gallery: [] 
//           }
//         ]
//       }
//     }))
//   }

//   const handleUpdateItem = (itemId, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId ? { ...item, [field]: value } : item
//         )
//       }
//     }))
//   }

//   const handleRemoveItem = (itemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.filter(item => item.id !== itemId)
//       }
//     }))
//   }

//   const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
//   const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg', 'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/x-ms-wmv', 'video/x-m4v', 'video/mpeg', 'video/3gpp', 'video/3gpp2']
//   const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg', 'ogv', 'ogg', '3gp', '3g2']
//   const MAX_IMAGE_SIZE = 20 * 1024 * 1024
//   const MAX_VIDEO_SIZE = 150 * 1024 * 1024

//   const getFileExtension = (file) => {
//     if (!file?.name) return ''
//     return file.name.split('.').pop().toLowerCase()
//   }

//   const isImageFile = (file) => {
//     return file && ACCEPTED_IMAGE_TYPES.includes(file.type)
//   }

//   const isVideoFile = (file) => {
//     if (!file) return false
//     const extension = getFileExtension(file)
//     return (file.type && file.type.startsWith('video/')) || ACCEPTED_VIDEO_TYPES.includes(file.type) || SUPPORTED_VIDEO_EXTENSIONS.includes(extension)
//   }

//   const validateMediaFile = (file, imageOnly = false) => {
//     if (!file) {
//       return { valid: false, message: 'Please select a file to upload' }
//     }

//     if (imageOnly) {
//       if (!isImageFile(file)) {
//         return { valid: false, message: 'Please upload a valid image file' }
//       }
//     } else {
//       if (!isImageFile(file) && !isVideoFile(file)) {
//         return {
//           valid: false,
//           message: 'Please upload a supported image or video file (JPG, PNG, GIF, WEBP, SVG, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, MPEG, OGG, 3GP, 3G2)'
//         }
//       }
//     }

//     const maxSize = isVideoFile(file) ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE
//     if (file.size > maxSize) {
//       return { valid: false, message: `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB` }
//     }

//     return { valid: true }
//   }

//   const uploadMediaFile = async (file) => {
//     const formDataUpload = new FormData()
//     formDataUpload.append('file', file)
//     formDataUpload.append('folder', 'website/sections')

//     const response = await axios.post('upload', formDataUpload, {
//       headers: { 'Content-Type': 'multipart/form-data' }
//     })

//     if (!response?.data?.url) {
//       throw new Error('Upload failed to return a valid URL')
//     }

//     return response.data
//   }

//   const getVideoMimeType = (url) => {
//     if (!url) return undefined
//     const extension = url.split('?')[0].split('.').pop().toLowerCase()
//     const mimeMap = {
//       mp4: 'video/mp4',
//       webm: 'video/webm',
//       ogg: 'video/ogg',
//       ogv: 'video/ogg',
//       mov: 'video/quicktime',
//       avi: 'video/x-msvideo',
//       mkv: 'video/x-matroska',
//       flv: 'video/x-flv',
//       wmv: 'video/x-ms-wmv',
//       m4v: 'video/x-m4v',
//       mpeg: 'video/mpeg',
//       mpg: 'video/mpeg',
//       '3gp': 'video/3gpp',
//       '3g2': 'video/3gpp2'
//     }
//     return mimeMap[extension]
//   }

//   const handleSpecialMediaUpload = async (file, index = null) => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey('special-media')

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           specialMedia: [
//             ...(prev.content.specialMedia || []),
//             {
//               id: Date.now(),
//               url: uploadResult.url,
//               publicId: uploadResult.publicId || '',
//               type: isImage ? 'image' : 'video',
//               display: 'content',
//               alt: file.name,
//               style: {
//                 width: '100%',
//                 height: 'auto',
//                 paddingLeft: '0px',
//                 paddingRight: '0px',
//                 marginLeft: '0px',
//                 marginRight: '0px'
//               }
//             }
//           ]
//         }
//       }))

//       toast.success('Special media uploaded')
//     } catch (err) {
//       console.error('Error uploading special media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload special media')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleSidebarUpload = async (file, side = 'left') => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey(`sidebar-${side}`)

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           [`${side}Sidebar`]: {
//             ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//             media: [
//               ...((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []),
//               {
//                 id: Date.now(),
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 type: isImage ? 'image' : 'video',
//                 alt: file.name
//               }
//             ]
//           }
//         }
//       }))

//       toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar media uploaded`)
//     } catch (err) {
//       console.error('Error uploading sidebar media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload sidebar media')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleRemoveSpecialMedia = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).filter(m => m.id !== id)
//       }
//     }))
//   }

//   const handleChangeSpecialDisplay = (id, display) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => m.id === id ? { ...m, display } : m)
//       }
//     }))
//   }

//   const handleChangeSpecialMediaStyle = (id, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => {
//           if (m.id !== id) return m
//           return {
//             ...m,
//             style: {
//               ...m.style,
//               [field]: value
//             }
//           }
//         })
//       }
//     }))
//   }

//   const handleRemoveSidebarMedia = (side, id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           media: ((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []).filter(m => m.id !== id)
//         }
//       }
//     }))
//   }

//   const handleSidebarContentChangeText = (side, text) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           contentText: text
//         }
//       }
//     }))
//   }

//   const handleItemImageUpload = async (itemId, file, isHeroImage = false) => {
//     if (!file) return

//     if (isHeroImage && !isImageFile(file)) {
//       toast.error('Please upload a valid image file')
//       return
//     }

//     if (!isHeroImage && !isImageFile(file) && !isVideoFile(file)) {
//       toast.error('Please upload a valid image or video file')
//       return
//     }

//     const validation = validateMediaFile(file, isHeroImage)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     setUploadingMediaKey(isHeroImage ? 'hero-carousel' : `item-${itemId}`)

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       if (!uploadResult?.url) {
//         throw new Error('Upload failed to return a valid URL')
//       }

//       if (isHeroImage) {
//         // Handle Hero image - add to carousel array
//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             heroImages: [
//               ...(prev.content.heroImages || []),
//               {
//                 id: `hero-${Date.now()}`,
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 order: (prev.content.heroImages || []).length
//               }
//             ]
//           }
//         }))
//         toast.success('Hero image added to carousel')
//       } else {
//         // Handle gallery items
//         const isImage = isImageFile(file)
//         const isVideo = isVideoFile(file)

//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             items: prev.content.items.map(item =>
//               item.id === itemId
//                 ? {
//                     ...item,
//                     gallery: [
//                       ...(item.gallery || []),
//                       {
//                         id: Date.now(),
//                         image: isImage ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         video: isVideo ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         title: file.name,
//                         description: ''
//                       }
//                     ]
//                   }
//                 : item
//             )
//           }
//         }))

//         toast.success('Image/Video uploaded successfully')
//       }
//     } catch (error) {
//       console.error('Error uploading file:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload file')
//     } finally {
//       setUploadingMediaKey(null)
//     }
//   }

//   const handleImageInputChange = (e, itemId) => {
//     const file = e.target.files?.[0]
//     if (file) {
//       handleItemImageUpload(itemId, file)
//     }
//   }

//   // Helper functions for carousel/hero images management
//   const handleRemoveHeroImage = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         heroImages: (prev.content.heroImages || []).filter(img => img.id !== id)
//       }
//     }))
//     toast.success('Image removed from carousel')
//   }

//   const handleReorderHeroImages = (fromIndex, toIndex) => {
//     setFormData(prev => {
//       const newImages = [...(prev.content.heroImages || [])]
//       const [movedImage] = newImages.splice(fromIndex, 1)
//       newImages.splice(toIndex, 0, movedImage)
//       // Update order property
//       const reorderedImages = newImages.map((img, idx) => ({ ...img, order: idx }))
//       return {
//         ...prev,
//         content: {
//           ...prev.content,
//           heroImages: reorderedImages
//         }
//       }
//     })
//   }


//   const handleRemoveGalleryItem = (itemId, galleryItemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId
//             ? {
//                 ...item,
//                 gallery: (item.gallery || []).filter(g => g.id !== galleryItemId)
//               }
//             : item
//         )
//       }
//     }))
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()

//     // Section title is optional - allow empty for flexible layouts
//     // if (!formData.title.trim()) {
//     //   toast.error('Section title is required')
//     //   return
//     // }

//     // For NEW sections, section type is required
//     if (formData.isNew && !formData.type) {
//       toast.error('Please select a section type')
//       return
//     }

//     setSaving(true)
//     try {
//       const submitData = {
//         title: formData.title,
//         description: formData.description,
//         disabled: !formData.enabled,
//         displayOrder: (function() { const v = parseInt(formData.displayOrder); return isNaN(v) ? 0 : v })(),
//         styling: formData.styling,
//         // sanitize content to match backend schema expectations
//         content: (function sanitizeContent(ct) {
//           if (!ct) return {}

//           const pickHeroImage = (h) => ({
//             url: h?.url || '',
//             publicId: h?.publicId || ''
//           })

//           const sanitizeMediaArray = (arr) => ((arr || [])
//             .map(m => ({
//               url: m?.url || '',
//               publicId: m?.publicId || '',
//               type: m?.type === 'video' ? 'video' : 'image',
//               display: m?.display === 'full' ? 'full' : 'content',
//               alt: m?.alt || '',
//               style: {
//                 width: m?.style?.width || '100%',
//                 height: m?.style?.height || 'auto',
//                 paddingLeft: m?.style?.paddingLeft || '0px',
//                 paddingRight: m?.style?.paddingRight || '0px',
//                 marginLeft: m?.style?.marginLeft || '0px',
//                 marginRight: m?.style?.marginRight || '0px'
//               }
//             }))
//             .filter(m => typeof m.url === 'string' && m.url.trim() !== '')
//           )

//           const specialMedia = sanitizeMediaArray(ct.specialMedia).map(m => ({
//             url: m.url,
//             publicId: m.publicId,
//             type: m.type,
//             display: m.display,
//             alt: m.alt,
//             style: {
//               width: m.style.width,
//               height: m.style.height,
//               paddingLeft: m.style.paddingLeft,
//               paddingRight: m.style.paddingRight,
//               marginLeft: m.style.marginLeft,
//               marginRight: m.style.marginRight
//             }
//           }))

//           const leftSidebar = {
//             contentText: (ct.leftSidebar && ct.leftSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.leftSidebar?.media)
//           }

//           const rightSidebar = {
//             contentText: (ct.rightSidebar && ct.rightSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.rightSidebar?.media)
//           }

//           const items = (ct.items || []).map(it => {
//             const itemObj = {
//               title: it?.title || '',
//               description: it?.description || '',
//               gallery: (it.gallery || []).map(g => {
//                 const galleryItem = {
//                   title: g?.title || '',
//                   description: g?.description || '',
//                   alt: g?.alt || ''
//                 }
                
//                 // Include image if it has a valid URL
//                 if (g?.image?.url) {
//                   galleryItem.image = { url: g.image.url, publicId: g.image.publicId || '' }
//                 }
                
//                 // Include video if it has a valid URL
//                 if (g?.video?.url) {
//                   galleryItem.video = { url: g.video.url, publicId: g.video.publicId || '' }
//                 }
                
//                 return galleryItem
//               }).filter(g => (g.image && g.image.url) || (g.video && g.video.url))
//             }
            
//             // Include WhatsApp contact if provided
//             if (it?.whatsappContact && typeof it.whatsappContact === 'string' && it.whatsappContact.trim()) {
//               itemObj.whatsappContact = it.whatsappContact.trim()
//             }
            
//             // Include price fields
//             if (it?.price !== undefined && it?.price !== null && it?.price !== '') {
//               itemObj.price = Number(it.price) || 0
//             }
//             if (it?.currency && typeof it.currency === 'string' && it.currency.trim()) {
//               itemObj.currency = it.currency.trim()
//             }
            
//             // Include any other fields that might be present
//             if (it?.link) itemObj.link = it.link
//             if (it?.image) itemObj.image = it.image
//             if (it?.video) itemObj.video = it.video
//             if (it?.name) itemObj.name = it.name
//             if (it?.id) itemObj.id = it.id
            
//             return itemObj
//           })

//           const logo = ct.logo ? { url: ct.logo.url || '', publicId: ct.logo.publicId || '', fileName: ct.logo.fileName || '' } : undefined

//           // Sanitize team members array
//           const teamMembers = Array.isArray(ct.teamMembers) ? ct.teamMembers.map(member => ({
//             id: member?.id || Date.now(),
//             name: (member?.name || '').trim(),
//             position: (member?.position || '').trim(),
//             bio: (member?.bio || '').trim(),
//             profileImage: member?.profileImage ? {
//               url: member.profileImage.url || '',
//               publicId: member.profileImage.publicId || ''
//             } : { url: '', publicId: '' }
//           })).filter(member => member.name) : []

//           // Sanitize heroImages array for carousel
//           const heroImages = Array.isArray(ct.heroImages) 
//             ? ct.heroImages
//                 .filter(img => img && (img.url || img.publicId))
//                 .map((img, idx) => ({
//                   id: img.id || `hero-${idx}`,
//                   url: img.url || '',
//                   publicId: img.publicId || '',
//                   order: typeof img.order === 'number' ? img.order : idx
//                 }))
//             : []

//           // Sanitize carousel settings
//           const carouselSettings = ct.carouselSettings ? {
//             autoPlay: ct.carouselSettings.autoPlay !== false,
//             interval: Math.max(1000, Math.min(30000, ct.carouselSettings.interval || 5000)),
//             transitionSpeed: Math.max(300, Math.min(3000, ct.carouselSettings.transitionSpeed || 1000)),
//             showIndicators: ct.carouselSettings.showIndicators !== false,
//             showNavigation: ct.carouselSettings.showNavigation !== false
//           } : {
//             autoPlay: true,
//             interval: 5000,
//             transitionSpeed: 1000,
//             showIndicators: true,
//             showNavigation: true
//           }

//           const audio = Array.isArray(ct.audio)
//             ? ct.audio
//                 .map(audioItem => ({
//                   id: audioItem?.id || Date.now().toString(),
//                   name: (audioItem?.name || '').trim(),
//                   description: (audioItem?.description || '').trim(),
//                   banner: {
//                     url: audioItem?.banner?.url || '',
//                     publicId: audioItem?.banner?.publicId || '',
//                     filename: audioItem?.banner?.filename || ''
//                   },
//                   audioFile: {
//                     url: audioItem?.audioFile?.url || '',
//                     publicId: audioItem?.audioFile?.publicId || '',
//                     filename: audioItem?.audioFile?.filename || ''
//                   }
//                 }))
//                 .filter(item => item.name && item.audioFile?.url)
//             : []

//           return {
//             heading: ct.heading || '',
//             subheading: ct.subheading || '',
//             ctaText: (ct.ctaText || '').toString().trim(),
//             ctaLink: (ct.ctaLink || '').toString().trim(),
//             heroImage: ct.heroImage ? pickHeroImage(ct.heroImage) : undefined,
//             heroImages: heroImages.length > 0 ? heroImages : undefined,
//             carouselSettings: heroImages.length > 0 ? carouselSettings : undefined,
//             heroOverlay: ct.heroOverlay || undefined,
//             heroButtons: Array.isArray(ct.heroButtons) ? ct.heroButtons.map(hb => ({ id: hb.id, text: hb.text, link: hb.link, style: hb.style })) : [],
//             buyRate: typeof ct.buyRate === 'number' ? ct.buyRate : undefined,
//             sellRate: typeof ct.sellRate === 'number' ? ct.sellRate : undefined,
//             usdtBuyRate: typeof ct.usdtBuyRate === 'number' ? ct.usdtBuyRate : undefined,
//             usdtSellRate: typeof ct.usdtSellRate === 'number' ? ct.usdtSellRate : undefined,
//             buyEnabled: ct.buyEnabled !== undefined ? Boolean(ct.buyEnabled) : undefined,
//             sellEnabled: ct.sellEnabled !== undefined ? Boolean(ct.sellEnabled) : undefined,
//             giftCardEnabled: ct.giftCardEnabled !== undefined ? Boolean(ct.giftCardEnabled) : undefined,
//             giftCardWhatsApp: typeof ct.giftCardWhatsApp === 'string' ? ct.giftCardWhatsApp.trim() : undefined,
//             giftCardNames: Array.isArray(ct.giftCardNames) ? ct.giftCardNames.map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean) : undefined,
//             backgroundImage: pickHeroImage(ct.backgroundImage || {}),
//             backgroundVideo: pickHeroImage(ct.backgroundVideo || {}),
//             logo,
//             specialMedia,
//             leftSidebar,
//             rightSidebar,
//             items,
//             teamMembers,
//             gallery: (ct.gallery || []).map(g => ({ title: g.title || '', description: g.description || '', image: g.image?.url ? { url: g.image.url, publicId: g.image.publicId || '' } : undefined, video: g.video?.url ? { url: g.video.url, publicId: g.video.publicId || '' } : undefined, alt: g.alt || '' })).filter(g => (g.image && g.image.url) || (g.video && g.video.url)),
//             audio
//           }
//         })(formData.content)
//       }

//       // Include sectionType for new sections or when it was changed during edit
//       if (formData.type || (formData.isNew && formData.type !== undefined)) {
//         const currentType = section?.sectionType || section?.type || null
//         if (formData.isNew || formData.type !== currentType) {
//           submitData.sectionType = formData.type
//         }
//       }

//       console.log('[SectionEditorModal] ===== SUBMIT START =====')
//       console.log('[SectionEditorModal] Submitting:', {
//         isNew: formData.isNew,
//         title: submitData.title,
//         sectionType: submitData.sectionType,
//         hasHeroImage: !!submitData.content?.heroImage?.url,
//         heroImageSize: submitData.content?.heroImage?.url ? Math.round(submitData.content.heroImage.url.length / 1024) + ' KB' : 'N/A',
//         heroImagesCount: submitData.content?.heroImages?.length || 0,
//         carouselSettingsAutoPlay: submitData.content?.carouselSettings?.autoPlay,
//         carouselSettingsInterval: submitData.content?.carouselSettings?.interval
//       })
//       if (submitData.content?.heroImages && submitData.content.heroImages.length > 0) {
//         console.log('[SectionEditorModal] CAROUSEL DATA:', {
//           imageCount: submitData.content.heroImages.length,
//           images: submitData.content.heroImages.map((img, idx) => ({
//             index: idx,
//             id: img.id,
//             hasUrl: !!img.url,
//             urlSize: img.url ? Math.round(img.url.length / 1024) + ' KB' : 'N/A',
//             order: img.order
//           })),
//           settings: submitData.content.carouselSettings
//         })
//       }
//       console.log('[SectionEditorModal] ITEMS WITH PRICE AND GALLERY:', (submitData.content?.items || []).map((item, idx) => ({
//         itemIndex: idx,
//         title: item.title,
//         price: item.price,
//         currency: item.currency,
//         whatsappContact: item.whatsappContact,
//         galleryCount: item.gallery?.length || 0,
//         galleryItems: (item.gallery || []).map((g, gi) => ({
//           galleryIndex: gi,
//           hasImage: !!g.image?.url,
//           hasVideo: !!g.video?.url,
//           imageSize: g.image?.url ? Math.round(g.image.url.length / 1024) + ' KB' : 'N/A',
//           videoSize: g.video?.url ? Math.round(g.video.url.length / 1024) + ' KB' : 'N/A'
//         }))
//       })))
//       console.log('[SectionEditorModal] TEAM MEMBERS:', submitData.content?.teamMembers ? submitData.content.teamMembers.length : 0, submitData.content?.teamMembers ? submitData.content.teamMembers.map((m, idx) => ({
//         index: idx,
//         name: m.name,
//         position: m.position,
//         hasImage: !!m.profileImage?.url,
//         imageSize: m.profileImage?.url ? Math.round(m.profileImage.url.length / 1024) + ' KB' : 'N/A'
//       })) : 'none')
//       console.log('[SectionEditorModal] CTA FIELDS SUBMITTED:', {
//         hasCtaText: !!submitData.content?.ctaText,
//         hasCtaLink: !!submitData.content?.ctaLink,
//         ctaText: submitData.content?.ctaText,
//         ctaLink: submitData.content?.ctaLink,
//         isValid: !!submitData.content?.ctaText && !!submitData.content?.ctaLink
//       })
//       console.log('[SectionEditorModal] Full submit data keys:', Object.keys(submitData))
//       console.log('[SectionEditorModal] Content keys:', Object.keys(submitData.content || {}))
//       if (submitData.content?.heroImage) {
//         console.log('[SectionEditorModal] Hero Image Object:', {
//           hasUrl: !!submitData.content.heroImage.url,
//           hasPublicId: !!submitData.content.heroImage.publicId,
//           urlLength: submitData.content.heroImage.url?.length || 0,
//           urlPreview: submitData.content.heroImage.url?.substring(0, 50)
//         })
//       }
//       // Await the async onSubmit callback and it will handle closing modal
//       await onSubmit(submitData)
//       console.log('[Modal] onSubmit completed successfully - modal will close via parent')
//     } catch (error) {
//       // Error is already handled and toasted in parent, just log it
//       console.error('[Modal] onSubmit failed (error handled by parent):', error.message)
//       // Don't show another toast - parent already showed one
//     } finally {
//       setSaving(false)
//     }
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">
//             {formData.isNew ? 'Create New Section' : 'Edit Section'}
//           </h2>
//           <button
//             onClick={onClose}
//             disabled={saving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Hero Section Banner */}
//           {formData.type === 'hero' && (
//             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
//               <div className="flex items-start gap-3">
//                 <div className="text-2xl">🎯</div>
//                 <div className="flex-1">
//                   <h3 className="font-semibold text-blue-900 mb-1">Hero Section</h3>
//                   <p className="text-sm text-blue-700">
//                     This is your website's hero/banner section. It appears at the top of your website with a full-width background image.
//                     The hero section cannot be deleted but can be edited and temporarily hidden.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Section Information</h3>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Title (optional)
//                 </label>
//                 <input
//                   type="text"
//                   name="title"
//                   value={formData.title}
//                   onChange={handleInputChange}
//                   placeholder="Enter section title (optional)"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Type
//                 </label>
//                 <select
//                   name="type"
//                   value={formData.type}
//                   onChange={handleInputChange}
//                   className="input-field w-full"
//                 >
//                   <option value="">Select type</option>
//                   {SECTION_TYPES.map(type => (
//                     <option key={type} value={type}>
//                       {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of this section"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             <div className="flex items-center gap-2">
//               <input
//                 type="checkbox"
//                 id="enabled"
//                 name="enabled"
//                 checked={formData.enabled}
//                 onChange={handleInputChange}
//                 className="w-4 h-4 rounded border-gray-300"
//               />
//               <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
//                 Section is enabled
//               </label>
//             </div>
//           </div>

//           {/* Products Management - Show when products section type is selected */}
//           {formData.type === 'products' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Product Management</h3>
//               <ProductsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Tickets Management - Show when tickets section type is selected */}
//           {formData.type === 'tickets' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">🎫 Ticket Management</h3>
//               <TicketsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Contact Form - Show message when contact section type is selected */}
//             {formData.type === 'contact' && website ? (
//               <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//                 <h3 className="text-lg font-semibold text-gray-900 mb-2">📧 Contact Form</h3>
//                 <p className="text-sm text-gray-700">
//                   A contact form will be displayed on your public website for this section after you save the settings.
//                 </p>
//               </div>
//             ) : null}

//           {/* Reviews Section - Show when reviews section type is selected */}
//           {formData.type === 'reviews' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">⭐ Customer Reviews Section</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays customer reviews and allows visitors to submit new reviews.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Visitors can submit reviews with a comment and optional star rating</li>
//                 <li>✓ All reviews are saved as "pending" until you approve them</li>
//                 <li>✓ Only approved reviews appear on the public website</li>
//                 <li>✓ Manage reviews (approve/reject/delete) in the "Reviews" dashboard tab</li>
//               </ul>
//             </div>
//           )}

//           {/* Fixtures Section - Show when fixtures section type is selected */}
//           {formData.type === 'fixtures' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 Football Fixtures Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Manage football match fixtures and view automated league standings.
//               </p>
//               <FixturesManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           )}

//           {/* Points Table Section - Show when points-table section type is selected */}
//           {formData.type === 'points-table' && (
//             <div className="pt-6 border-t border-gray-200 bg-indigo-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 League Points Table</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays the league standings and is automatically updated when fixtures are completed.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Standings are calculated automatically from finished fixtures</li>
//                 <li>✓ Table shows: Position, Team, Played, Wins, Draws, Losses, Goals For/Against, Goal Difference, Points</li>
//                 <li>✓ Teams are ranked by points, then goal difference, then goals for</li>
//                 <li>✓ Points: Win = 3, Draw = 1, Loss = 0</li>
//               </ul>
//             </div>
//           )}

//           {/* Resume Upload Section - Show when resume section type is selected */}
//           {formData.type === 'resume' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Resume Upload Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 This section allows visitors to submit their resumes for job positions.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-4">
//                 <li>✓ Visitors can upload PDF, DOC, DOCX, or TXT files (max 5MB)</li>
//                 <li>✓ Submitted resumes appear in your Resume Dashboard</li>
//                 <li>✓ You receive Telegram notifications for new submissions</li>
//                 <li>✓ Track candidate status: Submitted → Reviewed → Shortlisted/Rejected</li>
//               </ul>
              
//               {/* Resume Section Toggle */}
//               <div className="mb-4 p-3 bg-white rounded border">
//                 <div className="flex items-center gap-2">
//                   <input
//                     type="checkbox"
//                     id="resumeToggle"
//                     name="enabled"
//                     checked={formData.enabled}
//                     onChange={handleInputChange}
//                     className="w-4 h-4 rounded border-gray-300"
//                   />
//                   <label htmlFor="resumeToggle" className="text-sm font-medium text-gray-700">
//                     Resume form is visible on public website
//                   </label>
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Turn ON to show the resume submission form to visitors. Turn OFF to hide it completely.
//                 </p>
//               </div>
              
//               <ResumesManagement website={website} />
//             </div>
//           )}

//           {/* Team Members Section - Show when team section type is selected */}
//           {formData.type === 'team' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">👥 Team Members Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Create and manage your team members. Each team member can have a profile picture, name, position, and bio.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Add multiple team members with full names and roles</li>
//                 <li>✓ Upload profile pictures for each team member</li>
//                 <li>✓ Add optional bio/description for each member</li>
//                 <li>✓ Team will display in a responsive grid on your public website</li>
//                 <li>✓ Fully responsive layout for mobile, tablet, and desktop</li>
//               </ul>
              
//               <TeamMembersEditor formData={formData} setFormData={setFormData} />
//             </div>
//           )}
//           {/* Audio Section - Show when audio section type is selected */}
//           {formData.type === 'audio' && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🎵 Audio Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Add audio files to your website. Visitors can play audio directly on your site.
//               </p>
//               <AudioManagement formData={formData} setFormData={setFormData} />
//             </div>
//           )}

//           {/* Crypto Product Section - Show when crypto-product section type is selected */}
//           {formData.type === 'crypto-product' && (
//             <div className="pt-6 border-t border-gray-200 bg-orange-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">₿ Crypto Trading Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Enable cryptocurrency buy/sell functionality on your website. Users can purchase or sell crypto directly through secure payment processing.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Support for multiple cryptocurrencies (BTC, ETH, USDT, BNB, ADA, SOL, DOT, DOGE, XRP, LTC)</li>
//                 <li>✓ Secure buy/sell transactions with payment provider integration</li>
//                 <li>✓ Real-time exchange rates with 10-minute price locking</li>
//                 <li>✓ Automatic payment detection and status updates</li>
//                 <li>✓ Transaction dashboard for managing all orders</li>
//                 <li>✓ Admin notifications for completed transactions</li>
//               </ul>

//               {/* Buy/Sell Toggle Controls */}
//               <div className="bg-white rounded-lg p-4 border mb-4">
//                 <h4 className="font-semibold text-gray-900 mb-4">Feature Controls</h4>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Buy Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to buy cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.buyEnabled !== false}
//                         onChange={(e) => handleContentChange('buyEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Sell Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to sell cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.sellEnabled !== false}
//                         onChange={(e) => handleContentChange('sellEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                 </div>
//               </div>

//               {/* Rate Configuration */}
//               <div className="bg-white rounded-lg p-4 border">
//                 <div className="flex items-center justify-between mb-4">
//                   <h4 className="font-semibold text-gray-900">Exchange Rate Configuration</h4>
//                   {(formData.content.buyRate || formData.content.sellRate || formData.content.usdtBuyRate || formData.content.usdtSellRate) && (
//                     <button
//                       type="button"
//                       onClick={() => {
//                         handleContentChange('buyRate', null)
//                         handleContentChange('sellRate', null)
//                         handleContentChange('usdtBuyRate', null)
//                         handleContentChange('usdtSellRate', null)
//                       }}
//                       className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded border border-red-300"
//                     >
//                       Clear All Rates
//                     </button>
//                   )}
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Buy Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.buyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('buyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('buyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 50000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users buy BTC (in Naira)
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Sell Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.sellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('sellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('sellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 48000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users sell BTC (in Naira)
//                     </p>
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Buy Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtBuyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtBuyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtBuyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1500"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific buy rate for USDT transactions
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Sell Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtSellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtSellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtSellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1450"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific sell rate for USDT transactions
//                     </p>
//                   </div>
//                 </div>
//                 <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
//                   <p className="text-sm text-blue-800">
//                     <strong>Note:</strong> Rates must be valid numeric values greater than 0. These rates will be displayed on your public website and used for all transaction calculations.
//                     Update them regularly to reflect current market conditions. Use "Clear Rates" to remove rates entirely.
//                   </p>
//                 </div>
//               </div>

//               <div className="bg-white rounded-lg p-4 border mt-4">
//                 <div className="flex items-center justify-between mb-4">
//                   <div>
//                     <h4 className="font-semibold text-gray-900">Gift Card / Airtime Settings</h4>
//                     <p className="text-sm text-gray-500">Configure the gift card option shown on public crypto pages.</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={formData.content.giftCardEnabled !== false}
//                       onChange={(e) => handleContentChange('giftCardEnabled', e.target.checked)}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                   </label>
//                 </div>

//                 <div className="grid grid-cols-1 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       WhatsApp Number for Gift Card Requests
//                     </label>
//                     <input
//                       type="text"
//                       value={formData.content.giftCardWhatsApp || ''}
//                       onChange={(e) => handleContentChange('giftCardWhatsApp', e.target.value)}
//                       placeholder="e.g., +2348012345678"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       This number will be used to prefill WhatsApp messages when customers submit a gift card request.
//                     </p>
//                   </div>

//                   <div>
//                     <div className="flex items-center justify-between mb-2">
//                       <label className="block text-sm font-medium text-gray-700">Gift Card / Airtime Types</label>
//                       <button
//                         type="button"
//                         onClick={handleAddGiftCardName}
//                         className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded border border-orange-200 hover:bg-orange-200"
//                       >
//                         Add type
//                       </button>
//                     </div>
//                     <p className="text-xs text-gray-500 mb-3">
//                       Add the gift card names that users can select when requesting gift card payment.
//                     </p>
//                     <div className="space-y-3">
//                       {(formData.content.giftCardNames || []).map((name, index) => (
//                         <div key={index} className="flex items-center gap-2">
//                           <input
//                             type="text"
//                             value={name}
//                             onChange={(e) => handleUpdateGiftCardName(index, e.target.value)}
//                             placeholder={`Gift card type ${index + 1}`}
//                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveGiftCardName(index)}
//                             className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded border border-red-200 hover:bg-red-200"
//                           >
//                             Remove
//                           </button>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
//                 <p className="text-sm text-yellow-800">
//                   <strong>Note:</strong> Crypto trading requires proper payment provider setup and compliance with local regulations.
//                   Contact support for payment provider integration assistance.
//                 </p>
//               </div>
//             </div>
//           )}

//           {/* Generic Content Fields - Show for all section types */}
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Heading
//               </label>
//               <input
//                 type="text"
//                 value={formData.content.heading}
//                 onChange={(e) => handleContentChange('heading', e.target.value)}
//                 placeholder="Main heading"
//                 className="input-field w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Subheading
//               </label>
//               <textarea
//                 value={formData.content.subheading}
//                 onChange={(e) => handleContentChange('subheading', e.target.value)}
//                 placeholder="Subheading or description"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Special Media Upload (applies to section) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📁 Special Media Upload</h3>
//               <p className="text-sm text-gray-600 mb-3">Upload images or videos that will appear in this section on the public website. You can choose to display them full-width or constrained to the content width. There are no file size limits.</p>

//               <div className="flex items-center gap-3">
//                 <label className={`flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === 'special-media' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'special-media' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'special-media' ? 'Uploading...' : 'Upload Media'}
//                   <input
//                     type="file"
//                     accept="image/*,video/*"
//                     onChange={(e) => {
//                       const file = e.target.files?.[0]
//                       if (file) handleSpecialMediaUpload(file)
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//               </div>

//               <div className="grid grid-cols-3 gap-3 mt-4">
//                 {(formData.content.specialMedia || []).map(m => (
//                   <div key={m.id} className="relative border rounded-lg overflow-hidden">
//                     {m.type === 'image' ? (
//                       <img src={m.url} alt={m.alt || 'media'} className="w-full h-28 object-cover" />
//                     ) : (
//                       <video className="w-full h-28 object-cover" controls playsInline preload="metadata">
//                         <source src={m.url} type={getVideoMimeType(m.url)} />
//                         Your browser does not support video playback.
//                       </video>
//                     )}
//                     <div className="p-2 bg-white">
//                       <select value={m.display || 'content'} onChange={(e) => handleChangeSpecialDisplay(m.id, e.target.value)} className="input-field w-full mb-2">
//                         <option value="content">Aligned with content width</option>
//                         <option value="full">Full width</option>
//                       </select>

//                       <div className="grid grid-cols-2 gap-2 mb-2">
//                         <label className="text-xs font-medium text-gray-600">
//                           Width
//                           <input
//                             type="text"
//                             value={m.style?.width || '100%'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'width', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 100%, 400px"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Height
//                           <input
//                             type="text"
//                             value={m.style?.height || 'auto'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'height', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. auto, 300px"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Padding Left
//                           <input
//                             type="text"
//                             value={m.style?.paddingLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingLeft', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Padding Right
//                           <input
//                             type="text"
//                             value={m.style?.paddingRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingRight', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Margin Left
//                           <input
//                             type="text"
//                             value={m.style?.marginLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginLeft', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Margin Right
//                           <input
//                             type="text"
//                             value={m.style?.marginRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginRight', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                       </div>

//                       <div className="flex justify-between items-center">
//                         <button type="button" onClick={() => handleRemoveSpecialMedia(m.id)} className="text-sm text-red-600">Remove</button>
//                         <span className="text-xs text-gray-500">{m.type}</span>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Sidebars (Left / Right) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Left Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.leftSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('left', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className={`flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === 'sidebar-left' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'sidebar-left' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'sidebar-left' ? 'Uploading...' : 'Upload Left Sidebar Media'}
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'left') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.leftSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('left', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>

//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Right Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.rightSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('right', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className={`flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm ${uploadingMediaKey === 'sidebar-right' ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`}>
//                   {uploadingMediaKey === 'sidebar-right' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'sidebar-right' ? 'Uploading...' : 'Upload Right Sidebar Media'}
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'right') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.rightSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('right', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             {/* Hero Image Carousel Upload - Show when hero section type is selected */}
//             {formData.type === 'hero' && (
//               <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-5 rounded-lg border border-purple-200 space-y-4">
//                 <div className="flex items-center justify-between">
//                   <label className="block text-sm font-bold text-gray-900">
//                     🎠 Hero Image Carousel
//                   </label>
//                   {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                     <span className="text-xs font-semibold bg-purple-200 text-purple-900 px-3 py-1 rounded-full">
//                       {formData.content.heroImages.length} image{formData.content.heroImages.length !== 1 ? 's' : ''}
//                     </span>
//                   )}
//                 </div>
                
//                 {/* Hero Images Gallery Preview */}
//                 {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                   <div className="space-y-3">
//                     <p className="text-xs text-gray-600 font-medium">Images in carousel (drag to reorder):</p>
//                     <div className="space-y-2 bg-white p-3 rounded-lg border border-purple-100">
//                       {formData.content.heroImages.map((img, idx) => (
//                         <div
//                           key={img.id}
//                           className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200 hover:border-purple-300 transition-colors group"
//                         >
//                           <div className="flex-shrink-0 text-gray-500 font-semibold text-sm w-6 text-center">
//                             {idx + 1}
//                           </div>
//                           {img.url && (
//                             <img 
//                               src={img.url} 
//                               alt={`Hero carousel ${idx + 1}`}
//                               className="w-16 h-16 object-cover rounded border border-gray-300"
//                             />
//                           )}
//                           <div className="flex-1 min-w-0">
//                             <p className="text-xs text-gray-600 truncate">
//                               {img.url ? `Image ${idx + 1} • ${Math.round(img.url.length / 1024)}KB` : 'Pending...'}
//                             </p>
//                           </div>
//                           <div className="flex gap-1">
//                             {idx > 0 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx - 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move up"
//                               >
//                                 ↑
//                               </button>
//                             )}
//                             {idx < formData.content.heroImages.length - 1 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx + 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move down"
//                               >
//                                 ↓
//                               </button>
//                             )}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveHeroImage(img.id)}
//                               className="p-1 text-gray-400 hover:text-red-600 transition-colors"
//                               title="Remove image"
//                             >
//                               ✕
//                             </button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Carousel Settings */}
//                     <div className="bg-white p-3 rounded-lg border border-purple-100 space-y-2">
//                       <p className="text-xs font-medium text-gray-700">Carousel Settings:</p>
//                       <div className="grid grid-cols-2 gap-2">
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.autoPlay !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               autoPlay: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Auto-play</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showIndicators !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showIndicators: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show dots</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showNavigation !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showNavigation: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show arrows</span>
//                         </label>
//                         <div>
//                           <label className="text-xs text-gray-700">Interval (ms):</label>
//                           <input
//                             type="number"
//                             min="1000"
//                             max="30000"
//                             step="1000"
//                             value={formData.content.carouselSettings?.interval || 5000}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               interval: parseInt(e.target.value)
//                             })}
//                             className="input-field w-full text-xs"
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 )}
                
//                 {/* Upload Button */}
//                 <label className={`flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-none rounded font-medium text-sm transition-all shadow-md ${uploadingMediaKey === 'hero-carousel' ? 'cursor-not-allowed opacity-75' : 'hover:from-purple-600 hover:to-blue-600 cursor-pointer hover:shadow-lg'}`}>
//                   {uploadingMediaKey === 'hero-carousel' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                   {uploadingMediaKey === 'hero-carousel' ? 'Uploading...' : (formData.content.heroImages && formData.content.heroImages.length > 0 ? 'Add More Images' : 'Upload Hero Images')}
//                   <input
//                     type="file"
//                     accept="image/*"
//                     multiple
//                     onChange={(e) => {
//                       const files = e.target.files
//                       if (files) {
//                         Array.from(files).forEach(file => {
//                           handleItemImageUpload(null, file, true)
//                         })
//                       }
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//                 <p className="text-xs text-gray-600">
//                   ✨ Upload 1 image for a static hero, or multiple images for an auto-sliding carousel!<br/>
//                   Recommended: 1920x600px or larger. Supports JPEG, PNG, GIF, WebP. No file size limits.
//                 </p>
//               </div>
//             )}



//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Call-to-Action Text
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaText}
//                   onChange={(e) => handleContentChange('ctaText', e.target.value)}
//                   placeholder="e.g., Learn More"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Link
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaLink}
//                   onChange={(e) => handleContentChange('ctaLink', e.target.value)}
//                   placeholder="e.g., #products"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonBackgroundColor || '#000000'}
//                   onChange={(e) => handleStylingChange('ctaButtonBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverBackgroundColor || '#2563eb'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>
//             </div>

//           {/* Items (if applicable) */}
//           <div className="space-y-4 pt-6 border-t border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//             <div className="flex justify-between items-center">
//               <h3 className="text-lg font-semibold text-gray-900">Section Items</h3>
//               <button
//                 type="button"
//                 onClick={handleAddItem}
//                 className="text-sm px-3 py-1 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors font-medium"
//                 style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.ctaButtonBackgroundColor, color: formData.styling?.ctaButtonTextColor } : {}}
//               >
//                 + Add Item
//               </button>
//             </div>

//             <div className="space-y-3">
//               {formData.content.items && formData.content.items.map((item, idx) => (
//                 <div key={item.id} className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//                   <div className="flex justify-between items-center">
//                     <p className="font-medium text-gray-900">Item {idx + 1}</p>
//                     <button
//                       type="button"
//                       onClick={() => handleRemoveItem(item.id)}
//                       className="text-red-600 hover:text-red-900 text-sm font-medium"
//                       style={formData.type === 'section-items' ? { color: formData.styling?.ctaButtonTextColor } : {}}
//                     >
//                       Remove
//                     </button>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.title || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
//                     placeholder="Item title"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <textarea
//                     value={item.description || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
//                     placeholder="Item description"
//                     rows="2"
//                     className="input-field w-full resize-none"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <div className="grid grid-cols-2 gap-3">
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
//                       <input
//                         type="number"
//                         value={item.price || ''}
//                         onChange={(e) => handleUpdateItem(item.id, 'price', e.target.value)}
//                         placeholder="Item price"
//                         step="0.01"
//                         min="0"
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       />
//                     </div>
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
//                       <select
//                         value={item.currency || website?.paymentSettings?.currency || 'NGN'}
//                         onChange={(e) => handleUpdateItem(item.id, 'currency', e.target.value)}
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       >
//                         {getCurrencyOptions().map(option => (
//                           <option key={option.value} value={option.value}>
//                             {option.label}
//                           </option>
//                         ))}
//                       </select>
//                     </div>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.whatsappContact || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'whatsappContact', e.target.value)}
//                     placeholder="WhatsApp contact (e.g., +1234567890)"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   {/* Image/Video Upload Section */}
//                   <div className="space-y-2">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Images / Videos (Gallery)
//                     </label>
                    
//                     {/* Gallery Preview */}
//                     {item.gallery && item.gallery.length > 0 && (
//                       <div className="grid grid-cols-2 gap-2 mb-3">
//                         {item.gallery.map((galleryItem) => (
//                           <div key={galleryItem.id} className="relative group">
//                             {galleryItem.image?.url ? (
//                               <img 
//                                 src={galleryItem.image.url} 
//                                 alt={galleryItem.title || 'Gallery item'} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                               />
//                             ) : galleryItem.video?.url ? (
//                               <video 
//                                 src={galleryItem.video.url} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                                 controls
//                               />
//                             ) : null}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveGalleryItem(item.id, galleryItem.id)}
//                               className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
//                               title="Remove"
//                             >
//                               <FiX className="w-3 h-3" />
//                             </button>
//                           </div>
//                         ))}
//                       </div>
//                     )}

//                     <div className="flex gap-2">
//                       <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium text-sm transition-colors ${uploadingMediaKey === `item-${item.id}` ? 'cursor-not-allowed opacity-70' : 'hover:bg-blue-100 cursor-pointer'}`} style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}>
//                         {uploadingMediaKey === `item-${item.id}` ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
//                         {uploadingMediaKey === `item-${item.id}` ? 'Uploading...' : 'Add Image/Video'}
//                         <input
//                           type="file"
//                           accept="image/*,video/*"
//                           onChange={(e) => handleImageInputChange(e, item.id)}
//                           className="hidden"
//                         />
//                       </label>
//                     </div>
                    
//                     {item.gallery && item.gallery.length > 0 && (
//                       <p className="text-xs text-gray-600">
//                         {item.gallery.length} file(s) uploaded
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>



//           {/* Section Styling */}
//           <div className="pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">🎨 Section Styling</h3>
//             <p className="text-sm text-gray-600 mb-4">
//               Customize the appearance of this section on your public website.
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.backgroundColor || '#ffffff'}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.backgroundColor || ''}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     placeholder="#ffffff or white"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a background color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.textColor || '#000000'}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.textColor || ''}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     placeholder="#000000 or black"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a text color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverBackgroundColor || '#f9fafb'}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverBackgroundColor || ''}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     placeholder="#f9fafb or light gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Background color when hovering over the section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverTextColor || '#374151'}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverTextColor || ''}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     placeholder="#374151 or dark gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Text color when hovering over the section
//                 </p>
//               </div>
//             </div>
//           </div>



//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {saving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Section
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }

























// import { useState, useEffect } from 'react'
// import axios from 'axios'
// import { FiX, FiLoader, FiSave, FiUpload, FiImage } from 'react-icons/fi'
// import toast from 'react-hot-toast'
// import ProductsManagement from './ProductsManagement'
// import FixturesManagement from './FixturesManagement'
// import ResumesManagement from './ResumesManagement'
// import TeamMembersEditor from './TeamMembersEditor'
// import TicketsManagement from './TicketsManagement'
// import AudioManagement from './AudioManagement'
// import { getCurrencyOptions } from '../../utils/currency'

// const SECTION_TYPES = [
//   'hero',
//   'features',
//   'products',
//   'tickets',
//   'services',
//   'testimonials',
//   'pricing',
//   'gallery',
//   'contact',
//   'newsletter',
//   'team',
//   'about',
//   'faq',
//   'blog',
//   'cart',
//   'checkout',
//   'reviews',
//   'fixtures',
//   'points-table',
//   'resume',
//   'audio',
//   'crypto-product'
// ]

// export default function SectionEditorModal({ isOpen, onClose, onSubmit, section, website }) {
//   // Normalize section type (some places use `sectionType` instead of `type`)
//   const sectionType = section?.type || section?.sectionType || ''
//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     type: '',
//     enabled: true,
//     displayOrder: 0,
//     styling: {
//       backgroundColor: '',
//       textColor: '',
//       hoverBackgroundColor: '',
//       hoverTextColor: '',
//       ctaButtonBackgroundColor: '',
//       ctaButtonTextColor: '',
//       ctaButtonHoverBackgroundColor: '',
//       ctaButtonHoverTextColor: ''
//     },
//     content: {
//       heading: '',
//       subheading: '',
//       ctaText: '',
//       ctaLink: '',
//       ctaBackgroundColor: '',
//       ctaTextColor: '',
//       heroImage: { url: '', publicId: '' },
//       heroImages: [],
//       carouselSettings: {
//         autoPlay: true,
//         interval: 5000,
//         transitionSpeed: 1000,
//         showIndicators: true,
//         showNavigation: true
//       },
//       specialMedia: [],
//       leftSidebar: { contentText: '', media: [] },
//       rightSidebar: { contentText: '', media: [] },
//       items: [],
//       teamMembers: [],
//       audio: [],
//       buyRate: 0,
//       sellRate: 0,
//       usdtBuyRate: 0,
//       usdtSellRate: 0,
//       buyEnabled: true,
//       sellEnabled: true,
//       giftCardEnabled: true,
//       giftCardWhatsApp: '',
//       giftCardNames: []
//     },
//     isNew: false
//   })
//   const [saving, setSaving] = useState(false)

//   useEffect(() => {
//     if (section && isOpen) {
//       // Check if this is a new section
//       const isNew = !section._id
      
//       // Normalize section type (some places use `sectionType` instead of `type`)
//       const sectionType = section?.type || section?.sectionType || ''
      
//       // Ensure items have gallery arrays when loading from section
//       const contentWithGalleries = section.content ? {
//         ...section.content,
//         items: (section.content.items || []).map(item => ({
//           ...item,
//           // Migrate old single image format to gallery format if needed
//           gallery: item.gallery && Array.isArray(item.gallery) 
//             ? item.gallery 
//             : (item.image ? [{
//                 id: Date.now(),
//                 image: typeof item.image === 'string' 
//                   ? { url: item.image, publicId: '' } 
//                   : item.image,
//                 video: { url: '', publicId: '' },
//                 title: item.title || '',
//                 description: item.description || ''
//               }] : [])
//         })),
//         teamMembers: section.content.teamMembers || [],
//         heroImages: section.content.heroImages || [],
//         carouselSettings: section.content.carouselSettings || {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         audio: section.content.audio || [],
//         buyRate: section.content.buyRate || 0,
//         sellRate: section.content.sellRate || 0,
//         usdtBuyRate: section.content.usdtBuyRate || 0,
//         usdtSellRate: section.content.usdtSellRate || 0,
//         buyEnabled: section.content.buyEnabled !== false,
//         sellEnabled: section.content.sellEnabled !== false,
//         giftCardEnabled: section.content.giftCardEnabled !== false,
//         giftCardWhatsApp: section.content.giftCardWhatsApp || '',
//         giftCardNames: Array.isArray(section.content.giftCardNames) ? section.content.giftCardNames : []
//       } : {
//         heading: '',
//         subheading: '',
//         ctaText: '',
//         ctaLink: '',
//         ctaBackgroundColor: '',
//         ctaTextColor: '',
//         heroImage: { url: '', publicId: '' },
//         heroImages: [],
//         carouselSettings: {
//           autoPlay: true,
//           interval: 5000,
//           transitionSpeed: 1000,
//           showIndicators: true,
//           showNavigation: true
//         },
//         specialMedia: [],
//         leftSidebar: { contentText: '', media: [] },
//         rightSidebar: { contentText: '', media: [] },
//         items: [],
//         teamMembers: [],
//         audio: [],
//         buyRate: 0,
//         sellRate: 0,
//         usdtBuyRate: 0,
//         usdtSellRate: 0,
//         giftCardEnabled: true,
//         giftCardWhatsApp: '',
//         giftCardNames: []
//       }
      
//         setFormData({
//           title: section.title || '',
//           description: section.description || '',
//           type: sectionType || '',
//           enabled: !section.disabled,
//           displayOrder: section.displayOrder || 0,
//           styling: section.styling ? {
//             backgroundColor: section.styling.backgroundColor || '',
//             textColor: section.styling.textColor || '',
//             hoverBackgroundColor: section.styling.hoverBackgroundColor || '',
//             hoverTextColor: section.styling.hoverTextColor || '',
//             ctaButtonBackgroundColor: section.styling.ctaButtonBackgroundColor || '',
//             ctaButtonTextColor: section.styling.ctaButtonTextColor || '',
//             ctaButtonHoverBackgroundColor: section.styling.ctaButtonHoverBackgroundColor || '',
//             ctaButtonHoverTextColor: section.styling.ctaButtonHoverTextColor || ''
//           } : {
//             backgroundColor: '',
//             textColor: '',
//             hoverBackgroundColor: '',
//             hoverTextColor: '',
//             ctaButtonBackgroundColor: '',
//             ctaButtonTextColor: '',
//             ctaButtonHoverBackgroundColor: '',
//             ctaButtonHoverTextColor: ''
//           },
//           content: contentWithGalleries,
//           isNew
//         })
//     }
//   }, [section, isOpen])

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value
//     }))
//   }

//   const handleStylingChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       styling: {
//         ...prev.styling,
//         [field]: value
//       }
//     }))
//   }

//   const handleContentChange = (field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [field]: value
//       }
//     }))
//   }

//   const handleAddGiftCardName = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: [...(prev.content.giftCardNames || []), '']
//       }
//     }))
//   }

//   const handleUpdateGiftCardName = (index, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.map((name, idx) => idx === index ? value : name)
//       }
//     }))
//   }

//   const handleRemoveGiftCardName = (index) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         giftCardNames: prev.content.giftCardNames.filter((_, idx) => idx !== index)
//       }
//     }))
//   }

//   const handleAddItem = () => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: [
//           ...prev.content.items,
//           { 
//             id: Date.now(), 
//             title: '', 
//             description: '', 
//             price: '', 
//             currency: website?.paymentSettings?.currency || 'NGN',
//             whatsappContact: '', 
//             gallery: [] 
//           }
//         ]
//       }
//     }))
//   }

//   const handleUpdateItem = (itemId, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId ? { ...item, [field]: value } : item
//         )
//       }
//     }))
//   }

//   const handleRemoveItem = (itemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.filter(item => item.id !== itemId)
//       }
//     }))
//   }

//   const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
//   const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg', 'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/x-ms-wmv', 'video/x-m4v', 'video/mpeg', 'video/3gpp', 'video/3gpp2']
//   const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg', 'ogv', 'ogg', '3gp', '3g2']
//   const MAX_IMAGE_SIZE = 20 * 1024 * 1024
//   const MAX_VIDEO_SIZE = 150 * 1024 * 1024

//   const getFileExtension = (file) => {
//     if (!file?.name) return ''
//     return file.name.split('.').pop().toLowerCase()
//   }

//   const isImageFile = (file) => {
//     return file && ACCEPTED_IMAGE_TYPES.includes(file.type)
//   }

//   const isVideoFile = (file) => {
//     if (!file) return false
//     const extension = getFileExtension(file)
//     return (file.type && file.type.startsWith('video/')) || ACCEPTED_VIDEO_TYPES.includes(file.type) || SUPPORTED_VIDEO_EXTENSIONS.includes(extension)
//   }

//   const validateMediaFile = (file, imageOnly = false) => {
//     if (!file) {
//       return { valid: false, message: 'Please select a file to upload' }
//     }

//     if (imageOnly) {
//       if (!isImageFile(file)) {
//         return { valid: false, message: 'Please upload a valid image file' }
//       }
//     } else {
//       if (!isImageFile(file) && !isVideoFile(file)) {
//         return {
//           valid: false,
//           message: 'Please upload a supported image or video file (JPG, PNG, GIF, WEBP, SVG, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, MPEG, OGG, 3GP, 3G2)'
//         }
//       }
//     }

//     const maxSize = isVideoFile(file) ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE
//     if (file.size > maxSize) {
//       return { valid: false, message: `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB` }
//     }

//     return { valid: true }
//   }

//   const uploadMediaFile = async (file) => {
//     const formDataUpload = new FormData()
//     formDataUpload.append('file', file)
//     formDataUpload.append('folder', 'website/sections')

//     const response = await axios.post('upload', formDataUpload, {
//       headers: { 'Content-Type': 'multipart/form-data' }
//     })

//     if (!response?.data?.url) {
//       throw new Error('Upload failed to return a valid URL')
//     }

//     return response.data
//   }

//   const getVideoMimeType = (url) => {
//     if (!url) return undefined
//     const extension = url.split('?')[0].split('.').pop().toLowerCase()
//     const mimeMap = {
//       mp4: 'video/mp4',
//       webm: 'video/webm',
//       ogg: 'video/ogg',
//       ogv: 'video/ogg',
//       mov: 'video/quicktime',
//       avi: 'video/x-msvideo',
//       mkv: 'video/x-matroska',
//       flv: 'video/x-flv',
//       wmv: 'video/x-ms-wmv',
//       m4v: 'video/x-m4v',
//       mpeg: 'video/mpeg',
//       mpg: 'video/mpeg',
//       '3gp': 'video/3gpp',
//       '3g2': 'video/3gpp2'
//     }
//     return mimeMap[extension]
//   }

//   const handleSpecialMediaUpload = async (file, index = null) => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           specialMedia: [
//             ...(prev.content.specialMedia || []),
//             {
//               id: Date.now(),
//               url: uploadResult.url,
//               publicId: uploadResult.publicId || '',
//               type: isImage ? 'image' : 'video',
//               display: 'content',
//               alt: file.name,
//               style: {
//                 width: '100%',
//                 height: 'auto',
//                 paddingLeft: '0px',
//                 paddingRight: '0px',
//                 marginLeft: '0px',
//                 marginRight: '0px'
//               }
//             }
//           ]
//         }
//       }))

//       toast.success('Special media uploaded')
//     } catch (err) {
//       console.error('Error uploading special media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload special media')
//     }
//   }

//   const handleSidebarUpload = async (file, side = 'left') => {
//     if (!file) return

//     const validation = validateMediaFile(file)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)

//       setFormData(prev => ({
//         ...prev,
//         content: {
//           ...prev.content,
//           [`${side}Sidebar`]: {
//             ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//             media: [
//               ...((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []),
//               {
//                 id: Date.now(),
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 type: isImage ? 'image' : 'video',
//                 alt: file.name
//               }
//             ]
//           }
//         }
//       }))

//       toast.success(`${side === 'left' ? 'Left' : 'Right'} sidebar media uploaded`)
//     } catch (err) {
//       console.error('Error uploading sidebar media:', err)
//       toast.error(err.response?.data?.message || 'Failed to upload sidebar media')
//     }
//   }

//   const handleRemoveSpecialMedia = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).filter(m => m.id !== id)
//       }
//     }))
//   }

//   const handleChangeSpecialDisplay = (id, display) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => m.id === id ? { ...m, display } : m)
//       }
//     }))
//   }

//   const handleChangeSpecialMediaStyle = (id, field, value) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         specialMedia: (prev.content.specialMedia || []).map(m => {
//           if (m.id !== id) return m
//           return {
//             ...m,
//             style: {
//               ...m.style,
//               [field]: value
//             }
//           }
//         })
//       }
//     }))
//   }

//   const handleRemoveSidebarMedia = (side, id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           media: ((prev.content[`${side}Sidebar`] && prev.content[`${side}Sidebar`].media) || []).filter(m => m.id !== id)
//         }
//       }
//     }))
//   }

//   const handleSidebarContentChangeText = (side, text) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         [`${side}Sidebar`]: {
//           ...(prev.content[`${side}Sidebar`] || { contentText: '', media: [] }),
//           contentText: text
//         }
//       }
//     }))
//   }

//   const handleItemImageUpload = async (itemId, file, isHeroImage = false) => {
//     if (!file) return

//     if (isHeroImage && !isImageFile(file)) {
//       toast.error('Please upload a valid image file')
//       return
//     }

//     if (!isHeroImage && !isImageFile(file) && !isVideoFile(file)) {
//       toast.error('Please upload a valid image or video file')
//       return
//     }

//     const validation = validateMediaFile(file, isHeroImage)
//     if (!validation.valid) {
//       toast.error(validation.message)
//       return
//     }

//     try {
//       const uploadResult = await uploadMediaFile(file)
//       if (!uploadResult?.url) {
//         throw new Error('Upload failed to return a valid URL')
//       }

//       if (isHeroImage) {
//         // Handle Hero image - add to carousel array
//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             heroImages: [
//               ...(prev.content.heroImages || []),
//               {
//                 id: `hero-${Date.now()}`,
//                 url: uploadResult.url,
//                 publicId: uploadResult.publicId || '',
//                 order: (prev.content.heroImages || []).length
//               }
//             ]
//           }
//         }))
//         toast.success('Hero image added to carousel')
//       } else {
//         // Handle gallery items
//         const isImage = isImageFile(file)
//         const isVideo = isVideoFile(file)

//         setFormData(prev => ({
//           ...prev,
//           content: {
//             ...prev.content,
//             items: prev.content.items.map(item =>
//               item.id === itemId
//                 ? {
//                     ...item,
//                     gallery: [
//                       ...(item.gallery || []),
//                       {
//                         id: Date.now(),
//                         image: isImage ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         video: isVideo ? { url: uploadResult.url, publicId: uploadResult.publicId || '' } : { url: '', publicId: '' },
//                         title: file.name,
//                         description: ''
//                       }
//                     ]
//                   }
//                 : item
//             )
//           }
//         }))

//         toast.success('Image/Video uploaded successfully')
//       }
//     } catch (error) {
//       console.error('Error uploading file:', error)
//       toast.error(error.response?.data?.message || 'Failed to upload file')
//     }
//   }

//   const handleImageInputChange = (e, itemId) => {
//     const file = e.target.files?.[0]
//     if (file) {
//       handleItemImageUpload(itemId, file)
//     }
//   }

//   // Helper functions for carousel/hero images management
//   const handleRemoveHeroImage = (id) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         heroImages: (prev.content.heroImages || []).filter(img => img.id !== id)
//       }
//     }))
//     toast.success('Image removed from carousel')
//   }

//   const handleReorderHeroImages = (fromIndex, toIndex) => {
//     setFormData(prev => {
//       const newImages = [...(prev.content.heroImages || [])]
//       const [movedImage] = newImages.splice(fromIndex, 1)
//       newImages.splice(toIndex, 0, movedImage)
//       // Update order property
//       const reorderedImages = newImages.map((img, idx) => ({ ...img, order: idx }))
//       return {
//         ...prev,
//         content: {
//           ...prev.content,
//           heroImages: reorderedImages
//         }
//       }
//     })
//   }


//   const handleRemoveGalleryItem = (itemId, galleryItemId) => {
//     setFormData(prev => ({
//       ...prev,
//       content: {
//         ...prev.content,
//         items: prev.content.items.map(item =>
//           item.id === itemId
//             ? {
//                 ...item,
//                 gallery: (item.gallery || []).filter(g => g.id !== galleryItemId)
//               }
//             : item
//         )
//       }
//     }))
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()

//     // Section title is optional - allow empty for flexible layouts
//     // if (!formData.title.trim()) {
//     //   toast.error('Section title is required')
//     //   return
//     // }

//     // For NEW sections, section type is required
//     if (formData.isNew && !formData.type) {
//       toast.error('Please select a section type')
//       return
//     }

//     setSaving(true)
//     try {
//       const submitData = {
//         title: formData.title,
//         description: formData.description,
//         disabled: !formData.enabled,
//         displayOrder: (function() { const v = parseInt(formData.displayOrder); return isNaN(v) ? 0 : v })(),
//         styling: formData.styling,
//         // sanitize content to match backend schema expectations
//         content: (function sanitizeContent(ct) {
//           if (!ct) return {}

//           const pickHeroImage = (h) => ({
//             url: h?.url || '',
//             publicId: h?.publicId || ''
//           })

//           const sanitizeMediaArray = (arr) => ((arr || [])
//             .map(m => ({
//               url: m?.url || '',
//               publicId: m?.publicId || '',
//               type: m?.type === 'video' ? 'video' : 'image',
//               display: m?.display === 'full' ? 'full' : 'content',
//               alt: m?.alt || '',
//               style: {
//                 width: m?.style?.width || '100%',
//                 height: m?.style?.height || 'auto',
//                 paddingLeft: m?.style?.paddingLeft || '0px',
//                 paddingRight: m?.style?.paddingRight || '0px',
//                 marginLeft: m?.style?.marginLeft || '0px',
//                 marginRight: m?.style?.marginRight || '0px'
//               }
//             }))
//             .filter(m => typeof m.url === 'string' && m.url.trim() !== '')
//           )

//           const specialMedia = sanitizeMediaArray(ct.specialMedia).map(m => ({
//             url: m.url,
//             publicId: m.publicId,
//             type: m.type,
//             display: m.display,
//             alt: m.alt,
//             style: {
//               width: m.style.width,
//               height: m.style.height,
//               paddingLeft: m.style.paddingLeft,
//               paddingRight: m.style.paddingRight,
//               marginLeft: m.style.marginLeft,
//               marginRight: m.style.marginRight
//             }
//           }))

//           const leftSidebar = {
//             contentText: (ct.leftSidebar && ct.leftSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.leftSidebar?.media)
//           }

//           const rightSidebar = {
//             contentText: (ct.rightSidebar && ct.rightSidebar.contentText) || '',
//             media: sanitizeMediaArray(ct.rightSidebar?.media)
//           }

//           const items = (ct.items || []).map(it => {
//             const itemObj = {
//               title: it?.title || '',
//               description: it?.description || '',
//               gallery: (it.gallery || []).map(g => {
//                 const galleryItem = {
//                   title: g?.title || '',
//                   description: g?.description || '',
//                   alt: g?.alt || ''
//                 }
                
//                 // Include image if it has a valid URL
//                 if (g?.image?.url) {
//                   galleryItem.image = { url: g.image.url, publicId: g.image.publicId || '' }
//                 }
                
//                 // Include video if it has a valid URL
//                 if (g?.video?.url) {
//                   galleryItem.video = { url: g.video.url, publicId: g.video.publicId || '' }
//                 }
                
//                 return galleryItem
//               }).filter(g => (g.image && g.image.url) || (g.video && g.video.url))
//             }
            
//             // Include WhatsApp contact if provided
//             if (it?.whatsappContact && typeof it.whatsappContact === 'string' && it.whatsappContact.trim()) {
//               itemObj.whatsappContact = it.whatsappContact.trim()
//             }
            
//             // Include price fields
//             if (it?.price !== undefined && it?.price !== null && it?.price !== '') {
//               itemObj.price = Number(it.price) || 0
//             }
//             if (it?.currency && typeof it.currency === 'string' && it.currency.trim()) {
//               itemObj.currency = it.currency.trim()
//             }
            
//             // Include any other fields that might be present
//             if (it?.link) itemObj.link = it.link
//             if (it?.image) itemObj.image = it.image
//             if (it?.video) itemObj.video = it.video
//             if (it?.name) itemObj.name = it.name
//             if (it?.id) itemObj.id = it.id
            
//             return itemObj
//           })

//           const logo = ct.logo ? { url: ct.logo.url || '', publicId: ct.logo.publicId || '', fileName: ct.logo.fileName || '' } : undefined

//           // Sanitize team members array
//           const teamMembers = Array.isArray(ct.teamMembers) ? ct.teamMembers.map(member => ({
//             id: member?.id || Date.now(),
//             name: (member?.name || '').trim(),
//             position: (member?.position || '').trim(),
//             bio: (member?.bio || '').trim(),
//             profileImage: member?.profileImage ? {
//               url: member.profileImage.url || '',
//               publicId: member.profileImage.publicId || ''
//             } : { url: '', publicId: '' }
//           })).filter(member => member.name) : []

//           // Sanitize heroImages array for carousel
//           const heroImages = Array.isArray(ct.heroImages) 
//             ? ct.heroImages
//                 .filter(img => img && (img.url || img.publicId))
//                 .map((img, idx) => ({
//                   id: img.id || `hero-${idx}`,
//                   url: img.url || '',
//                   publicId: img.publicId || '',
//                   order: typeof img.order === 'number' ? img.order : idx
//                 }))
//             : []

//           // Sanitize carousel settings
//           const carouselSettings = ct.carouselSettings ? {
//             autoPlay: ct.carouselSettings.autoPlay !== false,
//             interval: Math.max(1000, Math.min(30000, ct.carouselSettings.interval || 5000)),
//             transitionSpeed: Math.max(300, Math.min(3000, ct.carouselSettings.transitionSpeed || 1000)),
//             showIndicators: ct.carouselSettings.showIndicators !== false,
//             showNavigation: ct.carouselSettings.showNavigation !== false
//           } : {
//             autoPlay: true,
//             interval: 5000,
//             transitionSpeed: 1000,
//             showIndicators: true,
//             showNavigation: true
//           }

//           const audio = Array.isArray(ct.audio)
//             ? ct.audio
//                 .map(audioItem => ({
//                   id: audioItem?.id || Date.now().toString(),
//                   name: (audioItem?.name || '').trim(),
//                   description: (audioItem?.description || '').trim(),
//                   banner: {
//                     url: audioItem?.banner?.url || '',
//                     publicId: audioItem?.banner?.publicId || '',
//                     filename: audioItem?.banner?.filename || ''
//                   },
//                   audioFile: {
//                     url: audioItem?.audioFile?.url || '',
//                     publicId: audioItem?.audioFile?.publicId || '',
//                     filename: audioItem?.audioFile?.filename || ''
//                   }
//                 }))
//                 .filter(item => item.name && item.audioFile?.url)
//             : []

//           return {
//             heading: ct.heading || '',
//             subheading: ct.subheading || '',
//             ctaText: (ct.ctaText || '').toString().trim(),
//             ctaLink: (ct.ctaLink || '').toString().trim(),
//             heroImage: ct.heroImage ? pickHeroImage(ct.heroImage) : undefined,
//             heroImages: heroImages.length > 0 ? heroImages : undefined,
//             carouselSettings: heroImages.length > 0 ? carouselSettings : undefined,
//             heroOverlay: ct.heroOverlay || undefined,
//             heroButtons: Array.isArray(ct.heroButtons) ? ct.heroButtons.map(hb => ({ id: hb.id, text: hb.text, link: hb.link, style: hb.style })) : [],
//             buyRate: typeof ct.buyRate === 'number' ? ct.buyRate : undefined,
//             sellRate: typeof ct.sellRate === 'number' ? ct.sellRate : undefined,
//             usdtBuyRate: typeof ct.usdtBuyRate === 'number' ? ct.usdtBuyRate : undefined,
//             usdtSellRate: typeof ct.usdtSellRate === 'number' ? ct.usdtSellRate : undefined,
//             buyEnabled: ct.buyEnabled !== undefined ? Boolean(ct.buyEnabled) : undefined,
//             sellEnabled: ct.sellEnabled !== undefined ? Boolean(ct.sellEnabled) : undefined,
//             giftCardEnabled: ct.giftCardEnabled !== undefined ? Boolean(ct.giftCardEnabled) : undefined,
//             giftCardWhatsApp: typeof ct.giftCardWhatsApp === 'string' ? ct.giftCardWhatsApp.trim() : undefined,
//             giftCardNames: Array.isArray(ct.giftCardNames) ? ct.giftCardNames.map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean) : undefined,
//             backgroundImage: pickHeroImage(ct.backgroundImage || {}),
//             backgroundVideo: pickHeroImage(ct.backgroundVideo || {}),
//             logo,
//             specialMedia,
//             leftSidebar,
//             rightSidebar,
//             items,
//             teamMembers,
//             gallery: (ct.gallery || []).map(g => ({ title: g.title || '', description: g.description || '', image: g.image?.url ? { url: g.image.url, publicId: g.image.publicId || '' } : undefined, video: g.video?.url ? { url: g.video.url, publicId: g.video.publicId || '' } : undefined, alt: g.alt || '' })).filter(g => (g.image && g.image.url) || (g.video && g.video.url)),
//             audio
//           }
//         })(formData.content)
//       }

//       // Include sectionType for new sections or when it was changed during edit
//       if (formData.type || (formData.isNew && formData.type !== undefined)) {
//         const currentType = section?.sectionType || section?.type || null
//         if (formData.isNew || formData.type !== currentType) {
//           submitData.sectionType = formData.type
//         }
//       }

//       console.log('[SectionEditorModal] ===== SUBMIT START =====')
//       console.log('[SectionEditorModal] Submitting:', {
//         isNew: formData.isNew,
//         title: submitData.title,
//         sectionType: submitData.sectionType,
//         hasHeroImage: !!submitData.content?.heroImage?.url,
//         heroImageSize: submitData.content?.heroImage?.url ? Math.round(submitData.content.heroImage.url.length / 1024) + ' KB' : 'N/A',
//         heroImagesCount: submitData.content?.heroImages?.length || 0,
//         carouselSettingsAutoPlay: submitData.content?.carouselSettings?.autoPlay,
//         carouselSettingsInterval: submitData.content?.carouselSettings?.interval
//       })
//       if (submitData.content?.heroImages && submitData.content.heroImages.length > 0) {
//         console.log('[SectionEditorModal] CAROUSEL DATA:', {
//           imageCount: submitData.content.heroImages.length,
//           images: submitData.content.heroImages.map((img, idx) => ({
//             index: idx,
//             id: img.id,
//             hasUrl: !!img.url,
//             urlSize: img.url ? Math.round(img.url.length / 1024) + ' KB' : 'N/A',
//             order: img.order
//           })),
//           settings: submitData.content.carouselSettings
//         })
//       }
//       console.log('[SectionEditorModal] ITEMS WITH PRICE AND GALLERY:', (submitData.content?.items || []).map((item, idx) => ({
//         itemIndex: idx,
//         title: item.title,
//         price: item.price,
//         currency: item.currency,
//         whatsappContact: item.whatsappContact,
//         galleryCount: item.gallery?.length || 0,
//         galleryItems: (item.gallery || []).map((g, gi) => ({
//           galleryIndex: gi,
//           hasImage: !!g.image?.url,
//           hasVideo: !!g.video?.url,
//           imageSize: g.image?.url ? Math.round(g.image.url.length / 1024) + ' KB' : 'N/A',
//           videoSize: g.video?.url ? Math.round(g.video.url.length / 1024) + ' KB' : 'N/A'
//         }))
//       })))
//       console.log('[SectionEditorModal] TEAM MEMBERS:', submitData.content?.teamMembers ? submitData.content.teamMembers.length : 0, submitData.content?.teamMembers ? submitData.content.teamMembers.map((m, idx) => ({
//         index: idx,
//         name: m.name,
//         position: m.position,
//         hasImage: !!m.profileImage?.url,
//         imageSize: m.profileImage?.url ? Math.round(m.profileImage.url.length / 1024) + ' KB' : 'N/A'
//       })) : 'none')
//       console.log('[SectionEditorModal] CTA FIELDS SUBMITTED:', {
//         hasCtaText: !!submitData.content?.ctaText,
//         hasCtaLink: !!submitData.content?.ctaLink,
//         ctaText: submitData.content?.ctaText,
//         ctaLink: submitData.content?.ctaLink,
//         isValid: !!submitData.content?.ctaText && !!submitData.content?.ctaLink
//       })
//       console.log('[SectionEditorModal] Full submit data keys:', Object.keys(submitData))
//       console.log('[SectionEditorModal] Content keys:', Object.keys(submitData.content || {}))
//       if (submitData.content?.heroImage) {
//         console.log('[SectionEditorModal] Hero Image Object:', {
//           hasUrl: !!submitData.content.heroImage.url,
//           hasPublicId: !!submitData.content.heroImage.publicId,
//           urlLength: submitData.content.heroImage.url?.length || 0,
//           urlPreview: submitData.content.heroImage.url?.substring(0, 50)
//         })
//       }
//       // Await the async onSubmit callback and it will handle closing modal
//       await onSubmit(submitData)
//       console.log('[Modal] onSubmit completed successfully - modal will close via parent')
//     } catch (error) {
//       // Error is already handled and toasted in parent, just log it
//       console.error('[Modal] onSubmit failed (error handled by parent):', error.message)
//       // Don't show another toast - parent already showed one
//     } finally {
//       setSaving(false)
//     }
//   }

//   if (!isOpen) return null

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
//           <h2 className="text-2xl font-bold text-gray-900">
//             {formData.isNew ? 'Create New Section' : 'Edit Section'}
//           </h2>
//           <button
//             onClick={onClose}
//             disabled={saving}
//             className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
//           >
//             <FiX className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <form onSubmit={handleSubmit} className="p-6 space-y-6">
//           {/* Hero Section Banner */}
//           {formData.type === 'hero' && (
//             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
//               <div className="flex items-start gap-3">
//                 <div className="text-2xl">🎯</div>
//                 <div className="flex-1">
//                   <h3 className="font-semibold text-blue-900 mb-1">Hero Section</h3>
//                   <p className="text-sm text-blue-700">
//                     This is your website's hero/banner section. It appears at the top of your website with a full-width background image.
//                     The hero section cannot be deleted but can be edited and temporarily hidden.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Basic Information */}
//           <div className="space-y-4">
//             <h3 className="text-lg font-semibold text-gray-900">Section Information</h3>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Title (optional)
//                 </label>
//                 <input
//                   type="text"
//                   name="title"
//                   value={formData.title}
//                   onChange={handleInputChange}
//                   placeholder="Enter section title (optional)"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Section Type
//                 </label>
//                 <select
//                   name="type"
//                   value={formData.type}
//                   onChange={handleInputChange}
//                   className="input-field w-full"
//                 >
//                   <option value="">Select type</option>
//                   {SECTION_TYPES.map(type => (
//                     <option key={type} value={type}>
//                       {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Description
//               </label>
//               <textarea
//                 name="description"
//                 value={formData.description}
//                 onChange={handleInputChange}
//                 placeholder="Brief description of this section"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             <div className="flex items-center gap-2">
//               <input
//                 type="checkbox"
//                 id="enabled"
//                 name="enabled"
//                 checked={formData.enabled}
//                 onChange={handleInputChange}
//                 className="w-4 h-4 rounded border-gray-300"
//               />
//               <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
//                 Section is enabled
//               </label>
//             </div>
//           </div>

//           {/* Products Management - Show when products section type is selected */}
//           {formData.type === 'products' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Product Management</h3>
//               <ProductsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Tickets Management - Show when tickets section type is selected */}
//           {formData.type === 'tickets' && website ? (
//             <div className="pt-6 border-t border-gray-200 bg-blue-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-4">🎫 Ticket Management</h3>
//               <TicketsManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           ) : null}

//           {/* Contact Form - Show message when contact section type is selected */}
//             {formData.type === 'contact' && website ? (
//               <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//                 <h3 className="text-lg font-semibold text-gray-900 mb-2">📧 Contact Form</h3>
//                 <p className="text-sm text-gray-700">
//                   A contact form will be displayed on your public website for this section after you save the settings.
//                 </p>
//               </div>
//             ) : null}

//           {/* Reviews Section - Show when reviews section type is selected */}
//           {formData.type === 'reviews' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">⭐ Customer Reviews Section</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays customer reviews and allows visitors to submit new reviews.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Visitors can submit reviews with a comment and optional star rating</li>
//                 <li>✓ All reviews are saved as "pending" until you approve them</li>
//                 <li>✓ Only approved reviews appear on the public website</li>
//                 <li>✓ Manage reviews (approve/reject/delete) in the "Reviews" dashboard tab</li>
//               </ul>
//             </div>
//           )}

//           {/* Fixtures Section - Show when fixtures section type is selected */}
//           {formData.type === 'fixtures' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 Football Fixtures Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Manage football match fixtures and view automated league standings.
//               </p>
//               <FixturesManagement website={website} section={{ ...section, type: formData.type }} />
//             </div>
//           )}

//           {/* Points Table Section - Show when points-table section type is selected */}
//           {formData.type === 'points-table' && (
//             <div className="pt-6 border-t border-gray-200 bg-indigo-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 League Points Table</h3>
//               <p className="text-sm text-gray-700 mb-3">
//                 This section displays the league standings and is automatically updated when fixtures are completed.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4">
//                 <li>✓ Standings are calculated automatically from finished fixtures</li>
//                 <li>✓ Table shows: Position, Team, Played, Wins, Draws, Losses, Goals For/Against, Goal Difference, Points</li>
//                 <li>✓ Teams are ranked by points, then goal difference, then goals for</li>
//                 <li>✓ Points: Win = 3, Draw = 1, Loss = 0</li>
//               </ul>
//             </div>
//           )}

//           {/* Resume Upload Section - Show when resume section type is selected */}
//           {formData.type === 'resume' && website && (
//             <div className="pt-6 border-t border-gray-200 bg-green-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Resume Upload Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 This section allows visitors to submit their resumes for job positions.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-4">
//                 <li>✓ Visitors can upload PDF, DOC, DOCX, or TXT files (max 5MB)</li>
//                 <li>✓ Submitted resumes appear in your Resume Dashboard</li>
//                 <li>✓ You receive Telegram notifications for new submissions</li>
//                 <li>✓ Track candidate status: Submitted → Reviewed → Shortlisted/Rejected</li>
//               </ul>
              
//               {/* Resume Section Toggle */}
//               <div className="mb-4 p-3 bg-white rounded border">
//                 <div className="flex items-center gap-2">
//                   <input
//                     type="checkbox"
//                     id="resumeToggle"
//                     name="enabled"
//                     checked={formData.enabled}
//                     onChange={handleInputChange}
//                     className="w-4 h-4 rounded border-gray-300"
//                   />
//                   <label htmlFor="resumeToggle" className="text-sm font-medium text-gray-700">
//                     Resume form is visible on public website
//                   </label>
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Turn ON to show the resume submission form to visitors. Turn OFF to hide it completely.
//                 </p>
//               </div>
              
//               <ResumesManagement website={website} />
//             </div>
//           )}

//           {/* Team Members Section - Show when team section type is selected */}
//           {formData.type === 'team' && (
//             <div className="pt-6 border-t border-gray-200 bg-amber-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">👥 Team Members Management</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Create and manage your team members. Each team member can have a profile picture, name, position, and bio.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Add multiple team members with full names and roles</li>
//                 <li>✓ Upload profile pictures for each team member</li>
//                 <li>✓ Add optional bio/description for each member</li>
//                 <li>✓ Team will display in a responsive grid on your public website</li>
//                 <li>✓ Fully responsive layout for mobile, tablet, and desktop</li>
//               </ul>
              
//               <TeamMembersEditor formData={formData} setFormData={setFormData} />
//             </div>
//           )}
//           {/* Audio Section - Show when audio section type is selected */}
//           {formData.type === 'audio' && (
//             <div className="pt-6 border-t border-gray-200 bg-purple-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">🎵 Audio Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Add audio files to your website. Visitors can play audio directly on your site.
//               </p>
//               <AudioManagement formData={formData} setFormData={setFormData} />
//             </div>
//           )}

//           {/* Crypto Product Section - Show when crypto-product section type is selected */}
//           {formData.type === 'crypto-product' && (
//             <div className="pt-6 border-t border-gray-200 bg-orange-50 rounded-lg p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">₿ Crypto Trading Section</h3>
//               <p className="text-sm text-gray-700 mb-4">
//                 Enable cryptocurrency buy/sell functionality on your website. Users can purchase or sell crypto directly through secure payment processing.
//               </p>
//               <ul className="text-sm text-gray-700 space-y-1 ml-4 mb-6">
//                 <li>✓ Support for multiple cryptocurrencies (BTC, ETH, USDT, BNB, ADA, SOL, DOT, DOGE, XRP, LTC)</li>
//                 <li>✓ Secure buy/sell transactions with payment provider integration</li>
//                 <li>✓ Real-time exchange rates with 10-minute price locking</li>
//                 <li>✓ Automatic payment detection and status updates</li>
//                 <li>✓ Transaction dashboard for managing all orders</li>
//                 <li>✓ Admin notifications for completed transactions</li>
//               </ul>

//               {/* Buy/Sell Toggle Controls */}
//               <div className="bg-white rounded-lg p-4 border mb-4">
//                 <h4 className="font-semibold text-gray-900 mb-4">Feature Controls</h4>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Buy Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to buy cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.buyEnabled !== false}
//                         onChange={(e) => handleContentChange('buyEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                   <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700">Enable Sell Crypto</label>
//                       <p className="text-xs text-gray-500 mt-1">Allow users to sell cryptocurrency</p>
//                     </div>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={formData.content.sellEnabled !== false}
//                         onChange={(e) => handleContentChange('sellEnabled', e.target.checked)}
//                         className="sr-only peer"
//                       />
//                       <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                     </label>
//                   </div>
//                 </div>
//               </div>

//               {/* Rate Configuration */}
//               <div className="bg-white rounded-lg p-4 border">
//                 <div className="flex items-center justify-between mb-4">
//                   <h4 className="font-semibold text-gray-900">Exchange Rate Configuration</h4>
//                   {(formData.content.buyRate || formData.content.sellRate || formData.content.usdtBuyRate || formData.content.usdtSellRate) && (
//                     <button
//                       type="button"
//                       onClick={() => {
//                         handleContentChange('buyRate', null)
//                         handleContentChange('sellRate', null)
//                         handleContentChange('usdtBuyRate', null)
//                         handleContentChange('usdtSellRate', null)
//                       }}
//                       className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded border border-red-300"
//                     >
//                       Clear All Rates
//                     </button>
//                   )}
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Buy Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.buyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('buyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('buyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 50000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users buy BTC (in Naira)
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       BTC Sell Rate (₦ per BTC)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.sellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('sellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('sellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 48000000"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Rate at which users sell BTC (in Naira)
//                     </p>
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Buy Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtBuyRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtBuyRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtBuyRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1500"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific buy rate for USDT transactions
//                     </p>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       USDT Sell Rate (₦ per USDT)
//                     </label>
//                     <input
//                       type="number"
//                       value={formData.content.usdtSellRate || ''}
//                       onChange={(e) => {
//                         const value = e.target.value
//                         if (value === '') {
//                           handleContentChange('usdtSellRate', null)
//                         } else {
//                           const num = parseFloat(value)
//                           if (!isNaN(num) && num > 0) {
//                             handleContentChange('usdtSellRate', num)
//                           }
//                         }
//                       }}
//                       placeholder="e.g., 1450"
//                       min="0"
//                       step="0.01"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       Specific sell rate for USDT transactions
//                     </p>
//                   </div>
//                 </div>
//                 <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
//                   <p className="text-sm text-blue-800">
//                     <strong>Note:</strong> Rates must be valid numeric values greater than 0. These rates will be displayed on your public website and used for all transaction calculations.
//                     Update them regularly to reflect current market conditions. Use "Clear Rates" to remove rates entirely.
//                   </p>
//                 </div>
//               </div>

//               <div className="bg-white rounded-lg p-4 border mt-4">
//                 <div className="flex items-center justify-between mb-4">
//                   <div>
//                     <h4 className="font-semibold text-gray-900">Gift Card / Airtime Settings</h4>
//                     <p className="text-sm text-gray-500">Configure the gift card option shown on public crypto pages.</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={formData.content.giftCardEnabled !== false}
//                       onChange={(e) => handleContentChange('giftCardEnabled', e.target.checked)}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
//                   </label>
//                 </div>

//                 <div className="grid grid-cols-1 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-2">
//                       WhatsApp Number for Gift Card Requests
//                     </label>
//                     <input
//                       type="text"
//                       value={formData.content.giftCardWhatsApp || ''}
//                       onChange={(e) => handleContentChange('giftCardWhatsApp', e.target.value)}
//                       placeholder="e.g., +2348012345678"
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">
//                       This number will be used to prefill WhatsApp messages when customers submit a gift card request.
//                     </p>
//                   </div>

//                   <div>
//                     <div className="flex items-center justify-between mb-2">
//                       <label className="block text-sm font-medium text-gray-700">Gift Card / Airtime Types</label>
//                       <button
//                         type="button"
//                         onClick={handleAddGiftCardName}
//                         className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded border border-orange-200 hover:bg-orange-200"
//                       >
//                         Add type
//                       </button>
//                     </div>
//                     <p className="text-xs text-gray-500 mb-3">
//                       Add the gift card names that users can select when requesting gift card payment.
//                     </p>
//                     <div className="space-y-3">
//                       {(formData.content.giftCardNames || []).map((name, index) => (
//                         <div key={index} className="flex items-center gap-2">
//                           <input
//                             type="text"
//                             value={name}
//                             onChange={(e) => handleUpdateGiftCardName(index, e.target.value)}
//                             placeholder={`Gift card type ${index + 1}`}
//                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveGiftCardName(index)}
//                             className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded border border-red-200 hover:bg-red-200"
//                           >
//                             Remove
//                           </button>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
//                 <p className="text-sm text-yellow-800">
//                   <strong>Note:</strong> Crypto trading requires proper payment provider setup and compliance with local regulations.
//                   Contact support for payment provider integration assistance.
//                 </p>
//               </div>
//             </div>
//           )}

//           {/* Generic Content Fields - Show for all section types */}
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Heading
//               </label>
//               <input
//                 type="text"
//                 value={formData.content.heading}
//                 onChange={(e) => handleContentChange('heading', e.target.value)}
//                 placeholder="Main heading"
//                 className="input-field w-full"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Subheading
//               </label>
//               <textarea
//                 value={formData.content.subheading}
//                 onChange={(e) => handleContentChange('subheading', e.target.value)}
//                 placeholder="Subheading or description"
//                 rows="2"
//                 className="input-field w-full resize-none"
//               />
//             </div>

//             {/* Special Media Upload (applies to section) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">📁 Special Media Upload</h3>
//               <p className="text-sm text-gray-600 mb-3">Upload images or videos that will appear in this section on the public website. You can choose to display them full-width or constrained to the content width. There are no file size limits.</p>

//               <div className="flex items-center gap-3">
//                 <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm">
//                   <FiUpload className="w-4 h-4" /> Upload Media
//                   <input
//                     type="file"
//                     accept="image/*,video/*"
//                     onChange={(e) => {
//                       const file = e.target.files?.[0]
//                       if (file) handleSpecialMediaUpload(file)
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//               </div>

//               <div className="grid grid-cols-3 gap-3 mt-4">
//                 {(formData.content.specialMedia || []).map(m => (
//                   <div key={m.id} className="relative border rounded-lg overflow-hidden">
//                     {m.type === 'image' ? (
//                       <img src={m.url} alt={m.alt || 'media'} className="w-full h-28 object-cover" />
//                     ) : (
//                       <video className="w-full h-28 object-cover" controls playsInline preload="metadata">
//                         <source src={m.url} type={getVideoMimeType(m.url)} />
//                         Your browser does not support video playback.
//                       </video>
//                     )}
//                     <div className="p-2 bg-white">
//                       <select value={m.display || 'content'} onChange={(e) => handleChangeSpecialDisplay(m.id, e.target.value)} className="input-field w-full mb-2">
//                         <option value="content">Aligned with content width</option>
//                         <option value="full">Full width</option>
//                       </select>

//                       <div className="grid grid-cols-2 gap-2 mb-2">
//                         <label className="text-xs font-medium text-gray-600">
//                           Width
//                           <input
//                             type="text"
//                             value={m.style?.width || '100%'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'width', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 100%, 400px"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Height
//                           <input
//                             type="text"
//                             value={m.style?.height || 'auto'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'height', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. auto, 300px"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Padding Left
//                           <input
//                             type="text"
//                             value={m.style?.paddingLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingLeft', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Padding Right
//                           <input
//                             type="text"
//                             value={m.style?.paddingRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'paddingRight', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 1rem"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Margin Left
//                           <input
//                             type="text"
//                             value={m.style?.marginLeft || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginLeft', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                         <label className="text-xs font-medium text-gray-600">
//                           Margin Right
//                           <input
//                             type="text"
//                             value={m.style?.marginRight || '0px'}
//                             onChange={(e) => handleChangeSpecialMediaStyle(m.id, 'marginRight', e.target.value)}
//                             className="input-field mt-1"
//                             placeholder="e.g. 0px, 5%"
//                           />
//                         </label>
//                       </div>

//                       <div className="flex justify-between items-center">
//                         <button type="button" onClick={() => handleRemoveSpecialMedia(m.id)} className="text-sm text-red-600">Remove</button>
//                         <span className="text-xs text-gray-500">{m.type}</span>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Sidebars (Left / Right) - moved up for visibility */}
//             <div className="pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Left Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.leftSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('left', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm">
//                   <FiUpload className="w-4 h-4" /> Upload Left Sidebar Media
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'left') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.leftSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('left', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>

//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold mb-2">Right Sidebar</h4>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">Content (text)</label>
//                 <textarea value={formData.content.rightSidebar?.contentText || ''} onChange={(e) => handleSidebarContentChangeText('right', e.target.value)} rows={3} className="input-field w-full resize-none mb-2" />
//                 <label className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm">
//                   <FiUpload className="w-4 h-4" /> Upload Right Sidebar Media
//                   <input type="file" accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleSidebarUpload(file, 'right') }} className="hidden" />
//                 </label>
//                 <div className="mt-3 space-y-2">
//                   {(formData.content.rightSidebar?.media || []).map(m => (
//                     <div key={m.id} className="flex items-center gap-2">
//                       {m.type === 'image' ? (
//                         <img src={m.url} alt={m.alt} className="w-12 h-12 object-cover rounded" />
//                       ) : (
//                         <video className="w-12 h-12 object-cover rounded" controls playsInline preload="metadata">
//                           <source src={m.url} type={getVideoMimeType(m.url)} />
//                         </video>
//                       )}
//                       <div className="flex-1 text-sm text-gray-700">{m.alt}</div>
//                       <button type="button" className="text-red-600 text-sm" onClick={() => handleRemoveSidebarMedia('right', m.id)}>Remove</button>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             {/* Hero Image Carousel Upload - Show when hero section type is selected */}
//             {formData.type === 'hero' && (
//               <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-5 rounded-lg border border-purple-200 space-y-4">
//                 <div className="flex items-center justify-between">
//                   <label className="block text-sm font-bold text-gray-900">
//                     🎠 Hero Image Carousel
//                   </label>
//                   {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                     <span className="text-xs font-semibold bg-purple-200 text-purple-900 px-3 py-1 rounded-full">
//                       {formData.content.heroImages.length} image{formData.content.heroImages.length !== 1 ? 's' : ''}
//                     </span>
//                   )}
//                 </div>
                
//                 {/* Hero Images Gallery Preview */}
//                 {formData.content.heroImages && formData.content.heroImages.length > 0 && (
//                   <div className="space-y-3">
//                     <p className="text-xs text-gray-600 font-medium">Images in carousel (drag to reorder):</p>
//                     <div className="space-y-2 bg-white p-3 rounded-lg border border-purple-100">
//                       {formData.content.heroImages.map((img, idx) => (
//                         <div
//                           key={img.id}
//                           className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200 hover:border-purple-300 transition-colors group"
//                         >
//                           <div className="flex-shrink-0 text-gray-500 font-semibold text-sm w-6 text-center">
//                             {idx + 1}
//                           </div>
//                           {img.url && (
//                             <img 
//                               src={img.url} 
//                               alt={`Hero carousel ${idx + 1}`}
//                               className="w-16 h-16 object-cover rounded border border-gray-300"
//                             />
//                           )}
//                           <div className="flex-1 min-w-0">
//                             <p className="text-xs text-gray-600 truncate">
//                               {img.url ? `Image ${idx + 1} • ${Math.round(img.url.length / 1024)}KB` : 'Pending...'}
//                             </p>
//                           </div>
//                           <div className="flex gap-1">
//                             {idx > 0 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx - 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move up"
//                               >
//                                 ↑
//                               </button>
//                             )}
//                             {idx < formData.content.heroImages.length - 1 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleReorderHeroImages(idx, idx + 1)}
//                                 className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
//                                 title="Move down"
//                               >
//                                 ↓
//                               </button>
//                             )}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveHeroImage(img.id)}
//                               className="p-1 text-gray-400 hover:text-red-600 transition-colors"
//                               title="Remove image"
//                             >
//                               ✕
//                             </button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Carousel Settings */}
//                     <div className="bg-white p-3 rounded-lg border border-purple-100 space-y-2">
//                       <p className="text-xs font-medium text-gray-700">Carousel Settings:</p>
//                       <div className="grid grid-cols-2 gap-2">
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.autoPlay !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               autoPlay: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Auto-play</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showIndicators !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showIndicators: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show dots</span>
//                         </label>
//                         <label className="flex items-center gap-2">
//                           <input
//                             type="checkbox"
//                             checked={formData.content.carouselSettings?.showNavigation !== false}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               showNavigation: e.target.checked
//                             })}
//                             className="w-4 h-4 rounded"
//                           />
//                           <span className="text-xs text-gray-700">Show arrows</span>
//                         </label>
//                         <div>
//                           <label className="text-xs text-gray-700">Interval (ms):</label>
//                           <input
//                             type="number"
//                             min="1000"
//                             max="30000"
//                             step="1000"
//                             value={formData.content.carouselSettings?.interval || 5000}
//                             onChange={(e) => handleContentChange('carouselSettings', {
//                               ...formData.content.carouselSettings,
//                               interval: parseInt(e.target.value)
//                             })}
//                             className="input-field w-full text-xs"
//                           />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 )}
                
//                 {/* Upload Button */}
//                 <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 border-none rounded cursor-pointer font-medium text-sm transition-all shadow-md hover:shadow-lg">
//                   <FiUpload className="w-4 h-4" />
//                   {formData.content.heroImages && formData.content.heroImages.length > 0 ? 'Add More Images' : 'Upload Hero Images'}
//                   <input
//                     type="file"
//                     accept="image/*"
//                     multiple
//                     onChange={(e) => {
//                       const files = e.target.files
//                       if (files) {
//                         Array.from(files).forEach(file => {
//                           handleItemImageUpload(null, file, true)
//                         })
//                       }
//                     }}
//                     className="hidden"
//                   />
//                 </label>
//                 <p className="text-xs text-gray-600">
//                   ✨ Upload 1 image for a static hero, or multiple images for an auto-sliding carousel!<br/>
//                   Recommended: 1920x600px or larger. Supports JPEG, PNG, GIF, WebP. No file size limits.
//                 </p>
//               </div>
//             )}



//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Call-to-Action Text
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaText}
//                   onChange={(e) => handleContentChange('ctaText', e.target.value)}
//                   placeholder="e.g., Learn More"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Link
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.content.ctaLink}
//                   onChange={(e) => handleContentChange('ctaLink', e.target.value)}
//                   placeholder="e.g., #products"
//                   className="input-field w-full"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonBackgroundColor || '#000000'}
//                   onChange={(e) => handleStylingChange('ctaButtonBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Background
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverBackgroundColor || '#2563eb'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverBackgroundColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   CTA Button Hover Text Color
//                 </label>
//                 <input
//                   type="color"
//                   value={formData.styling.ctaButtonHoverTextColor || '#ffffff'}
//                   onChange={(e) => handleStylingChange('ctaButtonHoverTextColor', e.target.value)}
//                   className="w-full h-10 rounded border border-gray-300"
//                 />
//               </div>
//             </div>

//           {/* Items (if applicable) */}
//           <div className="space-y-4 pt-6 border-t border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//             <div className="flex justify-between items-center">
//               <h3 className="text-lg font-semibold text-gray-900">Section Items</h3>
//               <button
//                 type="button"
//                 onClick={handleAddItem}
//                 className="text-sm px-3 py-1 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors font-medium"
//                 style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.ctaButtonBackgroundColor, color: formData.styling?.ctaButtonTextColor } : {}}
//               >
//                 + Add Item
//               </button>
//             </div>

//             <div className="space-y-3">
//               {formData.content.items && formData.content.items.map((item, idx) => (
//                 <div key={item.id} className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor } : {}}>
//                   <div className="flex justify-between items-center">
//                     <p className="font-medium text-gray-900">Item {idx + 1}</p>
//                     <button
//                       type="button"
//                       onClick={() => handleRemoveItem(item.id)}
//                       className="text-red-600 hover:text-red-900 text-sm font-medium"
//                       style={formData.type === 'section-items' ? { color: formData.styling?.ctaButtonTextColor } : {}}
//                     >
//                       Remove
//                     </button>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.title || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
//                     placeholder="Item title"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <textarea
//                     value={item.description || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
//                     placeholder="Item description"
//                     rows="2"
//                     className="input-field w-full resize-none"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   <div className="grid grid-cols-2 gap-3">
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
//                       <input
//                         type="number"
//                         value={item.price || ''}
//                         onChange={(e) => handleUpdateItem(item.id, 'price', e.target.value)}
//                         placeholder="Item price"
//                         step="0.01"
//                         min="0"
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       />
//                     </div>
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
//                       <select
//                         value={item.currency || website?.paymentSettings?.currency || 'NGN'}
//                         onChange={(e) => handleUpdateItem(item.id, 'currency', e.target.value)}
//                         className="input-field w-full"
//                         style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                       >
//                         {getCurrencyOptions().map(option => (
//                           <option key={option.value} value={option.value}>
//                             {option.label}
//                           </option>
//                         ))}
//                       </select>
//                     </div>
//                   </div>

//                   <input
//                     type="text"
//                     value={item.whatsappContact || ''}
//                     onChange={(e) => handleUpdateItem(item.id, 'whatsappContact', e.target.value)}
//                     placeholder="WhatsApp contact (e.g., +1234567890)"
//                     className="input-field w-full"
//                     style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}
//                   />

//                   {/* Image/Video Upload Section */}
//                   <div className="space-y-2">
//                     <label className="block text-sm font-medium text-gray-700">
//                       Images / Videos (Gallery)
//                     </label>
                    
//                     {/* Gallery Preview */}
//                     {item.gallery && item.gallery.length > 0 && (
//                       <div className="grid grid-cols-2 gap-2 mb-3">
//                         {item.gallery.map((galleryItem) => (
//                           <div key={galleryItem.id} className="relative group">
//                             {galleryItem.image?.url ? (
//                               <img 
//                                 src={galleryItem.image.url} 
//                                 alt={galleryItem.title || 'Gallery item'} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                               />
//                             ) : galleryItem.video?.url ? (
//                               <video 
//                                 src={galleryItem.video.url} 
//                                 className="h-24 w-full object-cover rounded border border-gray-200"
//                                 controls
//                               />
//                             ) : null}
//                             <button
//                               type="button"
//                               onClick={() => handleRemoveGalleryItem(item.id, galleryItem.id)}
//                               className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
//                               title="Remove"
//                             >
//                               <FiX className="w-3 h-3" />
//                             </button>
//                           </div>
//                         ))}
//                       </div>
//                     )}

//                     <div className="flex gap-2">
//                       <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm transition-colors" style={formData.type === 'section-items' ? { backgroundColor: formData.styling?.backgroundColor, color: formData.styling?.textColor, borderColor: formData.styling?.textColor } : {}}>
//                         <FiUpload className="w-4 h-4" />
//                         Add Image/Video
//                         <input
//                           type="file"
//                           accept="image/*,video/*"
//                           onChange={(e) => handleImageInputChange(e, item.id)}
//                           className="hidden"
//                         />
//                       </label>
//                     </div>
                    
//                     {item.gallery && item.gallery.length > 0 && (
//                       <p className="text-xs text-gray-600">
//                         {item.gallery.length} file(s) uploaded
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>



//           {/* Section Styling */}
//           <div className="pt-6 border-t border-gray-200">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">🎨 Section Styling</h3>
//             <p className="text-sm text-gray-600 mb-4">
//               Customize the appearance of this section on your public website.
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.backgroundColor || '#ffffff'}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.backgroundColor || ''}
//                     onChange={(e) => handleStylingChange('backgroundColor', e.target.value)}
//                     placeholder="#ffffff or white"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a background color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.textColor || '#000000'}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.textColor || ''}
//                     onChange={(e) => handleStylingChange('textColor', e.target.value)}
//                     placeholder="#000000 or black"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Choose a text color for this section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Background Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverBackgroundColor || '#f9fafb'}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverBackgroundColor || ''}
//                     onChange={(e) => handleStylingChange('hoverBackgroundColor', e.target.value)}
//                     placeholder="#f9fafb or light gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Background color when hovering over the section
//                 </p>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Hover Text Color
//                 </label>
//                 <div className="flex gap-2">
//                   <input
//                     type="color"
//                     value={formData.styling.hoverTextColor || '#374151'}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
//                   />
//                   <input
//                     type="text"
//                     value={formData.styling.hoverTextColor || ''}
//                     onChange={(e) => handleStylingChange('hoverTextColor', e.target.value)}
//                     placeholder="#374151 or dark gray"
//                     className="input-field flex-1"
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-1">
//                   Text color when hovering over the section
//                 </p>
//               </div>
//             </div>
//           </div>



//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-6 border-t border-gray-200">
//             <button
//               type="button"
//               onClick={onClose}
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={saving}
//               className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
//             >
//               {saving ? (
//                 <>
//                   <FiLoader className="w-4 h-4 animate-spin" /> Saving...
//                 </>
//               ) : (
//                 <>
//                   <FiSave className="w-4 h-4" /> Save Section
//                 </>
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }










